import { create } from 'zustand';
import { nanoid } from 'nanoid';
import { apiRequest } from '@/lib/queryClient';
import { FIGURE_TEMPLATE_PRESETS } from '@/lib/figureTemplates';
import { journalTemplatePresets } from '@/lib/journalTemplates';
import type { Data, Layout } from 'plotly.js';
import { usePlotsStore } from './plots';
import { useFigureEditorUiState } from '@/hooks/useFigureEditorUiState';
import {
  createFigureComposition,
  getFigureComposition,
  updateFigureComposition,
  deleteFigureComposition,
  listFigureCompositions,
  compositionToApiPayload,
} from '@/lib/api/figureCompositions';
import { DEFAULT_COLORBLIND_PALETTE } from '@/lib/colorblindPalettes';
import { generateHueLightnessPalette } from '@/lib/colors/hueLightness';
import { scaleFigureToPanel } from '@/lib/scaleFigureToPanel';
import { applyLegendLayout } from '@/lib/figureLegendLayout';

// Debounce utility function
function debounce<T extends (...args: any[]) => any>(func: T, delay: number): T {
  let timeoutId: NodeJS.Timeout;
  return ((...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  }) as T;
}

// Helper to safely convert updatedAt to ISO string (handles Date, string, or other types)
function updatedAtToISOString(updatedAt: Date | string | unknown): string {
  if (updatedAt instanceof Date) {
    return updatedAt.toISOString();
  }
  if (typeof updatedAt === 'string') {
    return updatedAt;
  }
  if (updatedAt) {
    try {
      return new Date(updatedAt as any).toISOString();
    } catch {
      return new Date().toISOString();
    }
  }
  return new Date().toISOString();
}

export interface PanelLayoutOverrides {
  showLegend?: boolean;
  legendPosition?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'none';
  legendOffsetX?: number; // Horizontal fine-tune offset for legend position (-0.5 to 0.5, default 0)
  legendOffsetY?: number; // Vertical fine-tune offset for legend position (-0.5 to 0.5, default 0)
  margin?: {
    t?: number;
    r?: number;
    b?: number;
    l?: number;
  };
  fontSize?: number;
  showTitle?: boolean;
  showXAxisTitle?: boolean;
  showYAxisTitle?: boolean;
  axesOverride?: boolean; // Override figure-level axes sharing for this panel
  zoom?: number; // Zoom level (0.5 to 2.0, default 1.0)
  panX?: number; // Horizontal pan offset (-1.0 to 1.0, default 0.0)
  panY?: number; // Vertical pan offset (-1.0 to 1.0, default 0.0)
  hideTitle?: boolean; // Hide plot title
  hideSubtitle?: boolean; // Hide plot subtitle
  hideAnnotations?: boolean; // Hide annotations (text annotations and shapes)
}

export interface PanelAnnotation {
  id: string;
  type: 'text' | 'arrow' | 'bracket';
  x: number; // Position relative to panel (0-1 normalized coordinates)
  y: number;
  x2?: number; // For arrow/bracket end point
  y2?: number;
  content?: string; // For text and bracket labels (e.g., "p<0.05", "n.s.")
  xanchor?: 'left' | 'center' | 'right'; // Horizontal anchor point for text
  yanchor?: 'top' | 'middle' | 'bottom'; // Vertical anchor point for text
  style?: {
    fontSizePt?: number;
    color?: string;
    fontWeight?: 'normal' | 'bold';
    fontStyle?: 'normal' | 'italic';
    opacity?: number;
    strokeWidthPx?: number;
  };
}

export interface PanelLabelStyle {
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  fontSize?: number;
  fontWeight?: 'normal' | 'bold';
  fontStyle?: 'normal' | 'italic';
}

export interface FigurePanel {
  id: string;
  plotId: string;
  plotVersionId?: string;
  // Optional inline figure JSON override (e.g., from Plot Editor's edited state)
  figureJsonOverride?: {
    data?: any[];
    layout?: Record<string, any>;
  };
  layoutOverrides?: PanelLayoutOverrides;

  // Free-form layout (react-grid-layout units)
  x: number;
  y: number;
  w: number;
  h: number;

  // Panel size in millimeters (source of truth for proportional scaling)
  panelWidthMm?: number;
  panelHeightMm?: number;

  label: string;
  labelStyle?: PanelLabelStyle;
  locked?: boolean;
  minW?: number;
  minH?: number;
  maxW?: number;
  maxH?: number;
  annotations?: PanelAnnotation[];
}

export type JournalTemplateId =
  | 'nature'
  | 'science'
  | 'cell'
  | 'pnas'
  | 'acs_generic'
  | 'plos_generic'
  | 'elsevier_generic'
  | 'wiley_generic'
  | 'science_advances';

export interface FigureCompositionGrid {
  cols: number;
  rowHeight: number;
  margin: [number, number];
}

export interface FigureCompositionTheme {
  fontFamily: string;
  fontScale?: number; // Font scale multiplier (0.5-2.0, default 1.0) - scales all text proportionally
  fontSize?: number; // Deprecated: kept for backward compatibility, use fontScale instead
  colorMode: 'RGB' | 'CMYK';
  journalTemplate?: JournalTemplateId;
  stylePreset?: 'default' | 'nature-like' | 'plos-like' | 'clean-minimal' | 'custom';
  colorblindSafe?: boolean;
  lineWidth?: number; // Default line width in px
}

export interface AxesSharing {
  shareXAcrossColumns: boolean;
  shareXAcrossAll: boolean;
  shareYAcrossRows: boolean;
  warnOnScaleMismatch: boolean;
  standardizeXTitles?: boolean; // Standardize X-axis titles across shared axes
  standardizeYTitles?: boolean; // Standardize Y-axis titles across shared axes
  standardizeXScales?: boolean; // Standardize X-axis scale types (linear/log/etc) across shared axes
  standardizeYScales?: boolean; // Standardize Y-axis scale types (linear/log/etc) across shared axes
}

export type LegendPosition = "auto" | "top_outside" | "bottom_outside" | "right_outside" | "none";

export interface FigureComposition {
  id: string;
  projectId: string;
  title: string;
  caption: string; // Legacy: kept for backwards compatibility and export. Use effectiveCaption() helper.
  captionMode?: 'auto' | 'manual'; // Default: 'manual' for existing figures
  captionAuto?: string; // Auto-generated caption from panel plots
  captionManual?: string; // User-edited caption
  panels: FigurePanel[];
  templatePresetId?: string;

  canvasSize: {
    width: number;
    height: number;
    unit: 'mm';
  };

  grid: FigureCompositionGrid;
  theme: FigureCompositionTheme;
  axesSharing?: AxesSharing;

  figureLegend?: {
    enabled: boolean;
    panelId?: string;
    position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  };

  legendPosition?: LegendPosition; // New unified legend position for multi-panel figures

  // X-axis standardization groups: key = "col:<x>" (column-based grouping, not data-based)
  // Panels in the same column (same panel.x) share X-scale when standardizeXScales is enabled
  lockedXScaleGroups?: Record<string, { scaleFactor: number; referencePanelId: string }> | null;
  
  // Y-axis standardization groups: key = "row:<y>" (row-based grouping, not data-based)
  // Panels in the same row (same panel.y) share Y-scale when standardizeYScales is enabled
  lockedYScaleGroups?: Record<string, { scaleFactor: number; referencePanelId: string }> | null;

  createdAt: Date;
  updatedAt: Date;
}

export interface FigureTemplatePreset {
  id: string;
  name: string;
  description: string;
  initialPanels: Array<{
    x: number;
    y: number;
    w: number;
    h: number;
    label: string;
  }>;
  maxPanels: number;
  suggestedLegendPanelIndex?: number;
}

export type SaveStatus = 'unsaved-draft' | 'unsaved-changes' | 'saving' | 'saved' | 'error' | 'offline';

export type FigureDraft = {
  updatedAt: string; // ISO
  title: string;
  caption: string | null;
  composition: FigureComposition;
  serverUpdatedAt?: string | null;
};

interface FigureCompositionState {
  compositions: FigureComposition[];
  currentComposition: FigureComposition | null;
  isLoading: boolean;
  isCreating: boolean;
  error: string | null;
  saveStatus: SaveStatus;
  lastSaveError: string | null;
  lastSavedAt: Date | null; // Track when composition was last successfully saved
  projectId: string | null; // Track current project ID
  figureId: string | null; // null when draft, set after first Save
  isDraft: boolean; // true for /figures/new before first Save
  isDirty: boolean; // true when local changes not saved to server
  selectedPanelId: string | null; // Selection state for context-aware properties
  selectedPanelIds: string[]; // Multi-select support
  selectedAnnotationId: string | null; // Selection state for annotation editing

  initNewComposition: (
    projectId: string,
    options?: {
      templatePresetId?: string;
      initialPlot?: {
        plotId: string;
        plotVersionId?: string;
        figureJsonOverride?: {
          data?: any[];
          layout?: Record<string, any>;
        };
      };
    }
  ) => void;
  createComposition: (
    projectId: string,
    options?: {
      templatePresetId?: string;
      initialPlot?: {
        plotId: string;
        plotVersionId?: string;
        figureJsonOverride?: {
          data?: any[];
          layout?: Record<string, any>;
        };
      };
    }
  ) => Promise<FigureComposition>;
  loadComposition: (id: string) => Promise<void>;
  saveComposition: (comp?: FigureComposition) => Promise<void>;
  saveDraftLocally: () => void;
  loadDraftIfAny: (projectId: string, figureId?: string) => FigureDraft | null;
  clearDraft: (projectId: string, figureId?: string) => void;
  setOnlineStatus: (isOnline: boolean) => void;
  invalidatePanelCacheForPlot: (plotId: string, preservePinnedVersions?: string[]) => void;
  listCompositionsForProject: (projectId: string) => Promise<void>;
  deleteComposition: (id: string) => Promise<void>;
  setCurrentCompositionId: (id: string | null) => void;
  updateCurrentComposition: (updates: Partial<FigureComposition>) => void;

  addPanel: (partial: Omit<FigurePanel, 'id' | 'label'>) => void;
  removePanel: (panelId: string) => void;
  updatePanel: (panelId: string, updates: Partial<FigurePanel>) => void;
  updatePanelLayout: (
    panelId: string,
    layout: { x: number; y: number; w: number; h: number }
  ) => void;

  applyTemplatePreset: (presetId: string) => void;
  hydratePanel: (panel: FigurePanel, composition?: FigureComposition) => Promise<{ data: Data[]; layout: Partial<Layout> }>;

  setTitle: (title: string) => void;
  generateFigureTitle: (composition: FigureComposition) => string; // Generate meaningful title from panels
  setCaption: (caption: string) => void; // Backwards-compat: sets captionManual and mode='manual'
  setCaptionMode: (mode: 'auto' | 'manual') => void;
  setCaptionManual: (caption: string) => void;
  recomputeAutoCaption: () => Promise<void>; // Rebuild captionAuto from current panels and plot captions
  getEffectiveCaption: () => string; // Get caption based on current mode
  resolveEffectivePlotVersion: (args: { plotId: string; panelVersionId?: string | null; projectId?: string }) => Promise<{ versionId: string | null; followLatest: boolean }>; // Unified version resolution helper
  resolveEffectivePlotVersionSync: (args: { plotId: string; panelVersionId?: string | null; projectId?: string }) => { versionId: string | null; isPinned: boolean }; // Synchronous version resolver for signatures
  panelCacheKey: (plotId: string, versionId: string | null, extraHash?: string) => string; // Simplified cache key builder
  hasPanelCachedData: (args: { plotId: string; effectiveVersionId: string | null; themeKey: string; axesSharingKey: string; overridesKey: string }) => boolean; // Check if panel has cached data
  setTheme: (theme: Partial<FigureCompositionTheme>) => void;
  setCanvasSize: (size: FigureComposition['canvasSize']) => void;
  setFigureLegend: (legend: Partial<NonNullable<FigureComposition['figureLegend']>>) => void;
  setLegendPosition: (position: LegendPosition) => void;
  updatePanelOverrides: (panelId: string, overrides: Partial<PanelLayoutOverrides>) => void;
  setGridSettings: (grid: Partial<FigureCompositionGrid>) => void;
  setAxesSharing: (axesSharing: Partial<AxesSharing>) => void;
  setSelectedPanelId: (panelId: string | null) => void;
  togglePanelSelection: (panelId: string) => void;
  clearSelection: () => void;
  duplicatePanel: (panelId: string) => void;
  duplicatePanels: (panelIds: string[]) => void;
  nudgePanels: (panelIds: string[], deltaX: number, deltaY: number) => void;

  // Annotation management
  addAnnotation: (panelId: string, annotation: Omit<PanelAnnotation, 'id'>) => void;
  updateAnnotation: (panelId: string, annotationId: string, updates: Partial<PanelAnnotation>) => void;
  removeAnnotation: (panelId: string, annotationId: string) => void;
  setSelectedAnnotationId: (annotationId: string | null) => void;
  nudgeAnnotation: (panelId: string, annotationId: string, deltaX: number, deltaY: number) => void;

  exportToSVG: () => Promise<string>;
  exportToPNG: (dpi?: number) => Promise<Blob>;
  exportPanelToSVG: (panelId: string) => Promise<string>;
  exportPanelToPNG: (panelId: string, dpi?: number) => Promise<Blob>;
  exportPanelsToZip: (format: 'svg' | 'png', dpi?: number) => Promise<Blob>;
}

function generatePanelLabel(panels: FigurePanel[]): string {
  const labels = panels.map(p => p.label);
  const letterIndex = labels.length;
  const charCode = 'a'.charCodeAt(0) + (letterIndex % 26);
  const letter = String.fromCharCode(charCode);
  const panelNumber = Math.floor(letterIndex / 26);
  const letterLabel = panelNumber === 0 ? letter : `${letter}${panelNumber}`;
  return `(${letterLabel})`;
}

const panelCache = new Map<string, { data: Data[]; layout: Partial<Layout> }>();

/**
 * Get the grid row index for a panel.
 * panel.y is already in React Grid Layout units (grid rows), not pixels,
 * so we don't need to divide by rowHeight.
 */
export function getPanelRow(panel: FigurePanel): number {
  return Math.max(0, Math.floor(panel.y));
}

/**
 * Compute effective caption for a composition.
 * Prefers manual caption if user has edited, otherwise uses auto-generated caption.
 * This is a pure helper function that can be used in export logic.
 */
function getEffectiveCaptionForComposition(comp: FigureComposition): string {
  // If user has manually edited, use that
  if (comp.captionManual) {
    return comp.captionManual;
  }
  // Otherwise, use auto-generated caption if available
  if (comp.captionAuto) {
    return comp.captionAuto;
  }
  // Fallback to legacy caption field
  return comp.caption || '';
}

/**
 * Normalize panel geometry for standardized axes to ensure perfect tick alignment
 * Sets consistent height, margins, domains, and disables automargin
 */
function normalizePanelGeometryForSharedAxis(
  layout: any,
  _composition: FigureComposition,
  _panel: FigurePanel,
  axisType: 'x' | 'y'
): void {
  // Use canonical geometry values to ensure all panels have identical plotting area dimensions
  // These defaults ensure consistent tick alignment across panels
  // TODO: Could make these configurable via composition.theme in the future
  const panelHeight = 320;
  const panelWidth = 640;
  const marginTop = 20;
  const marginBottom = 40;
  const marginLeft = 60;
  const marginRight = 20;

  // Set consistent panel dimensions
  layout.height = panelHeight;
  layout.width = panelWidth;

  // Set consistent margins with autoexpand disabled to prevent Plotly from widening margin.l per panel
  // Plotly's default margin.autoexpand = true allows it to widen margin.l to fit labels,
  // reintroducing per-panel left-margin differences and causing tick misalignment
  layout.margin = {
    ...(layout.margin || {}),
    t: marginTop,
    b: marginBottom,
    l: marginLeft,
    r: marginRight,
    autoexpand: false, // CRITICAL: Prevent Plotly from auto-adjusting margin.l per panel
    pad: 0, // CRITICAL: Disable padding that could vary per panel
  };

  // Disable automargin to prevent Plotly from auto-adjusting based on labels
  if (axisType === 'y' && layout.yaxis) {
    layout.yaxis.automargin = false;
    // Ensure domain is full panel for consistent positioning
    layout.yaxis.domain = [0, 1];
  }

  if (axisType === 'x' && layout.xaxis) {
    layout.xaxis.automargin = false;
    // Ensure domain is full panel for consistent positioning
    layout.xaxis.domain = [0, 1];
  }

  // Also set the other axis's automargin to false for consistency
  if (layout.xaxis) {
    layout.xaxis.automargin = layout.xaxis.automargin ?? false;
  }
  if (layout.yaxis) {
    layout.yaxis.automargin = layout.yaxis.automargin ?? false;
  }
}

/**
 * Compute X-axis scale groups for panels in the same column (layout-based grouping, not data-based)
 * Groups panels by panel.x (column position) when standardizeXScales is enabled
 */
function computeLockedXScaleGroups(
  composition: FigureComposition,
  grid: FigureCompositionGrid,
  canvasSize: { width: number; height: number; unit: string }
): Record<string, { scaleFactor: number; referencePanelId: string }> | null {
  const shareX = composition.axesSharing?.standardizeXScales;
  if (!shareX) return null;

  const panels = composition.panels ?? [];
  if (!panels.length) return null;

  const groups: Record<string, { scaleFactor: number; referencePanelId: string }> = {};
  const canonicalWidth = 640;
  const canonicalHeight = 320;

  // Convert canvas width from mm to pixels (assuming 96 DPI for preview)
  const PREVIEW_DPI = 96;
  const mmToPx = (mm: number) => (mm / 25.4) * PREVIEW_DPI;
  const canvasWidthPx = mmToPx(canvasSize.width);
  const [marginX, marginY] = grid.margin;
  
  // ✅ Use the same formula as RGL + export
  const colWidth = (canvasWidthPx - marginX * (grid.cols + 1)) / grid.cols;
  const rowHeightPx = grid.rowHeight;

  // Group panels by column index (panel.x)
  const panelsByCol: Record<string, typeof panels> = {};
  for (const panel of panels) {
    const axesOverride = panel.layoutOverrides?.axesOverride ?? false;
    if (axesOverride || !panel.plotId) continue; // skip non-participants
    const colKey = `col:${panel.x}`; // column-based grouping for X sharing
    if (!panelsByCol[colKey]) panelsByCol[colKey] = [];
    panelsByCol[colKey].push(panel);
  }

  for (const [colKey, colPanels] of Object.entries(panelsByCol)) {
    let minExpectedWidth = Infinity;
    let minExpectedHeight = Infinity;
    let refId = colPanels[0].id;

    for (const panel of colPanels) {
      const expectedWidth = panel.w * colWidth + (panel.w - 1) * marginX;
      const expectedHeight = panel.h * rowHeightPx + (panel.h - 1) * marginY;

      if (expectedWidth < minExpectedWidth) {
        minExpectedWidth = expectedWidth;
        refId = panel.id;
      }
      if (expectedHeight < minExpectedHeight) {
        minExpectedHeight = expectedHeight;
      }
    }

    const scaleX = minExpectedWidth / canonicalWidth;
    const scaleY = minExpectedHeight / canonicalHeight;
    const scaleFactor = Math.min(scaleX, scaleY);

    if (isFinite(scaleFactor) && scaleFactor > 0) {
      groups[colKey] = {
        scaleFactor,
        referencePanelId: refId,
      };
    }
  }

  return Object.keys(groups).length ? groups : null;
}

/**
 * Compute Y-axis scale groups for panels in the same row (layout-based grouping, not data-based)
 * Groups panels by panel.y (row position) when standardizeYScales is enabled
 */
function computeLockedYScaleGroups(
  composition: FigureComposition,
  grid: FigureCompositionGrid,
  canvasSize: { width: number; height: number; unit: string }
): Record<string, { scaleFactor: number; referencePanelId: string }> | null {
  const shareY = composition.axesSharing?.standardizeYScales;
  if (!shareY) return null;

  const panels = composition.panels ?? [];
  if (!panels.length) return null;

  const groups: Record<string, { scaleFactor: number; referencePanelId: string }> = {};
  const canonicalWidth = 640;
  const canonicalHeight = 320;

  // Convert canvas width from mm to pixels (assuming 96 DPI for preview)
  const PREVIEW_DPI = 96;
  const mmToPx = (mm: number) => (mm / 25.4) * PREVIEW_DPI;
  const canvasWidthPx = mmToPx(canvasSize.width);
  const [marginX, marginY] = grid.margin;
  
  // ✅ Use the same formula as RGL + export
  const colWidth = (canvasWidthPx - marginX * (grid.cols + 1)) / grid.cols;
  const rowHeightPx = grid.rowHeight;

  // Group panels by row index (panel.y)
  const panelsByRow: Record<string, typeof panels> = {};
  for (const panel of panels) {
    const axesOverride = panel.layoutOverrides?.axesOverride ?? false;
    if (axesOverride || !panel.plotId) continue; // skip non-participants
    const rowKey = `row:${panel.y}`; // row-based grouping for Y sharing
    if (!panelsByRow[rowKey]) panelsByRow[rowKey] = [];
    panelsByRow[rowKey].push(panel);
  }

  for (const [rowKey, rowPanels] of Object.entries(panelsByRow)) {
    let minExpectedWidth = Infinity;
    let minExpectedHeight = Infinity;
    let refId = rowPanels[0].id;

    for (const panel of rowPanels) {
      const expectedWidth = panel.w * colWidth + (panel.w - 1) * marginX;
      const expectedHeight = panel.h * rowHeightPx + (panel.h - 1) * marginY;

      if (expectedWidth < minExpectedWidth) {
        minExpectedWidth = expectedWidth;
      }
      if (expectedHeight < minExpectedHeight) {
        minExpectedHeight = expectedHeight;
        refId = panel.id;
      }
    }

    const scaleX = minExpectedWidth / canonicalWidth;
    const scaleY = minExpectedHeight / canonicalHeight;
    const scaleFactor = Math.min(scaleX, scaleY);

    if (isFinite(scaleFactor) && scaleFactor > 0) {
      groups[rowKey] = {
        scaleFactor,
        referencePanelId: refId,
      };
    }
  }

  return Object.keys(groups).length ? groups : null;
}

/**
 * Helper to ensure scale groups are computed for a composition
 * Called after loading from API or initializing new compositions
 */
function ensureScaleGroupsComputed(composition: FigureComposition): FigureComposition {
  const axesSharing = composition.axesSharing;
  
  // If no standardization is active, ensure groups are null
  if (!axesSharing?.standardizeXScales && !axesSharing?.standardizeYScales) {
    return {
      ...composition,
      lockedXScaleGroups: null,
      lockedYScaleGroups: null,
    };
  }

  // Compute groups if standardization is active
  let lockedXScaleGroups: Record<string, { scaleFactor: number; referencePanelId: string }> | null = null;
  let lockedYScaleGroups: Record<string, { scaleFactor: number; referencePanelId: string }> | null = null;

  if (axesSharing.standardizeXScales) {
    lockedXScaleGroups = computeLockedXScaleGroups(composition, composition.grid, composition.canvasSize);
  }

  if (axesSharing.standardizeYScales) {
    lockedYScaleGroups = computeLockedYScaleGroups(composition, composition.grid, composition.canvasSize);
  }

  return {
    ...composition,
    lockedXScaleGroups,
    lockedYScaleGroups,
  };
}

function computeSharedLockedScaleFactor(
  composition: FigureComposition,
  grid: FigureCompositionGrid,
  canvasSize: { width: number; height: number; unit: string }
): { scaleFactor: number; referencePanelId: string } | null {
  const axesSharing = composition.axesSharing;
  
  // If neither standardizeXScales nor standardizeYScales is active, return null
  if (!axesSharing?.standardizeXScales && !axesSharing?.standardizeYScales) {
    return null;
  }

  // Determine which panels are "locked" (have plotId and don't override axes)
  const lockedPanels = composition.panels.filter((panel) => {
    if (!panel.plotId) return false;
    const axesOverride = panel.layoutOverrides?.axesOverride ?? false;
    return !axesOverride;
  });

  if (lockedPanels.length === 0) {
    return null;
  }

  // Canonical normalized dimensions (from normalizePanelGeometryForSharedAxis)
  const canonicalWidth = 640;
  const canonicalHeight = 320;

  // Compute expected container sizes from grid dimensions (not DOM measurements)
  // Convert canvas width from mm to pixels (assuming 96 DPI for preview)
  const PREVIEW_DPI = 96;
  const mmToPx = (mm: number) => (mm / 25.4) * PREVIEW_DPI;
  const journalLimitWidthPx = mmToPx(canvasSize.width);
  const baseColWidthPx = journalLimitWidthPx / grid.cols;
  const rowHeightPx = grid.rowHeight;
  const marginX = grid.margin[0];
  const marginY = grid.margin[1];

  // Track smallest expected dimensions and the panel that produced the smallest width
  let minExpectedWidth = Infinity;
  let minExpectedHeight = Infinity;
  let referencePanelId = '';

  for (const panel of lockedPanels) {
    // Compute expected container size from grid dimensions
    const expectedWidth = panel.w * baseColWidthPx + (panel.w - 1) * marginX;
    const expectedHeight = panel.h * rowHeightPx + (panel.h - 1) * marginY;

    if (expectedWidth < minExpectedWidth) {
      minExpectedWidth = expectedWidth;
      referencePanelId = panel.id;
    }
    if (expectedHeight < minExpectedHeight) {
      minExpectedHeight = expectedHeight;
    }
  }

  // Compute scale factors
  const scaleX = minExpectedWidth / canonicalWidth;
  const scaleY = minExpectedHeight / canonicalHeight;
  const scaleFactor = Math.min(scaleX, scaleY);

  // Validate scale factor
  if (!isFinite(scaleFactor) || scaleFactor <= 0) {
    return null;
  }

  return { scaleFactor, referencePanelId };
}

/**
 * Apply shared axis properties (range, scale, ticks) AFTER journal template and style
 * This ensures nothing can override the standardized axis settings
 */
function applySharedAxisProps({
  baseResult,
  composition,
  panel,
  sharedProps,
  axesSharing,
}: {
  baseResult: { data: Data[]; layout: Partial<Layout> };
  composition: FigureComposition;
  panel: FigurePanel;
  sharedProps: Awaited<ReturnType<typeof calculateSharedAxisRanges>>;
  axesSharing: AxesSharing;
}): void {
  const layout = baseResult.layout as any;

  // ----- X AXIS: Apply shared range, title, scale, and ticks -----
  if (sharedProps.xRange) {
    layout.xaxis = layout.xaxis || {};
    layout.xaxis.range = sharedProps.xRange;
    // Mark as shared for domain calculation in render layer
    const gridUnitWidth = composition.grid.cols / 12;
    const panelCol = Math.floor(panel.x / gridUnitWidth);
    layout.xaxis._sharedAxis = axesSharing.shareXAcrossAll ? 'all' : `col-${panelCol}`;
  }

  if (sharedProps.xTitle !== undefined && sharedProps.xTitle) {
    layout.xaxis = layout.xaxis || {};
    layout.xaxis.title = { text: sharedProps.xTitle };
  }

  if (sharedProps.xScale !== undefined) {
    layout.xaxis = layout.xaxis || {};
    layout.xaxis.type = sharedProps.xScale;
  }

  if (axesSharing.standardizeXScales) {
    layout.xaxis = layout.xaxis || {};
    const xAxis = layout.xaxis;

    // Hard-disable auto ticks
    xAxis.autotick = false;

    // Clear conflicting properties
    delete xAxis.dtick;
    delete xAxis.tick0;

    // Apply canonical tick specification
    if (sharedProps.xTickMode === 'array' && sharedProps.xTickvals && sharedProps.xTickvals.length > 0) {
      xAxis.tickmode = 'array';
      xAxis.tickvals = sharedProps.xTickvals;
    }

    // Ensure labels are shown
    if (xAxis.showticklabels === undefined) {
      xAxis.showticklabels = true;
    }

    // Normalize geometry for standardized X axes
    normalizePanelGeometryForSharedAxis(layout, composition, panel, 'x');
  }

  // ----- Y AXIS: Apply shared range, title, scale, and ticks -----
  if (sharedProps.yRange) {
    layout.yaxis = layout.yaxis || {};
    layout.yaxis.range = sharedProps.yRange;
    // Mark as shared for domain calculation in render layer
    const panelRow = getPanelRow(panel);
    layout.yaxis._sharedAxis = `row-${panelRow}`;
  }

  if (sharedProps.yTitle !== undefined && sharedProps.yTitle) {
    layout.yaxis = layout.yaxis || {};
    layout.yaxis.title = { text: sharedProps.yTitle };
  }

  if (sharedProps.yScale !== undefined) {
    layout.yaxis = layout.yaxis || {};
    layout.yaxis.type = sharedProps.yScale;
  }

  if (axesSharing.standardizeYScales) {
    layout.yaxis = layout.yaxis || {};
    const yAxis = layout.yaxis;

    // Hard-disable auto ticks
    yAxis.autotick = false;

    // Clear conflicting properties
    delete yAxis.dtick;
    delete yAxis.tick0;

    // Apply canonical tick specification
    if (sharedProps.yTickMode === 'array' && sharedProps.yTickvals && sharedProps.yTickvals.length > 0) {
      yAxis.tickmode = 'array';
      yAxis.tickvals = sharedProps.yTickvals;
    }

    // Ensure labels are shown
    if (yAxis.showticklabels === undefined) {
      yAxis.showticklabels = true;
    }

    // Normalize geometry for standardized Y axes
    normalizePanelGeometryForSharedAxis(layout, composition, panel, 'y');
  }
}

/**
 * Calculate shared axis properties (ranges, titles, scales) for panels that should share axes
 * This loads all relevant panels and finds the union of their ranges and standardized titles/scales
 * NOTE: This function loads panels WITHOUT composition context to avoid recursion
 */
async function calculateSharedAxisRanges(
  composition: FigureComposition,
  panel: FigurePanel,
  axesSharing: AxesSharing,
  loadPanelData: (panel: FigurePanel) => Promise<{ data: Data[]; layout: Partial<Layout> }>
): Promise<{ 
  xRange?: [number, number]; 
  yRange?: [number, number];
  xTitle?: string; // Standardized title text
  yTitle?: string; // Standardized title text
  xScale?: string; // 'linear', 'log', 'date', 'category', etc.
  yScale?: string;
  xTickMode?: 'array'; // Always 'array' when standardized
  yTickMode?: 'array'; // Always 'array' when standardized
  xTickvals?: number[]; // Canonical explicit tick positions for this axis group
  yTickvals?: number[]; // Canonical explicit tick positions for this axis group
}> {
  const panelCol = panel.x;
  const panelRow = panel.y;
  
  // Base candidates: panels that can participate (have plotId and don't override axes)
  const baseCandidates = composition.panels.filter((p) => {
    const axesOverride = p.layoutOverrides?.axesOverride ?? false;
    return !axesOverride && p.plotId && p.id !== panel.id;
  });
  
  // X cohorts: determine which panels to check for X-axis sharing
  let xPanelsToCheck: FigurePanel[];
  if (axesSharing.shareXAcrossAll) {
    // True global X: check all panels
    xPanelsToCheck = baseCandidates;
  } else if (axesSharing.standardizeXScales) {
    // ✅ Standardize X only within same column (panel.x)
    xPanelsToCheck = baseCandidates.filter((p) => p.x === panelCol);
  } else if (axesSharing.shareXAcrossColumns) {
    // Share X across columns (legacy behavior, but still column-scoped)
    const gridUnitWidth = composition.grid.cols / 12;
    const panelColGroup = Math.floor(panel.x / gridUnitWidth);
    xPanelsToCheck = baseCandidates.filter(
      (p) => Math.floor(p.x / gridUnitWidth) === panelColGroup
    );
  } else {
    // No X sharing: only check current panel
    xPanelsToCheck = [];
  }
  
  // Y cohorts: determine which panels to check for Y-axis sharing
  let yPanelsToCheck: FigurePanel[];
  // Note: shareYAcrossAll doesn't exist in AxesSharing interface, only shareYAcrossRows
  if (axesSharing.shareYAcrossRows && axesSharing.standardizeYScales) {
    // If both shareYAcrossRows and standardizeYScales are enabled, check all panels for matching Y axes
    yPanelsToCheck = baseCandidates;
  } else if (axesSharing.standardizeYScales) {
    // ✅ Standardize Y only within same row (panel.y)
    yPanelsToCheck = baseCandidates.filter((p) => p.y === panelRow);
  } else if (axesSharing.shareYAcrossRows) {
    // Share Y across rows (row-scoped)
    yPanelsToCheck = baseCandidates.filter((p) => getPanelRow(p) === getPanelRow(panel));
  } else {
    // No Y sharing: only check current panel
    yPanelsToCheck = [];
  }
  
  // If no panels to check for either axis, return early
  if (xPanelsToCheck.length === 0 && yPanelsToCheck.length === 0) {
    return {};
  }
  
  // Load all relevant panels WITHOUT composition context to avoid recursion
  // We just need their raw axis ranges
  const allPanelsToCheck = [...new Set([...xPanelsToCheck, ...yPanelsToCheck])];
  const panelData = await Promise.all(
    allPanelsToCheck.map(p => loadPanelData(p).catch(() => null))
  );
  
  const validPanels = panelData.filter((d): d is { data: Data[]; layout: Partial<Layout> } => d !== null);
  
  let xRange: [number, number] | undefined;
  let yRange: [number, number] | undefined;
  
  // Also include current panel's range
  const currentPanelData = await loadPanelData(panel).catch(() => null);
  const allPanels = currentPanelData ? [currentPanelData, ...validPanels] : validPanels;
  
  /**
   * Extract axis title text (normalized for comparison)
   */
  const getAxisTitle = (layout: Partial<Layout>, axisKey: 'xaxis' | 'yaxis'): string | null => {
    const axis = (layout as any)?.[axisKey];
    if (!axis?.title) return null;
    const title = axis.title;
    const titleText = typeof title === 'string' ? title : (title && typeof title === 'object' ? title.text : undefined);
    if (!titleText) return null;
    // Normalize: lowercase, trim, remove extra whitespace
    return titleText.trim().toLowerCase().replace(/\s+/g, ' ');
  };
  
  /**
   * Check if two axes represent the same variable by comparing titles
   * Returns true if titles match (case-insensitive, normalized)
   */
  const axesMatch = (
    layout1: Partial<Layout>,
    layout2: Partial<Layout>,
    axisKey: 'xaxis' | 'yaxis'
  ): boolean => {
    const title1 = getAxisTitle(layout1, axisKey);
    const title2 = getAxisTitle(layout2, axisKey);
    
    // If both have titles, they must match
    if (title1 && title2) {
      return title1 === title2;
    }
    
    // If neither has a title, consider them matching (fallback to position-based grouping)
    if (!title1 && !title2) {
      return true;
    }
    
    // If only one has a title, they don't match
    return false;
  };
  
  /**
   * Filter panels to only include those with matching axes
   */
  const filterPanelsByAxisMatch = (
    panels: Array<{ data: Data[]; layout: Partial<Layout> }>,
    referenceLayout: Partial<Layout>,
    axisKey: 'xaxis' | 'yaxis'
  ): Array<{ data: Data[]; layout: Partial<Layout> }> => {
    return panels.filter(({ layout }) => axesMatch(referenceLayout, layout, axisKey));
  };
  
  /**
   * Extract axis range from layout or calculate from data
   */
  const getAxisRange = (
    layout: Partial<Layout>,
    axisKey: 'xaxis' | 'yaxis',
    data: Data[]
  ): [number, number] | null => {
    const axis = (layout as any)[axisKey];
    
    // If explicit range is set, use it
    if (axis && Array.isArray(axis.range) && axis.range.length === 2) {
      return [axis.range[0], axis.range[1]];
    }
    
    // Otherwise, try to extract from data
    // This is a simplified extraction - Plotly's actual auto-scaling is more complex
    const values: number[] = [];
    data.forEach((trace: any) => {
      const dataKey = axisKey === 'xaxis' ? 'x' : 'y';
      if (Array.isArray(trace[dataKey])) {
        trace[dataKey].forEach((v: any) => {
          if (typeof v === 'number' && isFinite(v)) {
            values.push(v);
          }
        });
      }
    });
    
    if (values.length > 0) {
      const min = Math.min(...values);
      const max = Math.max(...values);
      // Add small padding (5%)
      const padding = (max - min) * 0.05;
      return [min - padding, max + padding];
    }
    
    return null;
  };
  
  /**
   * Calculate standardized tick positions for aligned axes
   * Always returns explicit tickvals array to ensure perfect alignment across panels
   * Plotly auto-ticks can diverge, so we force explicit tick positions
   */
  const calculateStandardizedTicks = (
    range: [number, number],
    scaleType: string = 'linear'
  ): {
    tickMode: 'array';
    tickvals: number[];
  } => {
    const [min, max] = range;
    
    if (scaleType === 'log') {
      // For log scales, use log-spaced ticks at 1, 2, 5 multiples of each decade
      const minLog = Math.log10(Math.max(min, 1e-10));
      const maxLog = Math.log10(Math.max(max, 1e-10));
      const tickvals: number[] = [];
      const startDecade = Math.floor(minLog);
      const endDecade = Math.ceil(maxLog);
      
      for (let decade = startDecade; decade <= endDecade; decade++) {
        const base = Math.pow(10, decade);
        for (let mult of [1, 2, 5]) {
          const tick = base * mult;
          if (tick >= min && tick <= max) {
            tickvals.push(+tick.toFixed(10)); // Avoid FP drift
          }
        }
      }
      
      return {
        tickMode: 'array',
        tickvals: tickvals.length > 0 ? tickvals : [min, max] // Fallback to range bounds
      };
    } else {
      // For linear scales, always generate explicit tickvals array
      const targetCount = 5; // Aim for ~5 ticks (can be adjusted)
      const span = max - min;
      
      if (span <= 0) {
        return {
          tickMode: 'array',
          tickvals: [min, max]
        };
      }
      
      // Calculate nice step size
      const rawStep = span / targetCount;
      const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
      const normalizedStep = rawStep / magnitude;
      
      // Round to nice numbers: 1, 2, 5, 10, etc.
      let niceStep: number;
      if (normalizedStep <= 1) {
        niceStep = 1;
      } else if (normalizedStep <= 2) {
        niceStep = 2;
      } else if (normalizedStep <= 5) {
        niceStep = 5;
      } else {
        niceStep = 10;
      }
      
      const step = niceStep * magnitude;
      
      // Calculate starting position (round up to nearest multiple of step)
      const start = Math.ceil(min / step) * step;
      const end = Math.floor(max / step) * step;
      
      // Generate explicit tick positions
      const tickvals: number[] = [];
      for (let v = start; v <= end + 1e-9; v += step) {
        tickvals.push(+v.toFixed(10)); // Avoid FP drift
      }
      
      // Ensure at least min and max are included
      if (tickvals.length === 0 || tickvals[0] > min) {
        tickvals.unshift(+min.toFixed(10));
      }
      if (tickvals.length === 0 || tickvals[tickvals.length - 1] < max) {
        tickvals.push(+max.toFixed(10));
      }
      
      // Remove duplicates and sort
      const uniqueTicks = [...new Set(tickvals)].sort((a, b) => a - b);
      
      return {
        tickMode: 'array',
        tickvals: uniqueTicks.length > 0 ? uniqueTicks : [min, max]
      };
    }
  };
  
  // Find union of X-axis ranges (only for panels in xPanelsToCheck with matching X-axes)
  if (xPanelsToCheck.length > 0) {
    const currentLayout = currentPanelData?.layout || {};
    // Filter to only panels with matching X-axis titles (within the xPanelsToCheck cohort)
    const matchingXPanels = filterPanelsByAxisMatch(allPanels, currentLayout, 'xaxis');
    
    const xRanges: [number, number][] = [];
    matchingXPanels.forEach(({ layout, data }) => {
      const range = getAxisRange(layout, 'xaxis', data);
      if (range) {
        xRanges.push(range);
      }
    });
    
    if (xRanges.length > 0) {
      const minX = Math.min(...xRanges.map(r => r[0]));
      const maxX = Math.max(...xRanges.map(r => r[1]));
      xRange = [minX, maxX];
    }
  }
  
  // Find union of Y-axis ranges (only for panels in yPanelsToCheck with matching Y-axes)
  if (yPanelsToCheck.length > 0) {
    const currentLayout = currentPanelData?.layout || {};
    // Filter to only panels with matching Y-axis titles (within the yPanelsToCheck cohort)
    const matchingYPanels = filterPanelsByAxisMatch(allPanels, currentLayout, 'yaxis');
    
    const yRanges: [number, number][] = [];
    matchingYPanels.forEach(({ layout, data }) => {
      const range = getAxisRange(layout, 'yaxis', data);
      if (range) {
        yRanges.push(range);
      }
    });
    
    if (yRanges.length > 0) {
      const minY = Math.min(...yRanges.map(r => r[0]));
      const maxY = Math.max(...yRanges.map(r => r[1]));
      yRange = [minY, maxY];
    }
  }
  
  // Extract and standardize X-axis titles (only from panels with matching X-axes)
  let xTitle: string | undefined;
  if ((axesSharing.shareXAcrossColumns || axesSharing.shareXAcrossAll) && axesSharing.standardizeXTitles) {
    const currentLayout = currentPanelData?.layout || {};
    const matchingXPanels = filterPanelsByAxisMatch(allPanels, currentLayout, 'xaxis');
    
    const xTitles: string[] = [];
    matchingXPanels.forEach(({ layout }) => {
      const axis = (layout as any)?.xaxis;
      if (axis?.title) {
        const title = axis.title;
        const titleText = typeof title === 'string' ? title : (title && typeof title === 'object' ? title.text : undefined);
        if (titleText && titleText.trim().length > 0) {
          xTitles.push(titleText.trim());
        }
      }
    });
    // Use the most common title, or the first non-empty one if all are unique
    if (xTitles.length > 0) {
      const titleCounts = new Map<string, number>();
      xTitles.forEach(t => titleCounts.set(t, (titleCounts.get(t) || 0) + 1));
      let maxCount = 0;
      let mostCommon = xTitles[0]; // Default to first title
      titleCounts.forEach((count, title) => {
        if (count > maxCount) {
          maxCount = count;
          mostCommon = title;
        }
      });
      xTitle = mostCommon;
    }
  }
  
  // Extract and standardize Y-axis titles (only from panels with matching Y-axes)
  let yTitle: string | undefined;
  if (axesSharing.shareYAcrossRows && axesSharing.standardizeYTitles) {
    const currentLayout = currentPanelData?.layout || {};
    const matchingYPanels = filterPanelsByAxisMatch(allPanels, currentLayout, 'yaxis');
    
    const yTitles: string[] = [];
    matchingYPanels.forEach(({ layout }) => {
      const axis = (layout as any)?.yaxis;
      if (axis?.title) {
        const title = axis.title;
        const titleText = typeof title === 'string' ? title : (title && typeof title === 'object' ? title.text : undefined);
        if (titleText && titleText.trim().length > 0) {
          yTitles.push(titleText.trim());
        }
      }
    });
    // Use the most common title, or the first non-empty one if all are unique
    if (yTitles.length > 0) {
      const titleCounts = new Map<string, number>();
      yTitles.forEach(t => titleCounts.set(t, (titleCounts.get(t) || 0) + 1));
      let maxCount = 0;
      let mostCommon = yTitles[0]; // Default to first title
      titleCounts.forEach((count, title) => {
        if (count > maxCount) {
          maxCount = count;
          mostCommon = title;
        }
      });
      yTitle = mostCommon;
    }
  }
  
  // Extract and standardize X-axis scale types (only from panels with matching X-axes)
  let xScale: string | undefined;
  if ((axesSharing.shareXAcrossColumns || axesSharing.shareXAcrossAll) && axesSharing.standardizeXScales) {
    const currentLayout = currentPanelData?.layout || {};
    const matchingXPanels = filterPanelsByAxisMatch(allPanels, currentLayout, 'xaxis');
    
    const xScales: string[] = [];
    matchingXPanels.forEach(({ layout }) => {
      const axis = (layout as any)?.xaxis;
      const scaleType = axis?.type || 'linear'; // Default to linear if not specified
      xScales.push(scaleType);
    });
    // Use the most common scale type, or 'linear' as default
    if (xScales.length > 0) {
      const scaleCounts = new Map<string, number>();
      xScales.forEach(s => scaleCounts.set(s, (scaleCounts.get(s) || 0) + 1));
      let maxCount = 0;
      let mostCommon = 'linear';
      scaleCounts.forEach((count, scale) => {
        if (count > maxCount) {
          maxCount = count;
          mostCommon = scale;
        }
      });
      xScale = mostCommon;
    }
  }
  
  // Extract and standardize Y-axis scale types (only from panels with matching Y-axes)
  let yScale: string | undefined;
  if (axesSharing.shareYAcrossRows && axesSharing.standardizeYScales) {
    const currentLayout = currentPanelData?.layout || {};
    const matchingYPanels = filterPanelsByAxisMatch(allPanels, currentLayout, 'yaxis');
    
    const yScales: string[] = [];
    matchingYPanels.forEach(({ layout }) => {
      const axis = (layout as any)?.yaxis;
      const scaleType = axis?.type || 'linear'; // Default to linear if not specified
      yScales.push(scaleType);
    });
    // Use the most common scale type, or 'linear' as default
    if (yScales.length > 0) {
      const scaleCounts = new Map<string, number>();
      yScales.forEach(s => scaleCounts.set(s, (scaleCounts.get(s) || 0) + 1));
      let maxCount = 0;
      let mostCommon = 'linear';
      scaleCounts.forEach((count, scale) => {
        if (count > maxCount) {
          maxCount = count;
          mostCommon = scale;
        }
      });
      yScale = mostCommon;
    }
  }
  
  // Calculate standardized tick positions when scales are standardized
  // Always use explicit tickvals array to ensure perfect alignment across panels
  let xTickMode: 'array' | undefined;
  let xTickvals: number[] | undefined;
  if (axesSharing.standardizeXScales && xRange && xScale) {
    const tickInfo = calculateStandardizedTicks(xRange, xScale);
    xTickMode = tickInfo.tickMode; // Always 'array'
    xTickvals = tickInfo.tickvals;  // Canonical ticks for this axis group
  }
  
  let yTickMode: 'array' | undefined;
  let yTickvals: number[] | undefined;
  if (axesSharing.standardizeYScales && yRange && yScale) {
    const tickInfo = calculateStandardizedTicks(yRange, yScale);
    yTickMode = tickInfo.tickMode; // Always 'array'
    yTickvals = tickInfo.tickvals;  // Canonical ticks for this axis group
  }
  
  return { 
    xRange, 
    yRange, 
    xTitle, 
    yTitle, 
    xScale, 
    yScale,
    xTickMode,
    yTickMode,
    xTickvals,
    yTickvals
  };
}

// Export lock to prevent concurrent exports
let isExporting = false;

// Local draft storage helpers
const DRAFT_KEY_PREFIX = 'lumi:figure-draft:';

function getDraftKey(projectId: string, figureId?: string): string {
  if (figureId) {
    return `${DRAFT_KEY_PREFIX}figure:${figureId}`;
  }
  return `${DRAFT_KEY_PREFIX}project:${projectId}`;
}

function saveDraftToStorage(projectId: string, figureId: string | null, composition: FigureComposition): void {
  try {
    const draft: FigureDraft = {
      updatedAt: new Date().toISOString(),
      title: composition.title,
      caption: composition.caption || null,
      composition,
      serverUpdatedAt: figureId ? updatedAtToISOString(composition.updatedAt) : null,
    };
    const key = getDraftKey(projectId, figureId || undefined);
    const draftJson = JSON.stringify(draft);
    
    // Check size (5MB limit)
    if (draftJson.length > 5 * 1024 * 1024) {
      console.warn('Draft too large, not saving to localStorage');
      return;
    }
    
    localStorage.setItem(key, draftJson);
  } catch (error: any) {
    if (error?.name === 'QuotaExceededError') {
      console.warn('localStorage quota exceeded, draft not saved');
    } else {
      console.warn('Failed to save draft to localStorage:', error);
    }
  }
}

function loadDraftFromStorage(projectId: string, figureId?: string): FigureDraft | null {
  try {
    const key = getDraftKey(projectId, figureId);
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    
    const draft = JSON.parse(stored) as FigureDraft;
    
    // Check TTL (7 days)
    const draftAge = Date.now() - new Date(draft.updatedAt).getTime();
    const ttlMs = 7 * 24 * 60 * 60 * 1000; // 7 days
    if (draftAge > ttlMs) {
      localStorage.removeItem(key);
      return null;
    }
    
    return draft;
  } catch (error) {
    console.warn('Failed to load draft from localStorage:', error);
    return null;
  }
}

function clearDraftFromStorage(projectId: string, figureId?: string): void {
  try {
    const key = getDraftKey(projectId, figureId);
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('Failed to clear draft from localStorage:', error);
  }
}

// Helper to check if a figure is empty
function isEmptyFigure(composition: FigureComposition): boolean {
  if (!composition.panels || composition.panels.length === 0) {
    return true;
  }
  // Check if all panels lack content
  return composition.panels.every(
    (panel) => !panel.plotId && !(panel as any).imageId && !(panel as any).contentId
  );
}

let pendingCreatePromise: Promise<FigureComposition> | null = null;
let draftSaveTimer: NodeJS.Timeout | null = null;

// Helper to mark composition as dirty and save draft locally
// Note: This function is called from within store actions, so it needs access to get/set
// We'll define it as a closure that captures get/set from the store creation
let markDirtyAndSaveDraft: ((get: () => FigureCompositionState, set: (partial: Partial<FigureCompositionState> | ((state: FigureCompositionState) => Partial<FigureCompositionState>)) => void) => void) | null = null;

const mergeOverrides = (
  existing: PanelLayoutOverrides | undefined,
  updates: Partial<PanelLayoutOverrides>
): PanelLayoutOverrides => {
  const result: PanelLayoutOverrides = { ...(existing ?? {}) };
  for (const [key, value] of Object.entries(updates)) {
    if (value === undefined) {
      delete (result as any)[key];
    } else {
      (result as any)[key] = value;
    }
  }
  return result;
};

/**
 * Escape XML special characters for safe embedding in SVG
 */
function escapeXml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Extract inner content from an SVG string, removing the outer <svg> wrapper
 * Uses DOMParser for robustness, falls back to regex if parsing fails
 */
function extractSvgContent(svgString: string): string {
  try {
    // Remove XML declaration if present
    const cleaned = svgString.replace(/<\?xml[^>]*\?>/i, '').trim();
    
    // Try DOM-based parsing first
    const parser = new DOMParser();
    const doc = parser.parseFromString(cleaned, 'image/svg+xml');
    
    // Check for parsing errors
    const parserError = doc.querySelector('parsererror');
    if (parserError) {
      throw new Error('SVG parsing failed');
    }
    
    const svgElement = doc.documentElement;
    if (!svgElement || svgElement.tagName.toLowerCase() !== 'svg') {
      throw new Error('No SVG element found');
    }
    
    // Extract all child elements
    const children: string[] = [];
    for (let i = 0; i < svgElement.childNodes.length; i++) {
      const child = svgElement.childNodes[i];
      if (child.nodeType === Node.ELEMENT_NODE) {
        children.push((child as Element).outerHTML);
      }
    }
    
    return children.join('');
  } catch (error) {
    // Fallback to regex-based extraction
    const cleaned = svgString.replace(/<\?xml[^>]*\?>/i, '').trim();
    const match = cleaned.match(/<svg[^>]*>([\s\S]*)<\/svg>/i);
    if (match && match[1]) {
      return match[1];
    }
    // If all else fails, try to remove just the opening and closing tags
    return cleaned.replace(/<svg[^>]*>/i, '').replace(/<\/svg>$/i, '').trim();
  }
}

/**
 * Sanitize SVG content by removing script tags and dangerous event handlers
 */
function sanitizeSvg(svgString: string): string {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgString, 'image/svg+xml');
    
    // Remove script elements
    const scripts = doc.querySelectorAll('script');
    scripts.forEach((script) => script.remove());
    
    // Remove event handlers (on* attributes) from all elements
    const allElements = doc.querySelectorAll('*');
    allElements.forEach((el) => {
      const attrs = Array.from(el.attributes);
      attrs.forEach((attr) => {
        if (attr.name.toLowerCase().startsWith('on')) {
          el.removeAttribute(attr.name);
        }
      });
    });
    
    return doc.documentElement.outerHTML;
  } catch (error) {
    // If parsing fails, use regex to remove scripts
    let sanitized = svgString;
    sanitized = sanitized.replace(/<script[\s\S]*?<\/script>/gi, '');
    sanitized = sanitized.replace(/\s+on\w+\s*=\s*["'][^"']*["']/gi, '');
    return sanitized;
  }
}

/**
 * Extract legend element from a panel SVG
 * Returns the legend markup as a string, or null if not found
 */
function extractLegendFromSvg(svgString: string): string | null {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgString, 'image/svg+xml');
    
    // Plotly legends are typically in a <g> element with class="legend"
    const legendElement = doc.querySelector('g.legend');
    if (legendElement) {
      return legendElement.outerHTML;
    }
    
    // Alternative: look for elements with data attributes or IDs containing "legend"
    const legendById = doc.querySelector('[id*="legend" i]');
    if (legendById) {
      return legendById.outerHTML;
    }
    
    return null;
  } catch (error) {
    console.warn('Failed to extract legend from SVG:', error);
    return null;
  }
}

/**
 * Render annotations as SVG string for export
 */
function renderAnnotationsToSvg(
  annotations: PanelAnnotation[],
  panelWidth: number,
  panelHeight: number,
  theme: FigureCompositionTheme
): string {
  if (annotations.length === 0) return '';

  let svg = '';
  
  for (const annotation of annotations) {
    const x = annotation.x * panelWidth;
    const y = annotation.y * panelHeight;
    const x2 = annotation.x2 !== undefined ? annotation.x2 * panelWidth : undefined;
    const y2 = annotation.y2 !== undefined ? annotation.y2 * panelHeight : undefined;

    // Calculate font size: use annotation's explicit size, or base size (10pt) scaled by fontScale
    const baseAnnotationSize = 10; // Base size for annotations
    const fontScale = theme.fontScale ?? (theme.fontSize ? theme.fontSize / 8 : 1.0);
    const fontSize = annotation.style?.fontSizePt ?? Math.round(baseAnnotationSize * fontScale);
    const color = annotation.style?.color ?? '#000000';
    const fontWeight = annotation.style?.fontWeight ?? 'normal';
    const fontStyle = annotation.style?.fontStyle ?? 'normal';
    const opacity = annotation.style?.opacity ?? 1;
    const strokeWidth = annotation.style?.strokeWidthPx ?? 2;

    switch (annotation.type) {
      case 'text': {
        svg += `<text x="${x}" y="${y}" fill="${escapeXml(color)}" font-size="${fontSize}" font-weight="${fontWeight}" font-style="${fontStyle}" opacity="${opacity}" font-family="${escapeXml(theme.fontFamily)}" text-anchor="start" dominant-baseline="hanging">${escapeXml(annotation.content || 'Text')}</text>`;
        break;
      }
      case 'arrow': {
        if (x2 === undefined || y2 === undefined) break;
        
        const dx = x2 - x;
        const dy = y2 - y;
        const length = Math.sqrt(dx * dx + dy * dy);
        const angle = Math.atan2(dy, dx);
        const arrowheadLength = Math.min(10, length * 0.15);
        const arrowheadAngle = Math.PI / 6;
        const arrowheadX1 = x2 - arrowheadLength * Math.cos(angle - arrowheadAngle);
        const arrowheadY1 = y2 - arrowheadLength * Math.sin(angle - arrowheadAngle);
        const arrowheadX2 = x2 - arrowheadLength * Math.cos(angle + arrowheadAngle);
        const arrowheadY2 = y2 - arrowheadLength * Math.sin(angle + arrowheadAngle);
        
        svg += `<line x1="${x}" y1="${y}" x2="${x2}" y2="${y2}" stroke="${escapeXml(color)}" stroke-width="${strokeWidth}" opacity="${opacity}" stroke-linecap="round"/>`;
        svg += `<polygon points="${x2},${y2} ${arrowheadX1},${arrowheadY1} ${arrowheadX2},${arrowheadY2}" fill="${escapeXml(color)}" opacity="${opacity}"/>`;
        if (annotation.content) {
          svg += `<text x="${(x + x2) / 2}" y="${(y + y2) / 2 - 5}" fill="${escapeXml(color)}" font-size="${fontSize}" font-weight="${fontWeight}" font-style="${fontStyle}" opacity="${opacity}" font-family="${escapeXml(theme.fontFamily)}" text-anchor="middle" dominant-baseline="baseline">${escapeXml(annotation.content)}</text>`;
        }
        break;
      }
      case 'bracket': {
        if (x2 === undefined || y2 === undefined) break;
        
        const bracketHeight = 8;
        const isHorizontal = Math.abs(y2 - y) < Math.abs(x2 - x);
        
        if (isHorizontal) {
          const leftX = Math.min(x, x2);
          const rightX = Math.max(x, x2);
          const centerY = (y + y2) / 2;
          
          svg += `<line x1="${leftX}" y1="${centerY}" x2="${rightX}" y2="${centerY}" stroke="${escapeXml(color)}" stroke-width="${strokeWidth}" opacity="${opacity}"/>`;
          svg += `<line x1="${leftX}" y1="${centerY - bracketHeight / 2}" x2="${leftX}" y2="${centerY + bracketHeight / 2}" stroke="${escapeXml(color)}" stroke-width="${strokeWidth}" opacity="${opacity}"/>`;
          svg += `<line x1="${rightX}" y1="${centerY - bracketHeight / 2}" x2="${rightX}" y2="${centerY + bracketHeight / 2}" stroke="${escapeXml(color)}" stroke-width="${strokeWidth}" opacity="${opacity}"/>`;
          if (annotation.content) {
            svg += `<text x="${(leftX + rightX) / 2}" y="${centerY - bracketHeight / 2 - 5}" fill="${escapeXml(color)}" font-size="${fontSize}" font-weight="${fontWeight}" font-style="${fontStyle}" opacity="${opacity}" font-family="${escapeXml(theme.fontFamily)}" text-anchor="middle" dominant-baseline="baseline">${escapeXml(annotation.content)}</text>`;
          }
        } else {
          const topY = Math.min(y, y2);
          const bottomY = Math.max(y, y2);
          const centerX = (x + x2) / 2;
          
          svg += `<line x1="${centerX}" y1="${topY}" x2="${centerX}" y2="${bottomY}" stroke="${escapeXml(color)}" stroke-width="${strokeWidth}" opacity="${opacity}"/>`;
          svg += `<line x1="${centerX - bracketHeight / 2}" y1="${topY}" x2="${centerX + bracketHeight / 2}" y2="${topY}" stroke="${escapeXml(color)}" stroke-width="${strokeWidth}" opacity="${opacity}"/>`;
          svg += `<line x1="${centerX - bracketHeight / 2}" y1="${bottomY}" x2="${centerX + bracketHeight / 2}" y2="${bottomY}" stroke="${escapeXml(color)}" stroke-width="${strokeWidth}" opacity="${opacity}"/>`;
          if (annotation.content) {
            svg += `<text x="${centerX + bracketHeight / 2 + 5}" y="${(topY + bottomY) / 2}" fill="${escapeXml(color)}" font-size="${fontSize}" font-weight="${fontWeight}" font-style="${fontStyle}" opacity="${opacity}" font-family="${escapeXml(theme.fontFamily)}" text-anchor="start" dominant-baseline="middle">${escapeXml(annotation.content)}</text>`;
          }
        }
        break;
      }
    }
  }
  
  return svg;
}

// Internal helper function to export a single panel (without lock check)
// Helper function to apply hide flags to layout (matching Panel.tsx logic)
function applyHideFlagsToLayout(layout: Partial<Layout>, panel: FigurePanel): Partial<Layout> {
  const result = { ...layout };
  
  // Apply hideTitle and hideSubtitle flags
  if (result.title) {
    const originalTitle = result.title;
    
    // Handle hideSubtitle first (before hideTitle check)
    let titleToReturn = originalTitle;
    if (panel.layoutOverrides?.hideSubtitle === true && originalTitle.subtitle) {
      const { subtitle, ...titleWithoutSubtitle } = originalTitle;
      titleToReturn = titleWithoutSubtitle;
    }
    
    // If hideTitle is true, hide the main title text
    if (panel.layoutOverrides?.hideTitle === true) {
      if (titleToReturn.subtitle) {
        // Has subtitle: preserve all properties but make title text invisible
        const titleFont = titleToReturn.font || {};
        result.title = {
          ...titleToReturn,
          font: {
            ...titleFont,
            color: 'rgba(0,0,0,0)', // Transparent color to hide text while preserving layout
          },
        };
      } else {
        // No subtitle: can return undefined since there's nothing to preserve
        result.title = undefined;
      }
    } else {
      result.title = titleToReturn;
    }
  }
  
  // Hide annotations and shapes if hideAnnotations is enabled
  if (panel.layoutOverrides?.hideAnnotations === true) {
    result.annotations = undefined;
    result.shapes = undefined;
  }
  
  return result;
}

async function exportPanelToSVGInternal(get: () => FigureCompositionState, panelId: string): Promise<string> {
  const comp = get().currentComposition;
  if (!comp) {
    throw new Error('No composition loaded');
  }

  const panel = comp.panels.find(p => p.id === panelId);
  if (!panel || !panel.plotId) {
    throw new Error(`Panel ${panelId} not found or has no plot`);
  }

  // Export single panel using the same logic as exportToSVGInternal
  const { data, layout } = await get().hydratePanel(panel, comp);

  const baseFontSize = 8;
  const fontScale = comp.theme.fontScale ?? (comp.theme.fontSize ? comp.theme.fontSize / baseFontSize : 1.0);
  const calculatedFontSize = Math.round(baseFontSize * fontScale);
  
  const layoutWidth = (layout as any).width ?? 800;
  const layoutHeight = (layout as any).height ?? 600;
  
  const standardizedY = comp.axesSharing?.standardizeYScales;
  const standardizedX = comp.axesSharing?.standardizeXScales;
  const shouldPreserveNormalizedMargins = standardizedX || standardizedY;
  
  // Apply hide flags first
  const layoutWithHideFlags = applyHideFlagsToLayout(layout, panel);
  
  const normalizedLayout: Partial<Layout> = {
    ...layoutWithHideFlags,
    font: {
      ...(layoutWithHideFlags.font as any),
      family: comp.theme.fontFamily,
      size: calculatedFontSize,
    },
    margin: shouldPreserveNormalizedMargins
      ? (layoutWithHideFlags.margin as any)
      : {
          ...(layoutWithHideFlags.margin as any),
          ...(panel.layoutOverrides?.margin ?? {}),
        },
    showlegend:
      panel.layoutOverrides?.showLegend !== undefined
        ? panel.layoutOverrides.showLegend
        : layoutWithHideFlags.showlegend,
    autosize: false,
    width: layoutWidth,
    height: layoutHeight,
  };

  if (!window.Plotly || !window.Plotly.newPlot || !window.Plotly.toImage) {
    throw new Error('Plotly is not available');
  }

  const tempDiv = document.createElement('div');
  tempDiv.style.position = 'absolute';
  tempDiv.style.left = '-9999px';
  tempDiv.style.width = `${layoutWidth}px`;
  tempDiv.style.height = `${layoutHeight}px`;
  document.body.appendChild(tempDiv);

  // Get mapbox token for mapbox plots
  const mapboxToken = (() => {
    if (typeof window === 'undefined') return '';
    try {
      const env = (import.meta as any).env;
      if (env && env.VITE_MAPBOX_ACCESS_TOKEN) return env.VITE_MAPBOX_ACCESS_TOKEN;
      if ((window as any).__MAPBOX_TOKEN__) return (window as any).__MAPBOX_TOKEN__;
      return '';
    } catch {
      return '';
    }
  })();
  
  try {
    await window.Plotly.newPlot(tempDiv, { data, layout: normalizedLayout }, {
      displayModeBar: false,
      staticPlot: true,
      mapboxAccessToken: mapboxToken,
    });

    const svgUrl = await window.Plotly.toImage(tempDiv, {
      format: 'svg',
      width: layoutWidth,
      height: layoutHeight,
    });

    let svgContent: string;
    if (svgUrl.startsWith('data:image/svg+xml;base64,')) {
      svgContent = atob(svgUrl.split(',')[1]);
    } else if (svgUrl.startsWith('data:image/svg+xml,')) {
      svgContent = decodeURIComponent(svgUrl.split(',')[1]);
    } else {
      svgContent = svgUrl;
    }

    return sanitizeSvg(svgContent);
  } finally {
    if (tempDiv && tempDiv.parentNode === document.body) {
      document.body.removeChild(tempDiv);
    }
  }
}

// Internal helper function for SVG export (without lock check)
async function exportToSVGInternal(get: () => FigureCompositionState): Promise<string> {
  const comp = get().currentComposition;
  if (!comp) {
    throw new Error('No composition loaded');
  }

  const mmToPx = (mm: number): number => {
    return (mm / 25.4) * 96;
  };

  // Try to get actual rendered container dimensions from DOM
  // This ensures we match exactly what react-grid-layout is rendering
  let actualContainerWidth = 0;
  let actualContainerHeight = 0;
  const panelPositions = new Map<string, { x: number; y: number; w: number; h: number }>();
  
  try {
    // Find the react-grid-layout container
    const gridLayout = document.querySelector('.figure-composition-layout') as HTMLElement;
    if (gridLayout) {
      // Get the actual container (parent of react-grid-layout, accounting for padding)
      const container = gridLayout.parentElement;
      if (container) {
        const containerRect = container.getBoundingClientRect();
        // Account for padding (p-4 = 16px on each side)
        const padding = 16;
        actualContainerWidth = containerRect.width - (padding * 2);
        actualContainerHeight = containerRect.height - (padding * 2);
        
        // Get actual panel positions from DOM
        comp.panels.forEach((panel) => {
          const panelElement = gridLayout.querySelector(`[data-panel-id="${panel.id}"]`) as HTMLElement;
          if (panelElement) {
            const panelRect = panelElement.getBoundingClientRect();
            const gridRect = gridLayout.getBoundingClientRect();
            // Calculate position relative to grid layout (accounting for padding)
            panelPositions.set(panel.id, {
              x: panelRect.left - gridRect.left,
              y: panelRect.top - gridRect.top,
              w: panelRect.width,
              h: panelRect.height,
            });
          }
        });
      }
    }
  } catch (error) {
    console.warn('Failed to get actual container dimensions from DOM, using calculated values:', error);
  }

  // Calculate canvas dimensions - prefer DOM dimensions when available
  // When DOM dimensions are available, use them directly as they match what's rendered
  let canvasWidthPx: number;
  let canvasHeightPx: number;
  
  if (actualContainerWidth > 0 && actualContainerHeight > 0) {
    // Use DOM dimensions directly - they represent the actual rendered grid layout
    canvasWidthPx = actualContainerWidth;
    canvasHeightPx = actualContainerHeight;
    
    // Add space for title (top) if it exists
    // Caption is not included in export, so we don't add space for it
    const titleHeight = comp.title ? 40 : 0;
    canvasHeightPx = canvasHeightPx + titleHeight;
    
    console.log(`[exportToSVGInternal] Using DOM dimensions: ${canvasWidthPx.toFixed(1)}×${canvasHeightPx.toFixed(1)}px (grid: ${actualContainerWidth.toFixed(1)}×${actualContainerHeight.toFixed(1)}px + title:${titleHeight}px)`);
  } else {
    // Fallback to calculated dimensions when DOM not available
    canvasWidthPx = mmToPx(comp.canvasSize.width);
    canvasHeightPx = mmToPx(comp.canvasSize.height);
    
    // If we have DOM panel positions but not container dimensions, calculate bounds from panels
    if (panelPositions.size > 0) {
      let maxX = 0;
      let maxY = 0;
      panelPositions.forEach((pos) => {
        maxX = Math.max(maxX, pos.x + pos.w);
        maxY = Math.max(maxY, pos.y + pos.h);
      });
      // Use actual content bounds, but ensure minimum size
      canvasWidthPx = Math.max(canvasWidthPx, maxX);
      canvasHeightPx = Math.max(canvasHeightPx, maxY);
      
      // Add space for title (top) if it exists
      // Caption is not included in export, so we don't add space for it
      const titleHeight = comp.title ? 40 : 0;
      canvasHeightPx = Math.max(canvasHeightPx, maxY + titleHeight);
      
      console.log(`[exportToSVGInternal] Calculated dimensions from panel positions: ${canvasWidthPx.toFixed(1)}×${canvasHeightPx.toFixed(1)}px`);
    } else {
      console.log(`[exportToSVGInternal] Using fallback dimensions from canvasSize: ${canvasWidthPx.toFixed(1)}×${canvasHeightPx.toFixed(1)}px`);
    }
  }
  
  // Calculate grid unit width accounting for margins, matching react-grid-layout's calculation
  // react-grid-layout: colWidth = (containerWidth - margin[0] * (cols + 1)) / cols
  // Position: x = col * colWidth + margin[0] * (col + 1)
  // Position: y = row * (rowHeight + margin[1]) + margin[1]
  // Width: w = cols * colWidth + margin[0] * (cols - 1)
  // Height: h = rows * rowHeight + margin[1] * (rows - 1)
  const marginX = comp.grid.margin[0];
  const marginY = comp.grid.margin[1];
  
  // Use actual container width for column width calculation if available
  // This ensures calculated positions match what react-grid-layout actually rendered
  const gridWidthForCalc = actualContainerWidth > 0 ? actualContainerWidth : canvasWidthPx;
  const colWidth = (gridWidthForCalc - marginX * (comp.grid.cols + 1)) / comp.grid.cols;
  const rowHeight = comp.grid.rowHeight;
  
  console.log(`[exportToSVGInternal] Grid calculations: gridWidth=${gridWidthForCalc.toFixed(1)}, colWidth=${colWidth.toFixed(1)}, rowHeight=${rowHeight}, marginX=${marginX}, marginY=${marginY}, cols=${comp.grid.cols}`);

  const panelExports: Array<{
    panel: FigurePanel;
    svg: string;
    width: number;
    height: number;
  }> = [];

  const failedPanels: string[] = [];
  let tempDiv: HTMLDivElement | null = null;

  for (const panel of comp.panels) {
    if (!panel.plotId) continue;

    tempDiv = null;
    try {
      // Pass composition to hydratePanel so it can apply axes sharing and theme
      const { data, layout } = await get().hydratePanel(panel, comp);

      // Calculate base font size from fontScale (default base is 8pt)
      const baseFontSize = 8;
      const fontScale = comp.theme.fontScale ?? (comp.theme.fontSize ? comp.theme.fontSize / baseFontSize : 1.0);
      const calculatedFontSize = Math.round(baseFontSize * fontScale);
      
      // Use normalized dimensions from layout if available (set by normalizePanelGeometryForSharedAxis)
      // This ensures export respects the standardized geometry
      const layoutWidth = (layout as any).width ?? 800;
      const layoutHeight = (layout as any).height ?? 600;
      
      // For standardized axes, preserve normalized margins from normalizePanelGeometryForSharedAxis
      // Do not allow panel.layoutOverrides?.margin to override them, as this would break alignment
      const standardizedY = comp.axesSharing?.standardizeYScales;
      const standardizedX = comp.axesSharing?.standardizeXScales;
      const shouldPreserveNormalizedMargins = standardizedX || standardizedY;
      
      // Apply hide flags first
      const layoutWithHideFlags = applyHideFlagsToLayout(layout, panel);
      
      // Determine if this is a multi-panel figure
      const isMultiPanel = comp.panels.length >= 2;
      const isFirstPanel = panel.id === comp.panels[0]?.id;
      const legendPosition = comp.legendPosition ?? 'auto';
      
      // For multi-panel figures, apply legend layout to first panel only
      // Hide legends on other panels
      let showlegendValue: boolean;
      if (isMultiPanel) {
        if (isFirstPanel) {
          // First panel: apply legend layout based on legendPosition
          showlegendValue = legendPosition !== 'none';
        } else {
          // Other panels: hide legend
          showlegendValue = false;
        }
      } else {
        // Single panel: use existing logic
        showlegendValue = panel.layoutOverrides?.showLegend !== undefined
          ? panel.layoutOverrides.showLegend
          : layoutWithHideFlags.showlegend ?? false;
      }
      
      let normalizedLayout: Partial<Layout> = {
        ...layoutWithHideFlags,
        font: {
          ...(layoutWithHideFlags.font as any),
          family: comp.theme.fontFamily,
          size: calculatedFontSize,
        },
        // Preserve normalized margins when axes are standardized to ensure perfect alignment
        margin: shouldPreserveNormalizedMargins
          ? (layoutWithHideFlags.margin as any) // Use normalized margins from normalizePanelGeometryForSharedAxis
          : {
              ...(layoutWithHideFlags.margin as any),
              ...(panel.layoutOverrides?.margin ?? {}),
            },
        showlegend: showlegendValue,
        autosize: false,
        width: layoutWidth,
        height: layoutHeight,
      };
      
      // Apply legend layout to first panel in multi-panel figures
      if (isMultiPanel && isFirstPanel && legendPosition !== 'none') {
        normalizedLayout = applyLegendLayout(normalizedLayout, legendPosition, isMultiPanel);
      }

      if (!window.Plotly || !window.Plotly.newPlot || !window.Plotly.toImage) {
        throw new Error('Plotly is not available');
      }

      // Get actual DOM panel dimensions if available - these are the EXACT rendered dimensions
      const domPos = panelPositions.get(panel.id);
      const actualPanelWidth = domPos?.w ?? layoutWidth;
      const actualPanelHeight = domPos?.h ?? layoutHeight;
      
      console.log(`[exportToSVGInternal] Panel ${panel.id} export dimensions:`, {
        domWidth: domPos?.w,
        domHeight: domPos?.h,
        layoutWidth,
        layoutHeight,
        usingWidth: actualPanelWidth,
        usingHeight: actualPanelHeight,
      });

      // Scale the figure to match actual panel dimensions using scaleFigureToPanel
      // This ensures fonts, margins, and all elements scale correctly, just like in the Panel component
      const scaledFigure = scaleFigureToPanel(
        { data, layout: normalizedLayout },
        actualPanelWidth,
        actualPanelHeight
      );
      
      const exportLayout = scaledFigure.layout;

      // Validate dimensions before proceeding
      if (actualPanelWidth <= 0 || actualPanelHeight <= 0) {
        throw new Error(`Invalid panel dimensions: width=${actualPanelWidth}, height=${actualPanelHeight}`);
      }

      tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.width = `${actualPanelWidth}px`;
      tempDiv.style.height = `${actualPanelHeight}px`;
      document.body.appendChild(tempDiv);

      try {
        // Ensure Plotly is available
        if (!window.Plotly || !window.Plotly.newPlot) {
          throw new Error('Plotly is not available');
        }

        console.log(`[exportToSVGInternal] Rendering Plotly plot for panel ${panel.id} at ${actualPanelWidth}×${actualPanelHeight}px`);
        
        // Get mapbox token for mapbox plots
        const mapboxToken = (() => {
          if (typeof window === 'undefined') return '';
          try {
            const env = (import.meta as any).env;
            if (env && env.VITE_MAPBOX_ACCESS_TOKEN) return env.VITE_MAPBOX_ACCESS_TOKEN;
            if ((window as any).__MAPBOX_TOKEN__) return (window as any).__MAPBOX_TOKEN__;
            return '';
          } catch {
            return '';
          }
        })();
        
        await window.Plotly.newPlot(tempDiv, { data: scaledFigure.data, layout: exportLayout }, {
          displayModeBar: false,
          staticPlot: true,
          mapboxAccessToken: mapboxToken,
        });

        // Use actual panel dimensions for export to match what's rendered
        if (!window.Plotly.toImage) {
          throw new Error('Plotly.toImage is not available');
        }

        const svgUrl = await window.Plotly.toImage(tempDiv, {
          format: 'svg',
          width: actualPanelWidth,
          height: actualPanelHeight,
        });

        let svgContent: string;
        if (svgUrl.startsWith('data:image/svg+xml;base64,')) {
          svgContent = atob(svgUrl.split(',')[1]);
        } else if (svgUrl.startsWith('data:image/svg+xml,')) {
          svgContent = decodeURIComponent(svgUrl.split(',')[1]);
        } else {
          svgContent = svgUrl;
        }

        // Sanitize SVG before storing
        const sanitizedSvg = sanitizeSvg(svgContent);

        panelExports.push({
          panel,
          svg: sanitizedSvg,
          width: actualPanelWidth,
          height: actualPanelHeight,
        });
      } finally {
        if (tempDiv && tempDiv.parentNode === document.body) {
          document.body.removeChild(tempDiv);
        }
        tempDiv = null;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorStack = error instanceof Error ? error.stack : undefined;
      console.error(`Failed to export panel ${panel.label || panel.id}:`, {
        error,
        errorMessage,
        errorStack,
        panelId: panel.id,
        plotId: panel.plotId,
        domPos: panelPositions.get(panel.id),
      });
      failedPanels.push(panel.label || panel.id);
      
      // Clean up tempDiv if it exists
      if (tempDiv && tempDiv.parentNode === document.body) {
        document.body.removeChild(tempDiv);
      }
      tempDiv = null;
    }
  }

  if (panelExports.length === 0) {
    const errorMsg = failedPanels.length > 0
      ? `Failed to export all panels: ${failedPanels.join(', ')}`
      : 'No panels to export';
    throw new Error(errorMsg);
  }

  // Calculate panel positions - use actual DOM positions if available, otherwise calculate
  // For standardized axes, use group-based scale factors to ensure perfect alignment
  const sharing = comp.axesSharing;
  
  // Ensure scale groups are computed (they're client-side computed fields)
  // Recompute if not present (e.g., from cold load)
  let lockedX = comp.lockedXScaleGroups;
  let lockedY = comp.lockedYScaleGroups;
  
  if (!lockedX && sharing?.standardizeXScales) {
    lockedX = computeLockedXScaleGroups(comp, comp.grid, comp.canvasSize);
  }
  
  if (!lockedY && sharing?.standardizeYScales) {
    lockedY = computeLockedYScaleGroups(comp, comp.grid, comp.canvasSize);
  }
  
  const canonicalWidth = 640;
  const canonicalHeight = 320;
  
  function widthForPanel(panel: FigurePanel): number {
    // ALWAYS prefer DOM positions when available - they represent what's actually rendered
    const domPos = panelPositions.get(panel.id);
    if (domPos && domPos.w > 0) {
      console.log(`[widthForPanel] Using DOM width for panel ${panel.id}: ${domPos.w.toFixed(1)}px`);
      return domPos.w;
    }
    
    // Fallback to calculated width when DOM position not available
    const colKey = `col:${panel.x}`;
    let factor = 0;
    
    if (sharing?.standardizeXScales && lockedX?.[colKey]) {
      factor = lockedX[colKey].scaleFactor;
    } else if (sharing?.shareXAcrossAll) {
      // For true global X, use calculated width
      const calculated = panel.w * colWidth + marginX * (panel.w - 1);
      console.log(`[widthForPanel] Calculated width for panel ${panel.id} (shareXAcrossAll): ${calculated.toFixed(1)}px`);
      return calculated;
    } else {
      // No X sharing: use calculated width
      const calculated = panel.w * colWidth + marginX * (panel.w - 1);
      console.log(`[widthForPanel] Calculated width for panel ${panel.id}: ${calculated.toFixed(1)}px`);
      return calculated;
    }
    
    if (!factor || factor <= 0) {
      const calculated = panel.w * colWidth + marginX * (panel.w - 1);
      console.log(`[widthForPanel] Calculated width for panel ${panel.id} (no factor): ${calculated.toFixed(1)}px`);
      return calculated;
    }
    
    const standardized = Math.round(canonicalWidth * factor);
    console.log(`[widthForPanel] Standardized width for panel ${panel.id}: ${standardized}px (factor: ${factor})`);
    return standardized;
  }
  
  function heightForPanel(panel: FigurePanel): number {
    // ALWAYS prefer DOM positions when available - they represent what's actually rendered
    const domPos = panelPositions.get(panel.id);
    if (domPos && domPos.h > 0) {
      console.log(`[heightForPanel] Using DOM height for panel ${panel.id}: ${domPos.h.toFixed(1)}px`);
      return domPos.h;
    }
    
    // Fallback to calculated height when DOM position not available
    const rowKey = `row:${panel.y}`;
    let factor = 0;
    
    if (sharing?.standardizeYScales && lockedY?.[rowKey]) {
      factor = lockedY[rowKey].scaleFactor;
    } else if (sharing?.shareYAcrossRows) {
      // For shareYAcrossRows, use calculated height
      const calculated = panel.h * rowHeight + marginY * (panel.h - 1);
      console.log(`[heightForPanel] Calculated height for panel ${panel.id} (shareYAcrossRows): ${calculated.toFixed(1)}px`);
      return calculated;
    } else {
      // No Y sharing: use calculated height
      const calculated = panel.h * rowHeight + marginY * (panel.h - 1);
      console.log(`[heightForPanel] Calculated height for panel ${panel.id}: ${calculated.toFixed(1)}px`);
      return calculated;
    }
    
    if (!factor || factor <= 0) {
      const calculated = panel.h * rowHeight + marginY * (panel.h - 1);
      console.log(`[heightForPanel] Calculated height for panel ${panel.id} (no factor): ${calculated.toFixed(1)}px`);
      return calculated;
    }
    
    const standardized = Math.round(canonicalHeight * factor);
    console.log(`[heightForPanel] Standardized height for panel ${panel.id}: ${standardized}px (factor: ${factor})`);
    return standardized;
  }
  
  const xPx = (panel: FigurePanel) => {
    const domPos = panelPositions.get(panel.id);
    if (domPos) return domPos.x;
    return panel.x * colWidth + marginX * (panel.x + 1);
  };
  const yPx = (panel: FigurePanel) => {
    const domPos = panelPositions.get(panel.id);
    if (domPos) return domPos.y;
    return panel.y * (rowHeight + marginY) + marginY;
  };
  const wPx = widthForPanel;
  const hPx = heightForPanel;

  // Create SVG with both mm dimensions (for print) and pixel dimensions (for display/PNG export)
  // Include explicit pixel width/height for better browser compatibility
  let svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="${canvasWidthPx}" height="${canvasHeightPx}" viewBox="0 0 ${canvasWidthPx} ${canvasHeightPx}" style="width: ${comp.canvasSize.width}mm; height: ${comp.canvasSize.height}mm;">`;

  // Calculate font sizes from fontScale
  const baseFontSize = 8;
  const fontScale = comp.theme.fontScale ?? (comp.theme.fontSize ? comp.theme.fontSize / baseFontSize : 1.0);
  const titleFontSize = Math.round((baseFontSize + 4) * fontScale); // Title is base + 4, scaled
  
  // Title positioning - account for space at top
  const titleY = 24;
  if (comp.title) {
    svgContent += `<text x="${canvasWidthPx / 2}" y="${titleY}" text-anchor="middle" font-family="${escapeXml(comp.theme.fontFamily)}" font-size="${titleFontSize}" font-weight="bold">${escapeXml(comp.title)}</text>`;
  }

  // Extract figure-level legend if enabled
  let figureLegendMarkup: string | null = null;
  if (comp.figureLegend?.enabled && comp.figureLegend.panelId) {
    const legendPanelExport = panelExports.find(
      (item) => item.panel.id === comp.figureLegend?.panelId
    );
    if (legendPanelExport) {
      figureLegendMarkup = extractLegendFromSvg(legendPanelExport.svg);
      if (!figureLegendMarkup) {
        console.warn(
          `Figure-level legend enabled for panel ${comp.figureLegend.panelId}, but legend not found in SVG`
        );
      }
    } else {
      console.warn(
        `Figure-level legend enabled for panel ${comp.figureLegend.panelId}, but panel not found in exports`
      );
    }
  }

  // Calculate title height offset - panels need to be offset by title height in SVG
  // because title is rendered above the grid layout in the DOM, but in SVG we include it
  // Use the same titleY value (already declared above) plus some spacing to match the actual title height
  const titleHeight = comp.title ? titleY + 16 : 0; // titleY + spacing
  
  for (const exportItem of panelExports) {
    const panel = exportItem.panel;
    const x = xPx(panel);
    const originalY = yPx(panel);
    let y = originalY;
    const w = wPx(panel);
    const h = hPx(panel);
    
    // Offset panel Y position by title height to account for title space in SVG
    // DOM positions are relative to grid layout (no title), but SVG includes title at top
    y = y + titleHeight;
    
    console.log(`[exportToSVGInternal] Panel ${panel.id} position: x=${x.toFixed(1)}, y=${y.toFixed(1)} (original y=${originalY.toFixed(1)} + titleHeight=${titleHeight}), w=${w.toFixed(1)}, h=${h.toFixed(1)}`);
    
    // Calculate scale factors maintaining aspect ratio to prevent squeezing
    // Use uniform scale (min of scaleX/scaleY) to preserve aspect ratio
    const scaleX = w / exportItem.width;
    const scaleY = h / exportItem.height;
    const uniformScale = Math.min(scaleX, scaleY);
    
    // Calculate centered position if we're using uniform scale
    const scaledWidth = exportItem.width * uniformScale;
    const scaledHeight = exportItem.height * uniformScale;
    const offsetX = (w - scaledWidth) / 2;
    const offsetY = (h - scaledHeight) / 2;

    const innerSvg = extractSvgContent(exportItem.svg);
    // Use uniform scale and center the content within the panel bounds
    svgContent += `<g transform="translate(${x + offsetX},${y + offsetY}) scale(${uniformScale})">${innerSvg}</g>`;

    // Add panel label if it exists
    // Note: y already includes titleHeight offset, so labels are positioned correctly
    if (panel.label) {
      const labelStyle = panel.labelStyle ?? {};
      const position = labelStyle.position ?? 'top-left';
      // Calculate label font size: use explicit size or base (10pt) scaled by fontScale
      const baseLabelSize = 10;
      const fontScale = comp.theme.fontScale ?? (comp.theme.fontSize ? comp.theme.fontSize / 8 : 1.0);
      const fontSize = labelStyle.fontSize ?? Math.round(baseLabelSize * fontScale);
      const fontWeight = labelStyle.fontWeight ?? 'bold';
      const fontStyle = labelStyle.fontStyle ?? 'normal';
      
      // Calculate label position based on labelStyle
      // Match display positioning: top-2 = 8px, left-2 = 8px (from PanelLabel.tsx)
      // Use consistent 8px offset to match CSS classes
      // Note: y already includes titleHeight offset, so labels are positioned correctly relative to panels
      const labelOffset = 8;
      let labelX = x + labelOffset;
      let labelY = y + labelOffset + fontSize; // Add fontSize to account for text baseline
      if (position === 'top-right') {
        labelX = x + w - labelOffset;
        labelY = y + labelOffset + fontSize;
      } else if (position === 'bottom-left') {
        labelX = x + labelOffset;
        labelY = y + h - labelOffset;
      } else if (position === 'bottom-right') {
        labelX = x + w - labelOffset;
        labelY = y + h - labelOffset;
      }
      
      svgContent += `<text x="${labelX}" y="${labelY}" font-family="${escapeXml(
        comp.theme.fontFamily
      )}" font-size="${fontSize}" font-weight="${fontWeight}" font-style="${fontStyle}" fill="#000000">${escapeXml(
        panel.label
      )}</text>`;
    }

    // Add annotations if they exist
    if (panel.annotations && panel.annotations.length > 0) {
      // Render annotations at panel scale
      const annotationSvg = renderAnnotationsToSvg(
        panel.annotations,
        w,
        h,
        comp.theme
      );
      if (annotationSvg) {
        svgContent += `<g transform="translate(${x},${y})">${annotationSvg}</g>`;
      }
    }
  }

  // Add figure-level legend if available
  if (figureLegendMarkup && comp.figureLegend?.enabled) {
    const position = comp.figureLegend.position || 'top-right';
    const padding = 15;
    let legendX = 0;
    let legendY = 0;

    switch (position) {
      case 'top-right':
        legendX = canvasWidthPx - padding;
        legendY = padding;
        break;
      case 'top-left':
        legendX = padding;
        legendY = padding;
        break;
      case 'bottom-right':
        legendX = canvasWidthPx - padding;
        legendY = canvasHeightPx - padding;
        break;
      case 'bottom-left':
        legendX = padding;
        legendY = canvasHeightPx - padding;
        break;
    }

    // Wrap legend in a group with appropriate positioning
    // Note: The legend markup already contains its own positioning, so we may need to adjust
    // For now, we'll insert it at the calculated position
    svgContent += `<g transform="translate(${legendX},${legendY})">${figureLegendMarkup}</g>`;
  }

  // Caption removed from export - not included in exported images

  svgContent += '</svg>';

  // Include warning about failed panels if any
  if (failedPanels.length > 0) {
    console.warn(`Some panels failed to export: ${failedPanels.join(', ')}`);
  }

  return svgContent;
}

export const useFigureCompositionStore = create<FigureCompositionState>((set, get) => {
  // Initialize the helper function with access to set/get
  markDirtyAndSaveDraft = (getFn: () => FigureCompositionState, setFn: typeof set) => {
    const state = getFn();
    const composition = state.currentComposition;
    if (!composition) return;

    // Update dirty state
    const isDraft = state.isDraft || !state.figureId;
    setFn((s) => ({
      ...s,
      isDirty: true,
      saveStatus: isDraft ? 'unsaved-draft' : 'unsaved-changes',
    }));

    // Save draft locally (debounced)
    if (draftSaveTimer) {
      clearTimeout(draftSaveTimer);
    }
    draftSaveTimer = setTimeout(() => {
      const currentState = getFn();
      const currentComposition = currentState.currentComposition;
      const projectId = currentState.projectId;
      if (currentComposition && projectId) {
        saveDraftToStorage(projectId, currentState.figureId, currentComposition);
      }
    }, 500);
  };

  return {
  compositions: [],
  currentComposition: null,
  isLoading: false,
  isCreating: false,
  error: null,
  saveStatus: 'saved',
  lastSaveError: null,
  lastSavedAt: null,
  projectId: null,
  figureId: null,
  isDraft: false,
  isDirty: false,
  selectedPanelId: null,
  selectedPanelIds: [],
  selectedAnnotationId: null,

  initNewComposition: (
    projectId: string,
    options?: {
      templatePresetId?: string;
      initialPlot?: {
        plotId: string;
        plotVersionId?: string;
        figureJsonOverride?: {
          data?: any[];
          layout?: Record<string, any>;
        };
      };
    }
  ) => {
    // Create default composition (client-side only, no DB write)
      const defaultComposition: FigureComposition = {
      id: nanoid(), // Temporary ID for client-side use
      projectId,
      title: '',
      caption: '',
      captionMode: 'manual',
      captionAuto: '',
      captionManual: '',
      panels: [],
      canvasSize: {
        width: 180,
        height: 215,
        unit: 'mm',
      },
      grid: {
        cols: 12,
        rowHeight: 40,
        margin: [8, 8],
      },
      theme: {
        fontFamily: 'Arial',
        fontScale: 1.0,
        colorMode: 'RGB',
      },
      legendPosition: 'auto',
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    // Apply template preset if provided
    if (options?.templatePresetId) {
      const preset = FIGURE_TEMPLATE_PRESETS.find((p) => p.id === options.templatePresetId);
      if (preset) {
        defaultComposition.panels = preset.initialPanels.map((p, index) => {
          // Ensure label always exists - use preset label or generate default
          let label = p.label;
          if (!label || label.trim() === '') {
            const charCode = 'a'.charCodeAt(0) + (index % 26);
            const letter = String.fromCharCode(charCode);
            const panelNumber = Math.floor(index / 26);
            label = panelNumber === 0 ? `(${letter})` : `(${letter}${panelNumber})`;
          }
          return {
            id: nanoid(),
            plotId: '',
            plotVersionId: undefined,
            x: p.x,
            y: p.y,
            w: p.w,
            h: p.h,
            label,
            // Set default hideTitle and hideSubtitle to true for all panels
            layoutOverrides: {
              hideTitle: true,
              hideSubtitle: true,
            },
          };
        });
        defaultComposition.templatePresetId = options.templatePresetId;
      }
    }

    // If no panels exist and initialPlot is provided, create a default panel
    // Always use saved server data - no figureJsonOverride
    if (defaultComposition.panels.length === 0 && options?.initialPlot) {
      defaultComposition.panels = [
        {
          id: nanoid(),
          plotId: options.initialPlot.plotId,
          plotVersionId: options.initialPlot.plotVersionId,
          x: 0,
          y: 0,
          w: 12,
          h: 6,
          label: '(a)',
          // Set default hideTitle and hideSubtitle to true for all panels
          layoutOverrides: {
            hideTitle: true,
            hideSubtitle: true,
          },
        },
      ];
    } else if (defaultComposition.panels.length > 0 && options?.initialPlot) {
      // Assign plot to first panel
      const firstPanel = defaultComposition.panels[0];
      firstPanel.plotId = options.initialPlot.plotId;
      if (options.initialPlot.plotVersionId) {
        firstPanel.plotVersionId = options.initialPlot.plotVersionId;
      }
      // Set default hideTitle and hideSubtitle to true for all panels
      if (!firstPanel.layoutOverrides) {
        firstPanel.layoutOverrides = {};
      }
      firstPanel.layoutOverrides.hideTitle = firstPanel.layoutOverrides.hideTitle ?? true;
      firstPanel.layoutOverrides.hideSubtitle = firstPanel.layoutOverrides.hideSubtitle ?? true;
      // Always use saved server data - no figureJsonOverride
    }

    // If initialPlot was provided, ensure panel labels are visible BEFORE setting composition
    // This ensures labels are shown when adding a plot from plot editor
    // Always set to true when adding a plot, regardless of current state
    if (options?.initialPlot) {
      try {
        const currentState = useFigureEditorUiState.getState();
        console.log('🔍 [initNewComposition] Ensuring showPanelLabels is enabled:', { 
          currentShowPanelLabels: currentState.showPanelLabels,
          hasInitialPlot: !!options?.initialPlot,
          panelLabel: defaultComposition.panels[0]?.label
        });
        // Always enable when adding a plot from plot editor
        // Use setTimeout to ensure this happens after React processes the composition state update
        useFigureEditorUiState.getState().updateState({ showPanelLabels: true });
        const updatedState = useFigureEditorUiState.getState();
        console.log('🔍 [initNewComposition] showPanelLabels set to:', updatedState.showPanelLabels);
        
        // Force a re-render by updating again after a microtask to ensure React sees the change
        setTimeout(() => {
          const finalState = useFigureEditorUiState.getState();
          if (!finalState.showPanelLabels) {
            console.log('🔍 [initNewComposition] showPanelLabels was false, forcing update');
            useFigureEditorUiState.getState().updateState({ showPanelLabels: true });
          }
        }, 0);
      } catch (error) {
        console.error('❌ Failed to ensure panel labels are visible:', error);
      }
    }

    // Ensure scale groups are computed if axesSharing is set
    const compositionWithGroups = ensureScaleGroupsComputed(defaultComposition);
    
    set({
      currentComposition: compositionWithGroups,
      projectId,
      figureId: null,
      isDraft: true,
      isDirty: false,
      saveStatus: 'unsaved-draft',
      lastSavedAt: null,
      error: null,
      isLoading: false,
      isCreating: false,
    });

    // If initialPlot was provided, also fetch plot title and update label asynchronously
    // This matches the behavior in addPanel and ensures labels are properly formatted
    if (options?.initialPlot && compositionWithGroups.panels.length > 0 && compositionWithGroups.projectId) {
      const firstPanel = compositionWithGroups.panels[0];
      if (firstPanel.plotId) {
        // Fetch plot title asynchronously and update label
        (async () => {
          try {
            const { apiRequest } = await import('@/lib/queryClient');
            const response = await apiRequest('GET', `/api/projects/${compositionWithGroups.projectId}/plots`);
            const plots = await response.json();
            const plot = plots.find((p: any) => p.id === firstPanel.plotId);
            if (plot && plot.title) {
              // Format as "(a) Title" using the panel's letter label
              const formattedLabel = `${firstPanel.label} ${plot.title}`;
              const { updatePanel: updatePanelLabel } = get();
              updatePanelLabel(firstPanel.id, { label: formattedLabel });
              console.log('🔍 [initNewComposition] Updated panel label with plot title:', {
                panelId: firstPanel.id,
                originalLabel: firstPanel.label,
                formattedLabel
              });
            }
          } catch (error) {
            console.error('Failed to fetch plot title for label:', error);
          }
        })();
      }
    }
  },

  createComposition: async (
    projectId: string,
    options?: {
      templatePresetId?: string;
      initialPlot?: {
        plotId: string;
        plotVersionId?: string;
        figureJsonOverride?: {
          data?: any[];
          layout?: Record<string, any>;
        };
      };
    }
  ) => {
    // This function is now only called from the Save handler
    // It should only be called when saving a draft for the first time
    const state = get();
    if (!state.currentComposition || !state.isDraft) {
      throw new Error('Cannot create composition: no draft to save');
    }

    // Check if figure is empty
    if (isEmptyFigure(state.currentComposition)) {
      throw new Error('Cannot save empty figure. Add at least one panel with a plot before saving.');
    }

    // Check if offline
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw new Error('Cannot save while offline. Changes are stored locally.');
    }

    set({ isLoading: true, isCreating: true, error: null, saveStatus: 'saving' });

    try {
      // Auto-generate title if missing or empty
      let compositionToSave = state.currentComposition;
      if (!compositionToSave.title || compositionToSave.title.trim() === '') {
        const generatedTitle = get().generateFigureTitle(compositionToSave);
        compositionToSave = {
          ...compositionToSave,
          title: generatedTitle,
        };
        set({ currentComposition: compositionToSave });
      }
      
      const payload = compositionToApiPayload(compositionToSave);
      if (options?.templatePresetId) {
        payload.templatePresetId = options.templatePresetId;
      }
      const created = await createFigureComposition(projectId, payload);

      // Clear the project draft and create figure draft
      clearDraftFromStorage(projectId);
      saveDraftToStorage(projectId, created.id, created);

      set((state) => ({
        compositions: [...state.compositions, created],
        currentComposition: created,
        projectId,
        figureId: created.id,
        isDraft: false,
        isDirty: false,
        lastSavedAt: new Date(created.updatedAt),
        saveStatus: 'saved',
        lastSaveError: null,
        isLoading: false,
        isCreating: false,
      }));

      return created;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create composition';
      set({ 
        isLoading: false, 
        isCreating: false, 
        error: errorMessage,
        saveStatus: 'error',
        lastSaveError: errorMessage,
      });
      throw error;
    }
  },

  loadComposition: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const composition = await getFigureComposition(id);
      
      // Check for local draft
      const draft = loadDraftFromStorage(composition.projectId, id);
      const useDraft = draft && draft.updatedAt > updatedAtToISOString(composition.updatedAt);
      
      const finalComposition = useDraft ? draft.composition : composition;
      
      // Ensure scale groups are computed (they're client-side computed fields)
      const compositionWithGroups = ensureScaleGroupsComputed(finalComposition);
      
      // Ensure all panels have labels (fix for panels loaded without labels)
      // CRITICAL: For legacy panels without plotVersionId, ensure they're treated as follow-latest
      // (plotVersionId: undefined means follow latest, which is the default behavior)
      const panelsWithLabels = compositionWithGroups.panels.map((panel, index) => {
        let updatedPanel = { ...panel };
        
        // Fix missing labels
        if (!updatedPanel.label || updatedPanel.label.trim() === '') {
          // Generate a default label based on panel index
          const charCode = 'a'.charCodeAt(0) + (index % 26);
          const letter = String.fromCharCode(charCode);
          const panelNumber = Math.floor(index / 26);
          const defaultLabel = panelNumber === 0 ? `(${letter})` : `(${letter}${panelNumber})`;
          updatedPanel = { ...updatedPanel, label: defaultLabel };
        }
        
        // Legacy panels: if plotVersionId is explicitly null, convert to undefined (follow latest)
        // This handles old saved figures that might have null instead of undefined
        if (updatedPanel.plotVersionId === null) {
          updatedPanel = { ...updatedPanel, plotVersionId: undefined };
        }
        
        return updatedPanel;
      });
      
      // Handle legacy figures: initialize captionMode and captionManual if missing
      const compositionWithCaptionMode: FigureComposition = {
        ...compositionWithGroups,
        panels: panelsWithLabels,
        captionMode: compositionWithGroups.captionMode || 'manual',
        captionManual: compositionWithGroups.captionManual !== undefined 
          ? compositionWithGroups.captionManual 
          : (compositionWithGroups.caption || ''),
        captionAuto: compositionWithGroups.captionAuto || '',
      };
      
      set((state) => {
        const existingIndex = state.compositions.findIndex((c) => c.id === id);
        const updatedCompositions =
          existingIndex >= 0
            ? state.compositions.map((c) => (c.id === id ? compositionWithCaptionMode : c))
            : [...state.compositions, compositionWithCaptionMode];
        return {
          compositions: updatedCompositions,
          currentComposition: compositionWithCaptionMode,
          projectId: compositionWithCaptionMode.projectId,
          figureId: compositionWithCaptionMode.id,
          isDraft: false,
          isDirty: useDraft ? true : false,
          lastSavedAt: useDraft ? new Date(draft.serverUpdatedAt || composition.updatedAt) : new Date(composition.updatedAt),
          saveStatus: useDraft ? 'unsaved-changes' : 'saved',
          lastSaveError: null,
          isLoading: false,
        };
      });
      
      // If in auto mode, recompute caption after loading
      if (compositionWithCaptionMode.captionMode === 'auto') {
        // Use setTimeout to ensure state is set before recomputing
        setTimeout(async () => {
          await get().recomputeAutoCaption();
        }, 0);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load composition';
      set({ isLoading: false, error: errorMessage });
      throw error;
    }
  },

  /**
   * Generate a meaningful title for a figure composition based on its panels
   * Extracts the 4 most common words from panel labels, plot titles, or captions
   */
  generateFigureTitle: (composition: FigureComposition): string => {
    const panels = composition.panels || [];
    
    if (panels.length === 0) {
      return 'Figure';
    }
    
    const plotsState = usePlotsStore.getState();
    
    // Collect panel labels ONLY - extract the 4 most common words from all panel labels
    const panelLabels: string[] = [];
    
    for (const panel of panels) {
      if (panel.label && panel.label.trim()) {
        // Remove panel letter prefix like "(a)" or "a)" if present
        let cleanedLabel = panel.label
          .trim()
          .replace(/^\(?[a-z]\)\s*/i, '') // Remove "(a) " or "a) " prefix
          .trim();
        
        // Only use if there's meaningful content after removing the prefix
        if (cleanedLabel.length > 3) {
          panelLabels.push(cleanedLabel);
        }
      }
    }
    
    // If no panel labels, try to get them from plot titles as fallback
    if (panelLabels.length === 0) {
      for (const panel of panels) {
        const plot = plotsState.plots.find((p) => p.id === panel.plotId) ||
                     plotsState.getPlotByContext?.({ projectId: composition.projectId, id: panel.plotId });
        
        if (plot?.title && plot.title.trim() && plot.title !== 'Untitled Plot' && plot.title !== 'Generated Plot') {
          panelLabels.push(plot.title.trim());
        }
      }
    }
    
    // If still no labels, use captions as last resort
    if (panelLabels.length === 0) {
      for (const panel of panels) {
        const caption = plotsState.getPlotCaption(panel.plotId, composition.projectId);
        if (caption && caption.trim()) {
          panelLabels.push(caption.trim());
        }
      }
    }
    
    if (panelLabels.length === 0) {
      // Fallback to chart types
      const chartTypes: string[] = [];
      for (const panel of panels) {
        const plot = plotsState.plots.find((p) => p.id === panel.plotId);
        if (plot?.plotlyJson?.data?.[0]?.type) {
          const type = String(plot.plotlyJson.data[0].type);
          if (type && !chartTypes.includes(type)) {
            chartTypes.push(type);
          }
        }
      }
      
      if (chartTypes.length > 0) {
        const typeMap: Record<string, string> = {
          'heatmap': 'Heatmap', 'scatter': 'Scatter', 'bar': 'Bar',
          'box': 'Box', 'violin': 'Violin', 'histogram': 'Histogram',
          'dendrogram': 'Dendrogram', 'sankey': 'Sankey',
        };
        const typeLabels = chartTypes.slice(0, 2).map(t => typeMap[t] || t.charAt(0).toUpperCase() + t.slice(1));
        return chartTypes.length === 1 ? typeLabels[0] : typeLabels.join(' & ');
      }
      
      return panels.length === 1 ? 'Figure' : `${panels.length}-Panel Figure`;
    }
    
    // Extract all words from panel labels
    const stopWords = new Set([
      'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from',
      'has', 'he', 'in', 'is', 'it', 'its', 'of', 'on', 'that', 'the',
      'to', 'was', 'will', 'with', 'showing', 'shows', 'showed', 'show',
      'this', 'these', 'those', 'which', 'who', 'whom', 'whose',
      'from', 'into', 'within',
    ]);
    
    const wordCounts = new Map<string, number>();
    
    for (const label of panelLabels) {
      // Normalize: replace colons/punctuation with spaces, but keep hyphens for compound words
      const normalized = label
        .toLowerCase()
        .replace(/[:;.,!?]/g, ' ') // Replace punctuation with spaces
        .replace(/\s+/g, ' ') // Normalize multiple spaces
        .trim();
      
      // Split by spaces - hyphenated words like "high-dose" will stay together
      const words = normalized.split(/\s+/);
      
      for (const word of words) {
        // Clean word: remove any non-word chars except hyphens
        const cleanWord = word.replace(/[^\w-]/g, '').toLowerCase();
        
        // Skip if too short, is a stop word, or is just a number
        if (cleanWord.length <= 2 || stopWords.has(cleanWord) || /^\d+$/.test(cleanWord)) {
          continue;
        }
        
        // For hyphenated words like "high-dose", prefer the compound word
        // Only count individual parts if the compound word is short or common
        if (cleanWord.includes('-')) {
          // Always count the compound word (it's more meaningful)
          wordCounts.set(cleanWord, (wordCounts.get(cleanWord) || 0) + 1);
          
          // Only count individual parts if compound is very short (like "x-axis")
          // For longer compounds like "high-dose", prefer the compound
          if (cleanWord.length <= 8) {
            const parts = cleanWord.split('-');
            for (const part of parts) {
              if (part.length > 2 && !stopWords.has(part)) {
                wordCounts.set(part, (wordCounts.get(part) || 0) + 1);
              }
            }
          }
        } else {
          // Regular word
          wordCounts.set(cleanWord, (wordCounts.get(cleanWord) || 0) + 1);
        }
      }
    }
    
    // Get top 4 most common words
    // When frequencies are tied, prefer longer words (more descriptive)
    const topWords = Array.from(wordCounts.entries())
      .sort((a, b) => {
        // First sort by frequency (descending)
        if (b[1] !== a[1]) {
          return b[1] - a[1];
        }
        // If frequencies are equal, prefer longer words
        return b[0].length - a[0].length;
      })
      .slice(0, 4)
      .map(([word]) => {
        // Capitalize first letter, handle hyphenated words
        if (word.includes('-')) {
          return word.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('-');
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      });
    
    if (topWords.length > 0) {
      const title = topWords.join(' ');
      // Limit to 50 chars for gallery
      return title.length > 50 ? title.substring(0, 47) + '...' : title;
    }
    
    // Fallback: use chart types if no words found
    const chartTypes: string[] = [];
    for (const panel of panels) {
      const plot = plotsState.plots.find((p) => p.id === panel.plotId);
      if (plot?.plotlyJson?.data?.[0]?.type) {
        const type = String(plot.plotlyJson.data[0].type);
        if (type && !chartTypes.includes(type)) {
          chartTypes.push(type);
        }
      }
    }
    
    if (chartTypes.length > 0) {
      const typeMap: Record<string, string> = {
        'heatmap': 'Heatmap', 'scatter': 'Scatter', 'bar': 'Bar',
        'box': 'Box', 'violin': 'Violin', 'histogram': 'Histogram',
        'dendrogram': 'Dendrogram', 'sankey': 'Sankey',
      };
      const typeLabels = chartTypes.slice(0, 2).map(t => typeMap[t] || t.charAt(0).toUpperCase() + t.slice(1));
      return chartTypes.length === 1 ? typeLabels[0] : typeLabels.join(' & ');
    }
    
    // Last resort fallback
    return panels.length === 1 ? 'Figure' : `${panels.length}-Panel Figure`;
  },

  saveComposition: async (comp?: FigureComposition) => {
    const state = get();
    
    // Prevent concurrent save attempts
    if (state.saveStatus === 'saving') {
      return; // Already saving, ignore duplicate calls
    }
    
    let composition = comp ?? state.currentComposition;
    
    if (!composition) {
      throw new Error('No composition to save');
    }

    // Check if figure is empty
    if (isEmptyFigure(composition)) {
      throw new Error('Cannot save empty figure. Add at least one panel with a plot before saving.');
    }
    
    // Ensure captions are loaded and recomputed before saving (especially for auto mode)
    if (composition.captionMode === 'auto') {
      try {
        console.log('[saveComposition] Ensuring captions are loaded before save...');
        await get().recomputeAutoCaption();
        // Update composition with latest caption after recompute
        const updatedState = get();
        if (updatedState.currentComposition) {
          composition = {
            ...composition,
            captionAuto: updatedState.currentComposition.captionAuto,
          };
        }
      } catch (error) {
        console.warn('[saveComposition] Failed to recompute caption before save:', error);
        // Continue with save even if caption recompute fails
      }
    }
    
    // Auto-generate title if missing or empty
    if (!composition.title || composition.title.trim() === '') {
      const generatedTitle = get().generateFigureTitle(composition);
      composition = {
        ...composition,
        title: generatedTitle,
      };
      // Update current composition if we're saving the current one
      if (!comp) {
        set({ currentComposition: composition });
      }
    }

    // Check if offline
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      set({ saveStatus: 'offline' });
      throw new Error('Cannot save while offline. Changes are stored locally.');
    }

    // Handle draft (first save)
    if (state.isDraft || !state.figureId) {
      const projectId = state.projectId || composition.projectId;
      if (!projectId) {
        throw new Error('Cannot save: missing project ID');
      }

      set({ saveStatus: 'saving', error: null, lastSaveError: null });

      try {
        const payload = compositionToApiPayload(composition);
        const created = await createFigureComposition(projectId, payload);
        
        // Ensure scale groups are computed (they're client-side computed fields)
        const createdWithGroups = ensureScaleGroupsComputed(created);

        // Clear the project draft and create figure draft
        clearDraftFromStorage(projectId);
        saveDraftToStorage(projectId, createdWithGroups.id, createdWithGroups);

        set({
          compositions: [...state.compositions, createdWithGroups],
          currentComposition: createdWithGroups,
          projectId,
          figureId: createdWithGroups.id,
          isDraft: false,
          isDirty: false,
          lastSavedAt: new Date(createdWithGroups.updatedAt),
          saveStatus: 'saved',
          lastSaveError: null,
        });

        return;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to create composition';
        set({ 
          saveStatus: 'error',
          error: errorMessage,
          lastSaveError: errorMessage,
        });
        throw error;
      }
    }

    // Handle existing figure (update)
    if (!state.isDirty && !comp) {
      // No changes to save
      return;
    }

    set({ saveStatus: 'saving', error: null, lastSaveError: null });

    try {
      const payload = compositionToApiPayload(composition);
      // Debug: Log layoutOverrides before save
      console.log('🔍 [saveComposition] Before save - panel layoutOverrides:', 
        composition.panels.map(p => ({ 
          id: p.id, 
          layoutOverrides: p.layoutOverrides,
          hasHideTitle: p.layoutOverrides?.hideTitle,
          hasHideSubtitle: p.layoutOverrides?.hideSubtitle 
        }))
      );
      const updated = await updateFigureComposition(composition.id, payload);
      
      // Ensure scale groups are computed (they're client-side computed fields)
      const updatedWithGroups = ensureScaleGroupsComputed(updated);
      
      // Preserve layoutOverrides from original composition if backend doesn't return them
      // Create a map of original layoutOverrides by panel ID
      const originalOverridesByPanelId = new Map(
        composition.panels.map(p => [p.id, p.layoutOverrides])
      );
      
      // Merge original layoutOverrides with API response, preserving original hide flags
      const panelsWithPreservedOverrides = updatedWithGroups.panels.map(panel => {
        const originalOverrides = originalOverridesByPanelId.get(panel.id);
        if (originalOverrides) {
          // Debug: Log merge process
          console.log(`🔍 [saveComposition] Merging overrides for panel ${panel.id}:`, {
            original: {
              hideTitle: originalOverrides.hideTitle,
              hideSubtitle: originalOverrides.hideSubtitle,
              hideAnnotations: originalOverrides.hideAnnotations,
            },
            apiResponse: {
              hideTitle: panel.layoutOverrides?.hideTitle,
              hideSubtitle: panel.layoutOverrides?.hideSubtitle,
              hideAnnotations: panel.layoutOverrides?.hideAnnotations,
            },
          });
          
          // Merge: API response takes precedence for most properties, but always preserve hide flags from original
          // Build merged overrides: start with API response (or empty object if undefined), then override with original hide flags
          const mergedOverrides: PanelLayoutOverrides = {
            ...(panel.layoutOverrides || {}),
          };
          
          // Always preserve hide flags from original composition (user preferences)
          // If original had a value (true or false), use it; otherwise keep API value
          if (originalOverrides.hideTitle !== undefined) {
            mergedOverrides.hideTitle = originalOverrides.hideTitle;
          }
          if (originalOverrides.hideSubtitle !== undefined) {
            mergedOverrides.hideSubtitle = originalOverrides.hideSubtitle;
          }
          if (originalOverrides.hideAnnotations !== undefined) {
            mergedOverrides.hideAnnotations = originalOverrides.hideAnnotations;
          }
          
          console.log(`🔍 [saveComposition] Merged result for panel ${panel.id}:`, {
            hideTitle: mergedOverrides.hideTitle,
            hideSubtitle: mergedOverrides.hideSubtitle,
            hideAnnotations: mergedOverrides.hideAnnotations,
          });
          
          return { ...panel, layoutOverrides: mergedOverrides };
        }
        return panel;
      });
      
      const finalComposition = {
        ...updatedWithGroups,
        panels: panelsWithPreservedOverrides,
      };
      
      // Debug: Log layoutOverrides after load
      console.log('🔍 [saveComposition] After load - panel layoutOverrides:', 
        finalComposition.panels.map(p => ({ 
          id: p.id, 
          layoutOverrides: p.layoutOverrides,
          hasHideTitle: p.layoutOverrides?.hideTitle,
          hasHideSubtitle: p.layoutOverrides?.hideSubtitle 
        }))
      );

      // Update draft storage
      saveDraftToStorage(composition.projectId, finalComposition.id, finalComposition);

      set((state) => ({
        compositions: state.compositions.map((c) => (c.id === finalComposition.id ? finalComposition : c)),
        currentComposition: finalComposition,
        isDirty: false,
        lastSavedAt: new Date(finalComposition.updatedAt),
        saveStatus: 'saved',
        lastSaveError: null,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save composition';
      set({ 
        saveStatus: 'error', 
        error: errorMessage,
        lastSaveError: errorMessage,
      });
      throw error;
    }
  },

  saveDraftLocally: () => {
    const state = get();
    const composition = state.currentComposition;
    const projectId = state.projectId;
    
    if (!composition || !projectId) return;

    // Debounce draft saves
    if (draftSaveTimer) {
      clearTimeout(draftSaveTimer);
    }
    
    draftSaveTimer = setTimeout(() => {
      saveDraftToStorage(projectId, state.figureId, composition);
    }, 500);
  },

  loadDraftIfAny: (projectId: string, figureId?: string) => {
    return loadDraftFromStorage(projectId, figureId);
  },

  clearDraft: (projectId: string, figureId?: string) => {
    clearDraftFromStorage(projectId, figureId);
  },

  // Unified version resolution helper - single source of truth for "effective version" logic
  resolveEffectivePlotVersion: async (args: {
    plotId: string;
    panelVersionId?: string | null;
    projectId?: string;
  }): Promise<{ versionId: string | null; followLatest: boolean }> => {
    const { plotId, panelVersionId, projectId } = args;
    
    // If panelVersionId is defined, panel is pinned to that specific version
    if (panelVersionId) {
      return { versionId: panelVersionId, followLatest: false };
    }
    
    // Panel wants to follow latest - always fetch from API to get the true latest version
    // Don't rely on plots store which might have stale data
    let resolvedVersionId: string | null = null;
    
    // Always fetch latest version metadata directly from the plot's latest endpoint
    // This ensures we get the actual latest version from the database
    try {
      const response = await apiRequest('GET', `/api/ai/v2/plots/${plotId}/versions/latest/meta.json`);
      if (response.ok) {
        const meta = await response.json();
        if (meta?.versionId) {
          resolvedVersionId = meta.versionId;
          // Update plots store with this metadata for future lookups
          try {
            const { usePlotsStore } = await import('@/store/plots');
            const state = usePlotsStore.getState();
            const existingPlot = state.plots && Array.isArray(state.plots) ? state.plots.find(p => p.id === plotId) : null;
            if (existingPlot) {
              usePlotsStore.setState((prev) => ({
                plots: (prev.plots && Array.isArray(prev.plots) ? prev.plots : []).map(p => 
                  p.id === plotId 
                    ? { ...p, latestVersionId: resolvedVersionId! }
                    : p
                ),
              }));
            }
          } catch (updateErr) {
            console.warn(`[resolveEffectivePlotVersion] Failed to update plots store:`, updateErr);
          }
        }
      }
    } catch (apiErr) {
      console.warn(`[resolveEffectivePlotVersion] Failed to fetch latest version metadata from API for plot ${plotId}:`, apiErr);
    }
    
    // Fallback: If API fetch failed or projectId not available, check plots store
    if (!resolvedVersionId) {
      try {
        const { usePlotsStore } = await import('@/store/plots');
        const state = usePlotsStore.getState();
        
        // Check currentPlot first (most likely to be up-to-date)
        const currentPlot = state.currentPlot?.id === plotId ? state.currentPlot : null;
        if (currentPlot?.latestVersionId) {
          resolvedVersionId = currentPlot.latestVersionId;
        }
        
        // Check plots array
        if (!resolvedVersionId && state.plots && Array.isArray(state.plots)) {
          const plotFromArray = state.plots.find(p => p.id === plotId);
          if (plotFromArray?.latestVersionId) {
            resolvedVersionId = plotFromArray.latestVersionId;
          }
        }
        
        // Check byProjectPlots cache
        if (!resolvedVersionId && projectId) {
          const plotFromContext = state.getPlotByContext?.({ projectId, id: plotId });
          if (plotFromContext?.latestVersionId) {
            resolvedVersionId = plotFromContext.latestVersionId;
          }
        }
      } catch (err) {
        console.warn(`[resolveEffectivePlotVersion] Failed to check plots store for plot ${plotId}:`, err);
      }
    }
    
    if (!resolvedVersionId) {
      // Log warning but don't throw - let caller handle null
      console.warn(`[resolveEffectivePlotVersion] Could not resolve versionId for plot ${plotId} - returning null`);
    }
    return { versionId: resolvedVersionId, followLatest: true };
  },

  // Simplified cache key builder - primarily based on plotId and versionId
  panelCacheKey: (plotId: string, versionId: string | null, extraHash?: string): string => {
    const baseKey = `${plotId}:${versionId ?? 'latest'}`;
    return extraHash ? `${baseKey}:${extraHash}` : baseKey;
  },

  // Synchronous version resolver for use in signatures and cache checks
  // Pure browser-safe function that does NOT access stores or use require
  // Only determines if panel is pinned or follows latest
  // Actual version resolution for "latest" happens in hydratePanel (async)
  resolveEffectivePlotVersionSync: (args: {
    plotId: string;
    panelVersionId?: string | null;
    projectId?: string;
  }): { versionId: string | null; isPinned: boolean } => {
    const { panelVersionId } = args;
    
    // Case 1: Panel is explicitly pinned to a specific version
    if (panelVersionId && panelVersionId !== 'latest') {
      return { versionId: panelVersionId, isPinned: true };
    }
    
    // Case 2: Panel follows latest - return 'latest' as sentinel
    // hydratePanel will resolve 'latest' to actual versionId (e.g., '80c93631-031f-4e14-a369-458c1a2d4026')
    // This function does NOT resolve it synchronously to avoid require/store access
    return { versionId: 'latest', isPinned: false };
  },

  // Check if panel has cached data for given parameters
  hasPanelCachedData: (args: {
    plotId: string;
    effectiveVersionId: string | null;
    themeKey: string;
    axesSharingKey: string;
    overridesKey: string;
  }): boolean => {
    const { plotId, effectiveVersionId, themeKey, axesSharingKey, overridesKey } = args;
    const extraHash = `${themeKey}:${axesSharingKey}:${overridesKey}`;
    const cacheKey = get().panelCacheKey(plotId, effectiveVersionId, extraHash);
    return panelCache.has(cacheKey);
  },

  invalidatePanelCacheForPlot: (plotId: string, preservePinnedVersions?: string[]) => {
    const before = panelCache.size;
    const deletedKeys: string[] = [];
    const preservedKeys: string[] = [];
    
    // Preserve pinned versions if provided
    const pinnedSet = preservePinnedVersions ? new Set(preservePinnedVersions) : null;
    
    for (const key of panelCache.keys()) {
      // Check if key starts with `${plotId}:`
      if (key.startsWith(`${plotId}:`)) {
        // Extract versionId from key (format: plotId:versionId:...)
        const parts = key.split(':');
        const versionId = parts.length > 1 ? parts[1] : null;
        
        // Preserve entries for pinned versions
        if (pinnedSet && versionId && pinnedSet.has(versionId)) {
          preservedKeys.push(key);
          continue;
        }
        
        // Delete entries for "latest" key OR if pinnedSet exists and versionId is NOT in it
        // Only delete all entries if no pinnedSet provided (backward compatibility)
        if (versionId === 'latest' || (pinnedSet && versionId && !pinnedSet.has(versionId))) {
          panelCache.delete(key);
          deletedKeys.push(key);
        } else if (!pinnedSet) {
          // Backward compatibility: if no pinnedSet provided, delete all (old behavior)
          panelCache.delete(key);
          deletedKeys.push(key);
        }
      }
    }
    
    try {
      console.log('[figure-composition] invalidatePanelCacheForPlot', {
        plotId,
        before,
        after: panelCache.size,
        deleted: before - panelCache.size,
        preserved: preservedKeys.length,
        deletedKeys: deletedKeys.length > 0 ? deletedKeys.slice(0, 5) : [],
        totalDeleted: deletedKeys.length,
      });
    } catch {}
  },

  setOnlineStatus: (isOnline: boolean) => {
    const state = get();
    if (!state.currentComposition) return;

    // Only update status if we're actually offline and have unsaved changes
    if (!isOnline) {
      // If we have unsaved changes, mark as offline
      if (state.isDirty || state.isDraft) {
        set({ saveStatus: 'offline' });
      }
    } else {
      // When coming back online, restore the appropriate status based on dirty state
      if (state.isDraft) {
        set({ saveStatus: 'unsaved-draft' });
      } else if (state.isDirty) {
        set({ saveStatus: 'unsaved-changes' });
      } else {
        set({ saveStatus: 'saved' });
      }
    }
  },

  listCompositionsForProject: async (projectId: string) => {
    set({ isLoading: true, error: null });
    try {
      const compositions = await listFigureCompositions(projectId);
      // Ensure scale groups are computed for all loaded compositions
      const compositionsWithGroups = compositions.map(ensureScaleGroupsComputed);
      set({ compositions: compositionsWithGroups, isLoading: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to list compositions';
      set({ isLoading: false, error: errorMessage });
      throw error;
    }
  },

  deleteComposition: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await deleteFigureComposition(id);
      set((state) => {
        const updatedCompositions = state.compositions.filter((c) => c.id !== id);
        const updatedCurrent =
          state.currentComposition?.id === id ? null : state.currentComposition;
        return {
          compositions: updatedCompositions,
          currentComposition: updatedCurrent,
          isLoading: false,
        };
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete composition';
      set({ isLoading: false, error: errorMessage });
      throw error;
    }
  },

  setCurrentCompositionId: (id: string | null) => {
    if (id === null) {
      set({ currentComposition: null });
      return;
    }

    const composition = get().compositions.find((c) => c.id === id);
    if (composition) {
      set({ currentComposition: composition });
    }
  },

  updateCurrentComposition: (updates: Partial<FigureComposition>) => {
    const { currentComposition } = get();
    if (!currentComposition) return;

    const updated = {
      ...currentComposition,
      ...updates,
      updatedAt: new Date(),
    };

    set((state) => ({
      currentComposition: updated,
      compositions: state.compositions.map((c) =>
        c.id === updated.id ? updated : c
      ),
      // saveStatus will be updated by markDirtyAndSaveDraft
    }));

    // Mark as dirty and save draft locally
    if (markDirtyAndSaveDraft) {
      markDirtyAndSaveDraft(get, set);
    }
  },

  addPanel: (partial: Omit<FigurePanel, 'id' | 'label'>) => {
    const { currentComposition } = get();
    if (!currentComposition) return;

    // Generate default label (A, B, C, etc.)
    const defaultLabel = generatePanelLabel(currentComposition.panels);
    
    // Always use saved server data - ensure figureJsonOverride is never set
    // (kept for backward compatibility but should never be used)
    // Explicitly set plotVersionId to undefined if not provided to ensure "follow latest" behavior
    const cleanedPartial = {
      ...partial,
      figureJsonOverride: undefined,
      // Ensure plotVersionId is explicitly undefined (not omitted) when not provided
      // This ensures version resolution works correctly (undefined = follow latest)
      plotVersionId: partial.plotVersionId ?? undefined,
    };
    
    // Set default hideTitle and hideSubtitle to true for all panels
    const defaultLayoutOverrides = {
      ...cleanedPartial.layoutOverrides,
      hideTitle: cleanedPartial.layoutOverrides?.hideTitle ?? true,
      hideSubtitle: cleanedPartial.layoutOverrides?.hideSubtitle ?? true,
    };
    
    const panel: FigurePanel = {
      id: nanoid(),
      label: defaultLabel,
      ...cleanedPartial,
      layoutOverrides: defaultLayoutOverrides,
    };

    const updatedPanels = [...currentComposition.panels, panel];

    // Auto-set legend position to top_outside when multi-panel figure is created
    const isNowMulti = updatedPanels.length >= 2;
    const shouldAutoSetLegend = isNowMulti && (currentComposition.legendPosition === 'auto' || !currentComposition.legendPosition);
    const newLegendPosition = shouldAutoSetLegend ? 'top_outside' : (currentComposition.legendPosition ?? 'auto');

    // Recompute per-column/per-row scale groups if standardization is active
    // Adding a panel may change the set of locked panels
    let lockedXScaleGroups: Record<string, { scaleFactor: number; referencePanelId: string }> | null = null;
    let lockedYScaleGroups: Record<string, { scaleFactor: number; referencePanelId: string }> | null = null;

    const compositionWithNewPanel: FigureComposition = {
      ...currentComposition,
      panels: updatedPanels,
      legendPosition: newLegendPosition,
      lockedXScaleGroups: null,
      lockedYScaleGroups: null,
    };

    const axesSharing = currentComposition.axesSharing;
    if (axesSharing?.standardizeXScales) {
      lockedXScaleGroups = computeLockedXScaleGroups(compositionWithNewPanel, currentComposition.grid, currentComposition.canvasSize);
    }

    if (axesSharing?.standardizeYScales) {
      lockedYScaleGroups = computeLockedYScaleGroups(compositionWithNewPanel, currentComposition.grid, currentComposition.canvasSize);
    }

    const updated = {
      ...compositionWithNewPanel,
      lockedXScaleGroups,
      lockedYScaleGroups,
      updatedAt: new Date(),
    };

    set((state) => ({
      currentComposition: updated,
      compositions: state.compositions.map((c) =>
        c.id === updated.id ? updated : c
      ),
      // Clear selection when panel is added to keep view at figure level
      selectedPanelId: null,
      // saveStatus will be updated by markDirtyAndSaveDraft
    }));

    // Mark as dirty and save draft locally
    if (markDirtyAndSaveDraft) {
      markDirtyAndSaveDraft(get, set);
    }

    // Always recompute auto caption so it's available as ghost text
    get().recomputeAutoCaption().catch(err => console.warn('[addPanel] Failed to recompute caption:', err));

    // hideTitle and hideSubtitle are already set to true by default in layoutOverrides above
    // This ensures titles and subtitles are hidden by default for all panels
    if (partial.plotId) {
      
      // Ensure panel labels are visible when adding a plot
      // Always set to true when adding a plot, regardless of current state
      try {
        const currentState = useFigureEditorUiState.getState();
        console.log('🔍 [addPanel] Ensuring showPanelLabels is enabled:', { 
          currentShowPanelLabels: currentState.showPanelLabels,
          plotId: partial.plotId,
          panelId: panel.id,
          panelLabel: panel.label
        });
        // Always enable when adding a plot
        useFigureEditorUiState.getState().updateState({ showPanelLabels: true });
        const updatedState = useFigureEditorUiState.getState();
        console.log('🔍 [addPanel] showPanelLabels set to:', updatedState.showPanelLabels);
      } catch (error) {
        console.error('❌ [addPanel] Failed to ensure panel labels are visible:', error);
      }
    }

    // If plotId was provided, fetch plot title and update label
    if (partial.plotId && currentComposition.projectId) {
      // Fetch plot title asynchronously
      (async () => {
        try {
          const { apiRequest } = await import('@/lib/queryClient');
          const response = await apiRequest('GET', `/api/projects/${currentComposition.projectId}/plots`);
          const plots = await response.json();
          const plot = plots.find((p: any) => p.id === partial.plotId);
          if (plot && plot.title) {
            // Format as "(a) Title" using the panel's letter label
            const formattedLabel = `${panel.label} ${plot.title}`;
            const { updatePanel: updatePanelLabel } = get();
            updatePanelLabel(panel.id, { label: formattedLabel });
          }
        } catch (error) {
          console.error('Failed to fetch plot title for label:', error);
        }
      })();
    }
  },

  removePanel: (panelId: string) => {
    const { currentComposition, selectedPanelId, selectedPanelIds } = get();
    if (!currentComposition) return;

    const updatedPanels = currentComposition.panels.filter((p) => p.id !== panelId);

    // Recompute per-column/per-row scale groups if standardization is active
    // Removing a panel may change the set of locked panels
    let lockedXScaleGroups: Record<string, { scaleFactor: number; referencePanelId: string }> | null = null;
    let lockedYScaleGroups: Record<string, { scaleFactor: number; referencePanelId: string }> | null = null;

    const compositionWithoutPanel: FigureComposition = {
      ...currentComposition,
      panels: updatedPanels,
      lockedXScaleGroups: null,
      lockedYScaleGroups: null,
    };

    const axesSharing = currentComposition.axesSharing;
    if (axesSharing?.standardizeXScales) {
      lockedXScaleGroups = computeLockedXScaleGroups(compositionWithoutPanel, currentComposition.grid, currentComposition.canvasSize);
    }

    if (axesSharing?.standardizeYScales) {
      lockedYScaleGroups = computeLockedYScaleGroups(compositionWithoutPanel, currentComposition.grid, currentComposition.canvasSize);
    }

    const updated = {
      ...compositionWithoutPanel,
      lockedXScaleGroups,
      lockedYScaleGroups,
      updatedAt: new Date(),
    };

    set((state) => {
      // Remove from selection if it was selected
      const newSelectedPanelIds = selectedPanelIds.filter(id => id !== panelId);
      const newSelectedPanelId = selectedPanelId === panelId 
        ? (newSelectedPanelIds.length > 0 ? newSelectedPanelIds[0] : null)
        : selectedPanelId;
      
      return {
        currentComposition: updated,
        compositions: state.compositions.map((c) =>
          c.id === updated.id ? updated : c
        ),
        // saveStatus will be updated by markDirtyAndSaveDraft
        selectedPanelId: newSelectedPanelId,
        selectedPanelIds: newSelectedPanelIds,
      };
    });

    // Mark as dirty and save draft locally
    if (markDirtyAndSaveDraft) {
      markDirtyAndSaveDraft(get, set);
    }
    
    // Always recompute auto caption so it's available as ghost text
    get().recomputeAutoCaption().catch(err => console.warn('[addPanel] Failed to recompute caption:', err));
  },

  updatePanel: (panelId: string, updates: Partial<FigurePanel>) => {
    const { currentComposition } = get();
    if (!currentComposition) return;

    const panel = currentComposition.panels.find((p) => p.id === panelId);
    if (!panel) return;

    // Ensure label is never cleared - if updates would clear it, preserve existing label
    const safeUpdates = { ...updates };
    if ('label' in safeUpdates && (!safeUpdates.label || safeUpdates.label.trim() === '')) {
      // If trying to clear label, preserve existing label or generate a default one
      if (panel.label && panel.label.trim() !== '') {
        delete safeUpdates.label; // Keep existing label
      } else {
        // Generate a default label if panel doesn't have one
        safeUpdates.label = generatePanelLabel(currentComposition.panels);
      }
    }

    // If plotId is being added and label is not explicitly set, use plot title as label
    // If plotId is being set and we don't have a label yet, we'll set it later via async fetch
    const updatedPanels = currentComposition.panels.map((p) =>
      p.id === panelId ? { ...p, ...safeUpdates } : p
    );

    // Recompute per-column/per-row scale groups if standardization is active
    // Changes to plotId, axesOverride, or position (x/y) may change which panels are locked or grouped
    let lockedXScaleGroups: Record<string, { scaleFactor: number; referencePanelId: string }> | null = null;
    let lockedYScaleGroups: Record<string, { scaleFactor: number; referencePanelId: string }> | null = null;

    const compositionWithUpdatedPanel: FigureComposition = {
      ...currentComposition,
      panels: updatedPanels,
      lockedXScaleGroups: null,
      lockedYScaleGroups: null,
    };

    const axesSharing = currentComposition.axesSharing;
    if (axesSharing?.standardizeXScales) {
      lockedXScaleGroups = computeLockedXScaleGroups(compositionWithUpdatedPanel, currentComposition.grid, currentComposition.canvasSize);
    }

    if (axesSharing?.standardizeYScales) {
      lockedYScaleGroups = computeLockedYScaleGroups(compositionWithUpdatedPanel, currentComposition.grid, currentComposition.canvasSize);
    }

    const updated = {
      ...compositionWithUpdatedPanel,
      lockedXScaleGroups,
      lockedYScaleGroups,
      updatedAt: new Date(),
    };

    // Clear selectedPanelId when a plot is added to keep view at figure level
    const shouldClearSelection = updates.plotId && !panel.plotId;

    set((state) => ({
      currentComposition: updated,
      compositions: state.compositions.map((c) =>
        c.id === updated.id ? updated : c
      ),
      // Clear selection if plot was just added to keep view at figure level
      selectedPanelId: shouldClearSelection ? null : state.selectedPanelId,
      // saveStatus will be updated by markDirtyAndSaveDraft
    }));

    // Mark as dirty and save draft locally
    if (markDirtyAndSaveDraft) {
      markDirtyAndSaveDraft(get, set);
    }

    // Always recompute auto caption when plotId changes so it's available as ghost text
    const plotIdChanged = updates.plotId !== undefined && updates.plotId !== panel.plotId;
    if (plotIdChanged) {
      get().recomputeAutoCaption().catch(err => console.warn('[updatePanel] Failed to recompute caption:', err));
    }

    // If plotId was added, automatically hide the title so users know where to find it
    if (updates.plotId && !panel.plotId) {
      const { updatePanelOverrides } = get();
      updatePanelOverrides(panelId, { hideTitle: true });
    }

    // If plotId was added and label wasn't explicitly set, fetch plot title and update label
    if (updates.plotId && !updates.label && currentComposition.projectId) {
      // Fetch plot title asynchronously
      (async () => {
        try {
          const { apiRequest } = await import('@/lib/queryClient');
          const response = await apiRequest('GET', `/api/projects/${currentComposition.projectId}/plots`);
          const plots = await response.json();
          const plot = plots.find((p: any) => p.id === updates.plotId);
          if (plot && plot.title) {
            // Get the current panel to preserve the letter label (a, b, c, etc.)
            const { currentComposition: currentComp } = get();
            const currentPanel = currentComp?.panels.find((p) => p.id === panelId);
            if (currentPanel && currentComp) {
              // Extract the letter label (e.g., "(a)" or "(b)") or use the current label
              const letterLabel = currentPanel.label.match(/^\([a-z]\d*\)/)?.[0] || currentPanel.label.split(' ')[0] || `(${String.fromCharCode('a'.charCodeAt(0) + currentComp.panels.indexOf(currentPanel))})`;
              // Format as "(a) Title"
              const formattedLabel = `${letterLabel} ${plot.title}`;
              const { updatePanel: updatePanelLabel } = get();
              updatePanelLabel(panelId, { label: formattedLabel });
            }
          }
        } catch (error) {
          console.error('Failed to fetch plot title for label:', error);
        }
      })();
    }
  },

  updatePanelLayout: (
    panelId: string,
    layout: { x: number; y: number; w: number; h: number }
  ) => {
    const { currentComposition } = get();
    if (!currentComposition) return;

    const updatedPanels = currentComposition.panels.map((p) =>
      p.id === panelId ? { ...p, ...layout } : p
    );

    // Recompute per-column/per-row scale groups if standardization is active
    // Panel dimensions (w/h) and position (x/y) affect expected container size calculation and grouping
    let lockedXScaleGroups: Record<string, { scaleFactor: number; referencePanelId: string }> | null = null;
    let lockedYScaleGroups: Record<string, { scaleFactor: number; referencePanelId: string }> | null = null;

    const compositionWithUpdatedPanels: FigureComposition = {
      ...currentComposition,
      panels: updatedPanels,
      lockedXScaleGroups: null,
      lockedYScaleGroups: null,
    };

    const axesSharing = currentComposition.axesSharing;
    if (axesSharing?.standardizeXScales) {
      lockedXScaleGroups = computeLockedXScaleGroups(compositionWithUpdatedPanels, currentComposition.grid, currentComposition.canvasSize);
    }

    if (axesSharing?.standardizeYScales) {
      lockedYScaleGroups = computeLockedYScaleGroups(compositionWithUpdatedPanels, currentComposition.grid, currentComposition.canvasSize);
    }

    const updated = {
      ...compositionWithUpdatedPanels,
      lockedXScaleGroups,
      lockedYScaleGroups,
      updatedAt: new Date(),
    };

    set((state) => ({
      currentComposition: updated,
      compositions: state.compositions.map((c) =>
        c.id === updated.id ? updated : c
      ),
      // saveStatus will be updated by markDirtyAndSaveDraft
    }));

    // Mark as dirty and save draft locally
    if (markDirtyAndSaveDraft) {
      markDirtyAndSaveDraft(get, set);
    }
  },

  applyTemplatePreset: (presetId: string) => {
    const { currentComposition } = get();
    if (!currentComposition) {
      console.warn('[applyTemplatePreset] No current composition');
      return;
    }

    const preset = FIGURE_TEMPLATE_PRESETS.find((p) => p.id === presetId);
    if (!preset) {
      console.warn(`[applyTemplatePreset] Preset "${presetId}" not found`);
      return;
    }

    console.log(`[applyTemplatePreset] Applying preset "${presetId}" (${preset.name})`, {
      presetPanels: preset.initialPanels,
      currentGrid: currentComposition.grid,
      currentPanels: currentComposition.panels.length,
    });

    // Handle custom preset - clear all panels
    if (presetId === 'custom') {
      const updated = {
        ...currentComposition,
        panels: [],
        templatePresetId: presetId,
        updatedAt: new Date(),
      };

      set((state) => ({
        currentComposition: updated,
        compositions: state.compositions.map((c) =>
          c.id === updated.id ? updated : c
        ),
        // saveStatus will be updated by markDirtyAndSaveDraft
      }));

      console.log('[applyTemplatePreset] Cleared all panels for custom preset');
      
      // Trigger auto-save
      if (updated.id) {
        // Mark as dirty and save draft locally
        if (markDirtyAndSaveDraft) {
          markDirtyAndSaveDraft(get, set);
        }
      }
      return;
    }

    // Preserve existing plot assignments when possible
    const existingPanels = currentComposition.panels;
    const existingPlots = existingPanels
      .filter((p) => p.plotId)
      .map((p) => ({ plotId: p.plotId, plotVersionId: p.plotVersionId }));

    // Validate template panel definitions
    const maxX = Math.max(...preset.initialPanels.map(p => p.x + p.w), 0);
    const maxY = Math.max(...preset.initialPanels.map(p => p.y + p.h), 0);
    
    if (maxX > 12) {
      console.warn(`[applyTemplatePreset] Template "${presetId}" exceeds 12 columns: maxX=${maxX}`);
    }
    
    // Sort template panels by position (top to bottom, left to right) to ensure consistent ordering
    const sortedTemplatePanels = [...preset.initialPanels].sort((a, b) => {
      if (a.y !== b.y) return a.y - b.y; // Sort by row first
      return a.x - b.x; // Then by column
    });

    // Create new panels based on template layout
    const newPanels: FigurePanel[] = sortedTemplatePanels.map((p, index) => {
      // Validate panel dimensions
      if (p.x < 0 || p.y < 0 || p.w <= 0 || p.h <= 0) {
        console.warn(`[applyTemplatePreset] Invalid panel dimensions at index ${index}:`, p);
      }
      if (p.x + p.w > 12) {
        console.warn(`[applyTemplatePreset] Panel ${index} exceeds grid width: x=${p.x}, w=${p.w}, total=${p.x + p.w}`);
      }

      // Try to preserve plot assignment and content settings
      let plotId = '';
      let plotVersionId: string | undefined = undefined;
      let existingPanel: FigurePanel | undefined = undefined;

      // Map plot assignments based on original template order (not sorted order)
      // This preserves the original matching logic for plot assignment
      const originalIndex = preset.initialPanels.findIndex(
        orig => orig.x === p.x && orig.y === p.y && orig.w === p.w && orig.h === p.h
      );
      
      if (existingPanels.length === preset.initialPanels.length && originalIndex >= 0) {
        // Same number of panels - preserve by original template index
        existingPanel = existingPanels[originalIndex];
        if (existingPanel?.plotId) {
          plotId = existingPanel.plotId;
          // Always use latest version when applying template preset (undefined = follow latest)
          plotVersionId = undefined;
        }
      } else if (existingPlots.length > 0 && originalIndex >= 0 && originalIndex < existingPlots.length) {
        // Different number - assign existing plots based on original template order
        plotId = existingPlots[originalIndex].plotId;
        // Always use latest version when applying template preset
        plotVersionId = undefined;
        // Try to find the existing panel with this plotId to preserve its content settings
        existingPanel = existingPanels.find(ep => ep.plotId === plotId);
      }

      // Preserve existing label if we have a matching panel, otherwise use preset label or generate default
      let label = existingPanel?.label || p.label;
      if (!label || label.trim() === '') {
        const charCode = 'a'.charCodeAt(0) + (index % 26);
        const letter = String.fromCharCode(charCode);
        const panelNumber = Math.floor(index / 26);
        label = panelNumber === 0 ? `(${letter})` : `(${letter}${panelNumber})`;
      }

      // Preserve existing layoutOverrides (including hideSubtitle, hideAnnotations, etc.)
      // Only set hideTitle: true if not already set in existing panel
      const layoutOverrides: PanelLayoutOverrides = existingPanel?.layoutOverrides 
        ? { ...existingPanel.layoutOverrides }
        : { hideTitle: true };

      // If no existing layoutOverrides, ensure hideTitle is set to true
      if (!existingPanel?.layoutOverrides) {
        layoutOverrides.hideTitle = true;
      }

      const panel: FigurePanel = {
        id: nanoid(),
        plotId,
        plotVersionId,
        x: p.x,
        y: p.y,
        w: p.w,
        h: p.h,
        label,
        // Clear any constraints that might interfere
        minW: undefined,
        minH: undefined,
        maxW: undefined,
        maxH: undefined,
        // Preserve existing layoutOverrides to maintain content settings (hideSubtitle, hideAnnotations, etc.)
        layoutOverrides,
        // Preserve other content properties from existing panel
        ...(existingPanel && {
          labelStyle: existingPanel.labelStyle,
          annotations: existingPanel.annotations,
          figureJsonOverride: existingPanel.figureJsonOverride,
          panelWidthMm: existingPanel.panelWidthMm,
          panelHeightMm: existingPanel.panelHeightMm,
        }),
      };

      console.log(`[applyTemplatePreset] Created panel ${index}:`, {
        id: panel.id,
        position: `(${panel.x}, ${panel.y})`,
        size: `${panel.w}×${panel.h}`,
        label: panel.label,
        plotId: panel.plotId || '(empty)',
      });

      return panel;
    });

    // Ensure grid columns match template requirements (all templates use 12 columns)
    const gridCols = 12;
    const defaultRowHeight = 40;
    const defaultMargin: [number, number] = [8, 8];
    
    // Calculate minimum canvas width needed for 12 columns
    // Templates assume 12 columns, so we need to ensure canvas is wide enough
    // Standard figure widths: single column ~90mm, double column ~180mm, full width ~250mm+
    // For 12-column grid templates, we need at least ~250mm to ensure columns aren't constrained
    const minCanvasWidthMm = 250; // Minimum width to accommodate 12 columns comfortably
    
    // Calculate minimum canvas height needed for template (max y + h)
    const maxTemplateHeight = Math.max(...preset.initialPanels.map(p => p.y + p.h), 0);
    const padding = 32; // p-4 = 16px on each side
    const minCanvasHeightRows = maxTemplateHeight;
    const minCanvasHeightPx = (minCanvasHeightRows * defaultRowHeight) + (defaultMargin[1] * (minCanvasHeightRows + 1)) + padding;
    const minCanvasHeightMm = Math.ceil((minCanvasHeightPx / 96) * 25.4);
    
    // Ensure canvas size is large enough for the template
    const currentCanvasWidthMm = currentComposition.canvasSize.width;
    const currentCanvasHeightMm = currentComposition.canvasSize.height;
    const needsCanvasSizeUpdate = 
      currentCanvasWidthMm < minCanvasWidthMm ||
      currentCanvasHeightMm < minCanvasHeightMm;
    
    // Ensure grid is properly configured for templates
    const needsGridUpdate = 
      currentComposition.grid.cols !== gridCols ||
      currentComposition.grid.rowHeight !== defaultRowHeight ||
      currentComposition.grid.margin[0] !== defaultMargin[0] ||
      currentComposition.grid.margin[1] !== defaultMargin[1];

    const updated = {
      ...currentComposition,
      panels: newPanels,
      templatePresetId: presetId,
      canvasSize: needsCanvasSizeUpdate
        ? {
            width: Math.max(currentCanvasWidthMm, minCanvasWidthMm),
            height: Math.max(currentCanvasHeightMm, minCanvasHeightMm),
            unit: 'mm' as const,
          }
        : currentComposition.canvasSize,
      grid: needsGridUpdate
        ? {
            cols: gridCols,
            rowHeight: defaultRowHeight,
            margin: defaultMargin,
          }
        : currentComposition.grid,
      updatedAt: new Date(),
    };

    console.log(`[applyTemplatePreset] Applied preset "${presetId}":`, {
      panelsCreated: newPanels.length,
      gridUpdated: needsGridUpdate,
      canvasSizeUpdated: needsCanvasSizeUpdate,
      newGrid: updated.grid,
      newCanvasSize: updated.canvasSize,
      minRequiredSize: {
        width: minCanvasWidthMm,
        height: minCanvasHeightMm,
      },
    });

    set((state) => ({
      currentComposition: updated,
      compositions: state.compositions.map((c) =>
        c.id === updated.id ? updated : c
      ),
      // saveStatus will be updated by markDirtyAndSaveDraft
    }));

    // Mark as dirty and save draft locally
    if (markDirtyAndSaveDraft) {
      markDirtyAndSaveDraft(get, set);
    }
  },

  hydratePanel: async (panel: FigurePanel, composition?: FigureComposition) => {
    if (!panel.plotId) {
      throw new Error('Panel has no plot assigned');
    }

    // CRITICAL: Resolve effectiveVersionId using unified helper
    // NEVER use literal 'latest' in cache keys or effectiveVersionId
    const resolved = await get().resolveEffectivePlotVersion({
      plotId: panel.plotId,
      panelVersionId: panel.plotVersionId,
      projectId: composition?.projectId,
    });
    
    if (!resolved.versionId) {
      const errorMsg = `[hydratePanel] Panel ${panel.id}: Could not resolve versionId for plot ${panel.plotId}. Panel will not render.`;
      console.error(errorMsg);
      throw new Error(`Failed to resolve plot version for panel ${panel.id}: plot ${panel.plotId} has no versionId`);
    }
    
    const effectiveVersionId = resolved.versionId;

    // Read theme properties from composition (ensure we get the latest values)
    const journalTemplateId = composition?.theme?.journalTemplate || 'none';
    const hasJournalTemplate = journalTemplateId !== 'none';
    
    // Global Style overrides (these override journal template settings)
    const overrideFontFamily = composition?.theme?.fontFamily;
    // Use fontScale (multiplier) if available, otherwise fall back to fontSize for backward compatibility
    const fontScale = composition?.theme?.fontScale ?? (composition?.theme?.fontSize ? composition.theme.fontSize / 8 : 1.0);
    const colorMode = composition?.theme?.colorMode || 'RGB';
    const colorblindSafe = composition?.theme?.colorblindSafe ? 'cb' : 'no-cb';
    
    // Get line width from journal template (line width is set by template, not user override)
    // Default to 1 if no journal template
    let lineWidth = 1; // Default
    if (hasJournalTemplate) {
      const template = journalTemplatePresets.find(t => t.id === journalTemplateId);
      if (template?.typography) {
        // Use lineWeightPt_max or last value from lineWeightPt_range, convert pt to px
        // For line width, 1pt ≈ 1px is standard (we can round to nearest integer)
        const lineWeightPt = template.typography.lineWeightPt_max || template.typography.lineWeightPt_range?.[1] || template.typography.lineWeightPt_range?.[0] || 1;
        lineWidth = Math.round(lineWeightPt); // Convert pt to px (1pt ≈ 1px for line width)
      }
    }
    
    // Create cache key: when journal template is set, only include overrides (not template defaults)
    // When no journal template, include all style properties
    // Line width is NOT in cache key since it comes from journal template
    // CRITICAL: Use effectiveVersionId in cache key, not literal "latest"
    let themeKey: string;
    if (hasJournalTemplate) {
      // Journal template sets defaults, Global Style overrides them
      // Cache key only needs overrides + template ID (line width comes from template)
      const overrideKey = overrideFontFamily || fontScale !== 1.0
        ? `:override:${overrideFontFamily || ''}:${fontScale.toFixed(2)}`
        : '';
      themeKey = `${journalTemplateId}${overrideKey}:${colorMode}:${colorblindSafe}`;
    } else {
      // No journal template: Global Style is the only source, line width defaults to 1
      themeKey = `none:${overrideFontFamily || 'Arial'}:${fontScale.toFixed(2)}:${colorMode}:${colorblindSafe}`;
    }
    
    // Include axes sharing and panel overrides in cache key
    const axesSharingKey = composition && composition.axesSharing && !panel.layoutOverrides?.axesOverride
      ? JSON.stringify({
          shareXAcrossColumns: composition.axesSharing.shareXAcrossColumns,
          shareXAcrossAll: composition.axesSharing.shareXAcrossAll,
          shareYAcrossRows: composition.axesSharing.shareYAcrossRows,
          standardizeXTitles: composition.axesSharing.standardizeXTitles,
          standardizeYTitles: composition.axesSharing.standardizeYTitles,
          standardizeXScales: composition.axesSharing.standardizeXScales,
          standardizeYScales: composition.axesSharing.standardizeYScales,
        })
      : 'no-sharing';
    
    const overridesKey = panel.layoutOverrides
      ? JSON.stringify({
          margin: panel.layoutOverrides.margin,
          showLegend: panel.layoutOverrides.showLegend,
          axesOverride: panel.layoutOverrides.axesOverride,
          zoom: panel.layoutOverrides.zoom,
          panX: panel.layoutOverrides.panX,
          panY: panel.layoutOverrides.panY,
          // Explicitly include hide flags even if undefined to ensure they're preserved
          hideTitle: panel.layoutOverrides.hideTitle ?? false,
          hideSubtitle: panel.layoutOverrides.hideSubtitle ?? false,
          hideAnnotations: panel.layoutOverrides.hideAnnotations ?? false,
        })
      : 'no-overrides';
    
    // CRITICAL: Use simplified cache key - primarily plotId and versionId
    // Only append extra hash components (theme, axes, overrides) as a short hash, not full JSON
    // This prevents cache churn when layout/theme changes don't affect plot data
    const extraHash = `${themeKey}:${axesSharingKey}:${overridesKey}`;
    const cacheKey = get().panelCacheKey(panel.plotId, effectiveVersionId, extraHash);
    console.log(`🎨 [hydratePanel] Cache key for panel ${panel.id}:`, cacheKey);
    console.log(`🎨 [hydratePanel] Version resolution:`, {
      panelPlotVersionId: panel.plotVersionId ?? 'undefined (follow latest)',
      effectiveVersionId,
      isPinned: !!panel.plotVersionId,
    });
    console.log(`🎨 [hydratePanel] Theme values:`, {
      journalTemplateId,
      hasJournalTemplate,
      overrideFontFamily,
      fontScale,
      lineWidth, // From journal template
      colorMode,
      colorblindSafe,
    });
    
    let baseResult: { data: Data[]; layout: Partial<Layout> };
    
    // Always use saved server data - no figureJsonOverride
    // This ensures consistency: all panels (from "Add to Figure" or drag) use the same saved version
    try {
      console.log('[hydratePanel] cache lookup', {
        panelId: panel.id,
        cacheKey,
        hasCached: panelCache.has(cacheKey),
      });
    } catch {}
    
    const cached = panelCache.get(cacheKey);
    
    if (cached) {
      baseResult = cached;
      // DEBUG: Log cached plot state
      console.log(`🔍 [hydratePanel] DEBUG - Using cached plot for panel ${panel.id}:`, {
        hasMeta: !!baseResult.layout?.meta,
        hasLumi: !!(baseResult.layout?.meta as any)?.lumi,
        hasTheme: !!(baseResult.layout?.meta as any)?.lumi?.theme,
        hasSequential: !!(baseResult.layout?.meta as any)?.lumi?.theme?.sequential,
        sequentialScale: (baseResult.layout?.meta as any)?.lumi?.theme?.sequential?.scale,
        coloraxisColorscale: (baseResult.layout?.coloraxis as any)?.colorscale,
        coloraxisExists: !!baseResult.layout?.coloraxis,
        coloraxisFull: baseResult.layout?.coloraxis,
        traceCount: baseResult.data.length,
        tracesWithColoraxis: baseResult.data.filter((t: any) => t.coloraxis).length,
        traceColoraxisValues: baseResult.data.map((t: any) => ({ type: t.type, coloraxis: t.coloraxis })),
      });
    } else {
      // CRITICAL: Use effectiveVersionId for API call (already resolved above)
      const res = await apiRequest(
        'GET',
        `/api/ai/v2/plots/${panel.plotId}/versions/${effectiveVersionId}/plotly_figure.json`
      );

      if (!res.ok) {
        throw new Error('Failed to load plot version');
      }

      const plotlyJson = await res.json();
      baseResult = {
        data: plotlyJson.data ?? [],
        layout: plotlyJson.layout ?? {},
      };
      
      // DEBUG: Log theme metadata from fetched plot
      console.log(`🔍 [hydratePanel] DEBUG - Plot JSON from API for panel ${panel.id}:`, {
        hasMeta: !!plotlyJson.layout?.meta,
        hasLumi: !!plotlyJson.layout?.meta?.lumi,
        hasTheme: !!plotlyJson.layout?.meta?.lumi?.theme,
        hasSequential: !!plotlyJson.layout?.meta?.lumi?.theme?.sequential,
        sequentialScale: plotlyJson.layout?.meta?.lumi?.theme?.sequential?.scale,
        sequentialReversed: plotlyJson.layout?.meta?.lumi?.theme?.sequential?.reversed,
        coloraxisColorscale: plotlyJson.layout?.coloraxis?.colorscale,
        coloraxisExists: !!plotlyJson.layout?.coloraxis,
        plotId: panel.plotId,
        versionId: effectiveVersionId, // Always a concrete UUID, never 'latest'
        panelPlotVersionId: panel.plotVersionId ?? 'undefined (follow latest)',
        isPinned: !!panel.plotVersionId,
      });
    }

    // Apply plot theme colors from layout.meta.lumi.theme BEFORE other transformations
    // This ensures colors set in the plot editor are preserved when moving to multiplot panel
    // Store sequential scale settings to apply AFTER journal template (since template might reset layout)
    let sequentialScaleToApply: { scale: string; reversed: boolean; range?: string; min?: number | null; max?: number | null } | null = null;
    
    // CRITICAL FIX: In Figure Editor context, skip theme sequential colorscale logic entirely
    // The figure editor should preserve original figure colors from R/Python/Notebook JSON
    // Theme sequential scales should not be applied during hydration (drag/resize/open)
    // Only explicit color operations should change colors
    //
    // Since hydratePanel is only called from Panel.tsx which is in figure editor context,
    // we can assume all calls are in figure editor. However, we check composition existence
    // to be explicit about the context.
    const isFigureEditorPanel = !!composition;
    
    try {
      const theme = (baseResult.layout?.meta as any)?.lumi?.theme || {};
      const chosenRow: string[] | undefined = (
        Array.isArray((theme as any).categorical) && (theme as any).categorical.length
          ? (theme as any).categorical
          : (Array.isArray(theme.swatches) && theme.swatches.length
            ? theme.swatches
            : undefined)
      );
      
      // Apply colorway from theme if available
      if (chosenRow && chosenRow.length > 0) {
        const themeMappings: any = (theme as any)?.mappings || {};
        const domainLen = Array.isArray(themeMappings?.domain) ? themeMappings.domain.length : 0;
        const effectiveSwatches = (function(){
          if (domainLen > chosenRow.length) {
            try {
              const generated = generateHueLightnessPalette(domainLen);
              return generated && generated.length ? generated : chosenRow;
            } catch {
              return chosenRow;
            }
          }
          return chosenRow;
        })();
        const catReversed = !!(theme as any)?.categoricalReversed;
        const swatchesToApply = effectiveSwatches ? (catReversed ? effectiveSwatches.slice().reverse() : effectiveSwatches) : effectiveSwatches;
        if (swatchesToApply && swatchesToApply.length > 0) {
          baseResult.layout.colorway = swatchesToApply;
          (baseResult.layout as any).sunburstcolorway = swatchesToApply;
          (baseResult.layout as any).treemapcolorway = swatchesToApply;
          (baseResult.layout as any).iciclecolorway = swatchesToApply;
          (baseResult.layout as any).funnelareacolorway = swatchesToApply;
          (baseResult.layout as any).extendsunburstcolors = true;
        }
      }

      // Apply mapping-based colors (for traces with explicit name/label mappings)
      const mappings: any = (theme as any)?.mappings || {};
      if (mappings && mappings.colors && Object.keys(mappings.colors).length > 0 && Array.isArray(baseResult.data)) {
        const miss = mappings.missing || '#9e9e9e';
        baseResult.data = baseResult.data.map((tr: any) => {
          const next: any = { ...tr };
          const t = String((next.type || 'scatter') as string).toLowerCase();
          
          // CRITICAL: Skip continuous color traces - don't apply categorical mapping
          const isContinuous = (tr: any): boolean => {
            const continuousTypes = new Set(['heatmap', 'contour', 'histogram2d', 'histogram2dcontour', 'choropleth', 'choroplethmapbox', 'densitymapbox', 'image']);
            if (continuousTypes.has(String(tr.type || '').toLowerCase())) return true;
            if (tr.colorscale || tr.coloraxis) return true;
            if (tr.marker?.colorscale || tr.marker?.coloraxis) return true;
            if (tr.marker?.color && Array.isArray(tr.marker.color) && typeof tr.marker.color[0] === 'number') return true;
            if (tr.z && (Array.isArray(tr.z) || Array.isArray(tr.z?.[0]))) return true;
            return false;
          };
          
          // Per-series mapping via trace.name (only if color not already set AND not continuous)
          if (typeof next.name === 'string' && !isContinuous(next) && !next.marker?.color && !next.line?.color) {
            const mapped = mappings.colors[next.name] || miss;
            if (t === 'scatter' || t === 'scattergl' || t === 'line') {
              next.line = next.line || {};
              if (!(next as any)._lumiTheme?.decoupledLine) {
                next.marker = next.marker || {};
                if (next.marker.color === undefined) next.marker.color = mapped;
              }
              if (next.line.color === undefined) next.line.color = mapped;
            } else if (t === 'bar' || t === 'histogram' || t === 'funnel') {
              next.marker = next.marker || {};
              if (next.marker.color === undefined) next.marker.color = mapped;
            } else if (t === 'indicator') {
              if (!next.gauge) next.gauge = {};
              if (!next.gauge.bar) next.gauge.bar = {};
              // Only apply mapped color if color is undefined and not locked
              // This respects script-generated colors by default
              if (!((next as any)._lumiTheme?.lockedBarColor === true) && next.gauge.bar.color === undefined) {
                next.gauge.bar.color = mapped;
              }
            }
          }
          
          // Per-slice mapping for pie/funnelarea by labels
          if ((t === 'pie' || t === 'funnelarea') && Array.isArray(next.labels)) {
            next.marker = next.marker || {};
            if (!next.marker.colors) {
              next.marker.colors = (next.labels as any[]).map((v: any) => mappings.colors[String(v)] || miss);
            }
          }
          
          return next;
        });
      }

      if (isFigureEditorPanel) {
        console.log(`🔍 [hydratePanel] DEBUG - Figure editor panel ${panel.id}: preserving original figure colors (ignoring theme sequential scale)`);
        // Do NOT set sequentialScaleToApply
        // Do NOT inspect theme.sequential for continuous traces
        // Do NOT modify layout.coloraxis.colorscale or trace-level colorscales
        // The original figure JSON's colorscales are authoritative
      } else {
        // Non-figure-editor context (reports, canvases, etc.) - can use theme sequential logic
        const sequential = (theme as any)?.sequential;
        
        // Helper function to check if a colorscale is valid and authoritative
        const hasAuthoritativeColorscale = (): boolean => {
          // Check layout.coloraxis.colorscale
          const layoutColorscale = (baseResult.layout?.coloraxis as any)?.colorscale;
          if (layoutColorscale && (typeof layoutColorscale === 'string' || Array.isArray(layoutColorscale))) {
            return true;
          }
          
          // Check trace-level colorscales (marker.colorscale, trace.colorscale)
          const hasTraceColorscale = baseResult.data.some((trace: any) => {
            if (trace.colorscale && (typeof trace.colorscale === 'string' || Array.isArray(trace.colorscale))) {
              return true;
            }
            if (trace.marker?.colorscale && (typeof trace.marker.colorscale === 'string' || Array.isArray(trace.marker.colorscale))) {
              return true;
            }
            if (trace.line?.colorscale && (typeof trace.line.colorscale === 'string' || Array.isArray(trace.line.colorscale))) {
              return true;
            }
            return false;
          });
          
          return hasTraceColorscale;
        };
        
        // DEBUG: Log theme reading (only in non-figure-editor contexts)
        console.log(`🔍 [hydratePanel] DEBUG - Theme reading for panel ${panel.id}:`, {
          themeExists: !!theme,
          sequentialExists: !!sequential,
          sequentialScale: sequential?.scale,
          sequentialReversed: sequential?.reversed,
          themeKeys: theme ? Object.keys(theme) : [],
          existingColoraxisColorscale: (baseResult.layout?.coloraxis as any)?.colorscale,
          hasAuthoritativeColorscale: hasAuthoritativeColorscale(),
        });
        
        // CRITICAL FIX: Only apply sequential scale if figure doesn't already have an authoritative colorscale
        // This preserves colorscales from the original plot JSON (from R/Python)
        if (hasAuthoritativeColorscale()) {
          console.log(`🔍 [hydratePanel] DEBUG - Figure already has authoritative colorscale, preserving it (not applying theme sequential scale)`);
          // Don't set sequentialScaleToApply - preserve existing colorscale
        } else if (!sequential?.scale && (baseResult.layout?.coloraxis as any)?.colorscale) {
          // If theme metadata is missing but coloraxis already has colorscale, use that as fallback
          const existingColorscale = (baseResult.layout?.coloraxis as any).colorscale;
          console.log(`🔍 [hydratePanel] DEBUG - Theme sequential scale missing, using existing coloraxis colorscale: ${existingColorscale}`);
          // Create sequential object from existing coloraxis
          sequentialScaleToApply = {
            scale: existingColorscale.replace('_r', ''), // Remove _r suffix if present
            reversed: existingColorscale.endsWith('_r'),
            range: undefined,
            min: (baseResult.layout?.coloraxis as any)?.cmin ?? null,
            max: (baseResult.layout?.coloraxis as any)?.cmax ?? null,
          };
        } else if (sequential && sequential.scale) {
          // Check if any trace uses continuous color scale (heatmap, contour, etc.)
          const hasContinuousColorTrace = baseResult.data.some((trace: any) => {
            const traceType = String(trace.type || '').toLowerCase();
            const continuousTypes = new Set(['heatmap', 'contour', 'histogram2d', 'histogram2dcontour', 'choropleth', 'choroplethmapbox', 'densitymapbox', 'image']);
            if (continuousTypes.has(traceType)) return true;
            if (trace.colorscale || trace.coloraxis) return true;
            if (trace.marker?.colorscale || trace.marker?.coloraxis) return true;
            if (trace.line?.colorscale || trace.line?.coloraxis) return true;
            if (trace.z && (Array.isArray(trace.z) || Array.isArray(trace.z?.[0]))) return true;
            return false;
          });

          console.log(`🔍 [hydratePanel] DEBUG - Continuous trace check for panel ${panel.id}:`, {
            hasContinuousColorTrace,
            traceTypes: baseResult.data.map((t: any) => t.type),
            traceHasZ: baseResult.data.some((t: any) => t.z),
          });

          if (hasContinuousColorTrace) {
            sequentialScaleToApply = {
              scale: sequential.scale,
              reversed: !!sequential.reversed,
              range: sequential.range,
              min: sequential.min ?? null,
              max: sequential.max ?? null,
            };
            console.log(`🔍 [hydratePanel] DEBUG - Stored sequential scale to apply:`, sequentialScaleToApply);
          }
        } else {
          console.log(`🔍 [hydratePanel] DEBUG - No sequential scale found in theme for panel ${panel.id}`);
        }
      }
    } catch (error) {
      console.warn('Failed to apply plot theme colors in hydratePanel:', error);
    }

    // Calculate shared axis properties early (but don't apply yet)
    // We'll apply them AFTER journal template and style to prevent overrides
    let sharedProps: Awaited<ReturnType<typeof calculateSharedAxisRanges>> | null = null;
    if (composition?.axesSharing && composition.panels.length > 1 && !panel.layoutOverrides?.axesOverride) {
      const axesSharing = composition.axesSharing;
      
      try {
        // Create a non-recursive loader that doesn't apply axes sharing
        const loadPanelWithoutSharing = async (p: FigurePanel) => {
          // Resolve effectiveVersionId using unified helper
          const resolved = await get().resolveEffectivePlotVersion({
            plotId: p.plotId,
            panelVersionId: p.plotVersionId,
            projectId: composition?.projectId,
          });
          
          if (!resolved.versionId) {
            throw new Error(`Failed to resolve versionId for panel ${p.id} in axis sharing calculation`);
          }
          
          const peerEffectiveVersionId = resolved.versionId;
          
          // CRITICAL: Use a simplified cache key for loadPanelWithoutSharing
          // This is only used for axis sharing calculations, not full panel rendering
          // The full cache key with theme/axes/overrides is used in main hydratePanel
          // Using simplified key here avoids cache pollution and ensures axis calculations
          // always get fresh data when needed
          // NOTE: peerEffectiveVersionId is always a concrete UUID, never 'latest'
          const cacheKey = get().panelCacheKey(p.plotId, peerEffectiveVersionId, 'axis-sharing-only');
          const cached = panelCache.get(cacheKey);
          if (cached) {
            return cached;
          }
          
          const res = await apiRequest(
            'GET',
            `/api/ai/v2/plots/${p.plotId}/versions/${peerEffectiveVersionId}/plotly_figure.json`
          );
          
          if (!res.ok) {
            throw new Error('Failed to load plot version');
          }
          
          const plotlyJson = await res.json();
          const result = {
            data: plotlyJson.data ?? [],
            layout: plotlyJson.layout ?? {},
          };
          
          // Only cache for axis sharing calculations - use distinct key to avoid conflicts
          panelCache.set(cacheKey, result);
          return result;
        };
        
        // Calculate shared axis properties by loading other panels (without composition context to avoid recursion)
        sharedProps = await calculateSharedAxisRanges(
          composition,
          panel,
          axesSharing,
          loadPanelWithoutSharing
        );
      } catch (error) {
        // If range calculation fails, fall back to individual panel ranges
        console.warn('Failed to calculate shared axis ranges:', error);
      }
    }

    // Step 1: Apply journal template first (sets defaults)
    if (hasJournalTemplate && composition) {
      try {
        const { applyJournalTemplate } = await import('@/lib/applyJournalTemplate');
        const journalTemplateId = composition.theme.journalTemplate;
        // Create a copy of the plotly JSON to apply template to
        const plotlyJsonToUpdate = {
          data: baseResult.data,
          layout: { ...baseResult.layout },
        };
        // Apply journal template (this sets default fonts, sizes, etc.)
        applyJournalTemplate(plotlyJsonToUpdate, journalTemplateId as any);
        
        // Normalize plot size to canonical dimensions (640×320px) for consistent display in panels
        // This ensures all plots appear at similar visual size regardless of journal template
        // IMPORTANT: Preserve aspect ratio to avoid distortion - scale proportionally to fit within bounds
        const CANONICAL_WIDTH = 640;
        const CANONICAL_HEIGHT = 320;
        const CANONICAL_ASPECT = CANONICAL_WIDTH / CANONICAL_HEIGHT; // 2:1
        
        // Get the size after journal template is applied
        const templateWidth = typeof plotlyJsonToUpdate.layout.width === 'number' 
          ? plotlyJsonToUpdate.layout.width 
          : CANONICAL_WIDTH;
        const templateHeight = typeof plotlyJsonToUpdate.layout.height === 'number' 
          ? plotlyJsonToUpdate.layout.height 
          : CANONICAL_HEIGHT;
        
        // Calculate aspect ratio of the template-applied plot
        const plotAspect = templateWidth / templateHeight;
        
        // Scale proportionally to fit within canonical bounds while preserving aspect ratio
        // This prevents distortion while ensuring consistent sizing
        let normalizedWidth: number;
        let normalizedHeight: number;
        
        if (plotAspect > CANONICAL_ASPECT) {
          // Plot is wider than canonical - fit to width, scale height proportionally
          normalizedWidth = CANONICAL_WIDTH;
          normalizedHeight = Math.round(CANONICAL_WIDTH / plotAspect);
        } else {
          // Plot is taller than canonical - fit to height, scale width proportionally
          normalizedHeight = CANONICAL_HEIGHT;
          normalizedWidth = Math.round(CANONICAL_HEIGHT * plotAspect);
        }
        
        // Apply normalized dimensions
        plotlyJsonToUpdate.layout.width = normalizedWidth;
        plotlyJsonToUpdate.layout.height = normalizedHeight;
        plotlyJsonToUpdate.layout.autosize = false;
        
        // Store original template dimensions in metadata for reference
        if (!plotlyJsonToUpdate.layout.meta) {
          plotlyJsonToUpdate.layout.meta = {};
        }
        if (!plotlyJsonToUpdate.layout.meta.lumi) {
          plotlyJsonToUpdate.layout.meta.lumi = {};
        }
        if (!plotlyJsonToUpdate.layout.meta.lumi.base_layout) {
          plotlyJsonToUpdate.layout.meta.lumi.base_layout = {};
        }
        plotlyJsonToUpdate.layout.meta.lumi.base_layout.width = templateWidth;
        plotlyJsonToUpdate.layout.meta.lumi.base_layout.height = templateHeight;
        
        // Update baseResult with the template-applied layout
        baseResult.layout = plotlyJsonToUpdate.layout;
        console.log(`🎨 [hydratePanel] Applied journal template "${journalTemplateId}" and normalized size to ${normalizedWidth}×${normalizedHeight}px (from ${templateWidth}×${templateHeight}px, aspect ${plotAspect.toFixed(2)}) for panel ${panel.id}`);
      } catch (error) {
        console.warn('Failed to apply journal template to panel:', error);
      }
    }

    // Apply sequential color scale AFTER journal template (to ensure it's not overwritten)
    // CRITICAL: Skip this entirely for figure editor panels - preserve original figure colors
    if (sequentialScaleToApply && !isFigureEditorPanel) {
      try {
        // CRITICAL FIX: Check again if figure has authoritative colorscale after journal template
        // Journal template might have reset layout, but traces may still have colorscales
        const hasAuthoritativeColorscaleAfterTemplate = (): boolean => {
          // Check layout.coloraxis.colorscale
          const layoutColorscale = (baseResult.layout?.coloraxis as any)?.colorscale;
          if (layoutColorscale && (typeof layoutColorscale === 'string' || Array.isArray(layoutColorscale))) {
            return true;
          }
          
          // Check trace-level colorscales (marker.colorscale, trace.colorscale)
          const hasTraceColorscale = baseResult.data.some((trace: any) => {
            if (trace.colorscale && (typeof trace.colorscale === 'string' || Array.isArray(trace.colorscale))) {
              return true;
            }
            if (trace.marker?.colorscale && (typeof trace.marker.colorscale === 'string' || Array.isArray(trace.marker.colorscale))) {
              return true;
            }
            if (trace.line?.colorscale && (typeof trace.line.colorscale === 'string' || Array.isArray(trace.line.colorscale))) {
              return true;
            }
            return false;
          });
          
          return hasTraceColorscale;
        };
        
        if (hasAuthoritativeColorscaleAfterTemplate()) {
          console.log(`🔍 [hydratePanel] DEBUG - Figure still has authoritative colorscale after journal template, preserving it (not applying sequentialScaleToApply)`);
          // Don't apply sequentialScaleToApply - preserve existing colorscale
        } else {
          // Ensure coloraxis exists in layout
          if (!baseResult.layout.coloraxis) {
            baseResult.layout.coloraxis = {};
          }
          
          // Apply sequential scale from theme
          const colorscale = sequentialScaleToApply.scale;
          const reversed = sequentialScaleToApply.reversed;
          
          // Determine the expected colorscale value
          const expectedColorscale = reversed && typeof colorscale === 'string'
            ? (colorscale.endsWith('_r') ? colorscale : `${colorscale}_r`)
            : colorscale;
          
          // Only apply colorscale if layout doesn't already have one
          if (!(baseResult.layout.coloraxis as any).colorscale) {
            (baseResult.layout.coloraxis as any).colorscale = expectedColorscale;
          }

          // Apply range settings if specified
          if (sequentialScaleToApply.range === 'custom' && (sequentialScaleToApply.min !== null || sequentialScaleToApply.max !== null)) {
            if (sequentialScaleToApply.min !== null) {
              (baseResult.layout.coloraxis as any).cmin = sequentialScaleToApply.min;
            }
            if (sequentialScaleToApply.max !== null) {
              (baseResult.layout.coloraxis as any).cmax = sequentialScaleToApply.max;
            }
          }

          // Ensure traces are bound to coloraxis (but preserve existing colorscales)
          const theme = (baseResult.layout?.meta as any)?.lumi?.theme || {};
          const coloraxisName = (theme as any)?.coloraxis || 'coloraxis';
          const finalColorscale = (baseResult.layout.coloraxis as any)?.colorscale || expectedColorscale;
          
          baseResult.data = baseResult.data.map((trace: any) => {
            const traceType = String(trace.type || '').toLowerCase();
            const continuousTypes = new Set(['heatmap', 'contour', 'histogram2d', 'histogram2dcontour', 'choropleth', 'choroplethmapbox', 'densitymapbox', 'image']);
            
            // Check if trace uses continuous coloring via z-data (heatmaps, contours)
            const hasZData = trace.z && (Array.isArray(trace.z) || Array.isArray(trace.z?.[0]));
            
            // Check if trace uses continuous coloring via marker.color (scatter plots)
            const hasMarkerColorArray = trace.marker?.color && Array.isArray(trace.marker.color) && 
              trace.marker.color.length > 0 && typeof trace.marker.color[0] === 'number';
            
            // Check if trace already uses marker.coloraxis or marker.colorscale
            const hasMarkerColoraxis = !!(trace.marker?.coloraxis || trace.marker?.colorscale);
            
            // CRITICAL FIX: Check if trace already has an authoritative colorscale
            const traceHasAuthoritativeColorscale = !!(trace.colorscale || trace.marker?.colorscale || trace.line?.colorscale);
            
            if (continuousTypes.has(traceType) || hasZData || hasMarkerColorArray || hasMarkerColoraxis) {
              // CRITICAL: Only remove trace-level colorscale if it conflicts with layout coloraxis
              // AND trace doesn't have its own authoritative colorscale
              if (trace.colorscale && !traceHasAuthoritativeColorscale && baseResult.layout.coloraxis) {
                console.log(`🔍 [hydratePanel] Removing trace-level colorscale "${trace.colorscale}" to use layout coloraxis "${finalColorscale}"`);
                delete trace.colorscale;
              }
              
              // CRITICAL: Always set autocolorscale to false to prevent Plotly from overriding
              trace.autocolorscale = false;
              if (trace.marker) {
                trace.marker.autocolorscale = false;
              }
              
              // CRITICAL: Bind trace to coloraxis, but preserve existing marker.colorscale if present
              // For z-based traces (heatmaps), use trace.coloraxis
              // For marker.color-based traces (scatter), Plotly.js may not support marker.coloraxis
              // So we set marker.colorscale directly to match the layout coloraxis colorscale
              if (hasZData || continuousTypes.has(traceType)) {
                // Only bind if not already bound
                if (!trace.coloraxis) {
                  trace.coloraxis = coloraxisName;
                  console.log(`🔍 [hydratePanel] Bound trace ${traceType} (z-based) to coloraxis "${coloraxisName}" with colorscale "${finalColorscale}"`);
                }
              } else if (hasMarkerColorArray || hasMarkerColoraxis) {
                // CRITICAL FIX: Preserve existing marker.colorscale if present
                // Only set marker.colorscale if it's not already set
                if (!trace.marker) trace.marker = {};
                
                // Set coloraxis reference if not already set
                if (!trace.marker.coloraxis) {
                  trace.marker.coloraxis = coloraxisName;
                }
                
                // Only set marker.colorscale if it's not already set (preserve existing)
                if (!trace.marker.colorscale) {
                  trace.marker.colorscale = finalColorscale;
                  console.log(`🔍 [hydratePanel] Set marker.colorscale "${finalColorscale}" directly for scatter trace to match layout coloraxis`);
                } else {
                  console.log(`🔍 [hydratePanel] Preserved existing marker.colorscale "${trace.marker.colorscale}" for scatter trace (not overwriting)`);
                }
              }
            }
            return trace;
          });
          
          console.log(`🎨 [hydratePanel] Applied sequential colorscale "${colorscale}"${reversed ? ' (reversed)' : ''} for continuous color traces after journal template`);
        }
        
        // DEBUG: Verify colorscale was applied
        const tracesBound = baseResult.data.filter((t: any) => t.coloraxis || t.marker?.coloraxis).length;
        console.log(`🔍 [hydratePanel] DEBUG - After sequential scale application for panel ${panel.id}:`, {
          coloraxisExists: !!baseResult.layout.coloraxis,
          colorscale: (baseResult.layout.coloraxis as any)?.colorscale,
          cmin: (baseResult.layout.coloraxis as any)?.cmin,
          cmax: (baseResult.layout.coloraxis as any)?.cmax,
          tracesBound: tracesBound,
          tracesWithTraceColoraxis: baseResult.data.filter((t: any) => t.coloraxis).length,
          tracesWithMarkerColoraxis: baseResult.data.filter((t: any) => t.marker?.coloraxis).length,
          traceBindings: baseResult.data.map((t: any) => ({
            type: t.type,
            traceColoraxis: t.coloraxis,
            markerColoraxis: t.marker?.coloraxis,
          })),
        });
      } catch (error) {
        console.warn('Failed to apply sequential colorscale after journal template:', error);
      }
    } else {
      if (isFigureEditorPanel) {
        console.log(`🔍 [hydratePanel] DEBUG - Figure editor panel ${panel.id}: skipping theme sequential scale application (preserving original figure colors)`);
      } else {
        console.log(`🔍 [hydratePanel] DEBUG - No sequential scale to apply for panel ${panel.id} (sequentialScaleToApply is null)`);
      }
    }

    // Step 2: Apply Global Style overrides (font family, font scale) and Journal Template settings (line width)
    // Font family and font scale override journal template settings
    // Font scale multiplies all text sizes proportionally, preserving relative differences
    // Line width is part of journal template bundle settings
    
    // Get original font sizes from journal template (after template is applied, sizes are in layout)
    // Use _templateFontSizes if available (stored by journal template), otherwise read from layout
    const templateFontSizes = (baseResult.layout as any)?._templateFontSizes;
    const baseFontSize = baseResult.layout?.font?.size || (hasJournalTemplate && templateFontSizes?.base) || 8;
    // Title size: prefer template stored value, then read from layout, then calculate from base
    const titleFontSize = (hasJournalTemplate && templateFontSizes?.title) 
      || (baseResult.layout?.title && typeof baseResult.layout.title === 'object' && (baseResult.layout.title as any).font?.size)
      || baseFontSize * 1.3;
    const subtitleFontSize = (hasJournalTemplate && templateFontSizes?.subtitle) 
      || (baseResult.layout?.title?.subtitle && typeof baseResult.layout.title.subtitle === 'object' && (baseResult.layout.title.subtitle as any).font?.size)
      || baseFontSize * 0.9;
    
    console.log(`🎨 [hydratePanel] Applying style settings to panel ${panel.id}:`, {
      hasJournalTemplate,
      overrideFontFamily,
      fontScale,
      lineWidth, // From journal template
      baseFontSize,
      titleFontSize,
      templateFontSizes,
    });

    // Apply font family override if specified (to all text elements including legend)
    if (overrideFontFamily) {
      if (!baseResult.layout.font) {
        baseResult.layout.font = {};
      }
      baseResult.layout.font = {
        ...baseResult.layout.font,
        family: overrideFontFamily,
      };

      // Override title font family
      if (baseResult.layout.title && typeof baseResult.layout.title === 'object') {
        if (!(baseResult.layout.title as any).font) {
          (baseResult.layout.title as any).font = {};
        }
        (baseResult.layout.title as any).font = {
          ...(baseResult.layout.title as any).font,
          family: overrideFontFamily,
        };
      }

      // Override axis font families
      if (baseResult.layout.xaxis) {
        if ((baseResult.layout.xaxis as any).titlefont) {
          (baseResult.layout.xaxis as any).titlefont = {
            ...(baseResult.layout.xaxis as any).titlefont,
            family: overrideFontFamily,
          };
        }
        if ((baseResult.layout.xaxis as any).tickfont) {
          (baseResult.layout.xaxis as any).tickfont = {
            ...(baseResult.layout.xaxis as any).tickfont,
            family: overrideFontFamily,
          };
        }
      }

      if (baseResult.layout.yaxis) {
        if ((baseResult.layout.yaxis as any).titlefont) {
          (baseResult.layout.yaxis as any).titlefont = {
            ...(baseResult.layout.yaxis as any).titlefont,
            family: overrideFontFamily,
          };
        }
        if ((baseResult.layout.yaxis as any).tickfont) {
          (baseResult.layout.yaxis as any).tickfont = {
            ...(baseResult.layout.yaxis as any).tickfont,
            family: overrideFontFamily,
          };
        }
      }

      // Override legend font family
      if (baseResult.layout.legend) {
        if (!(baseResult.layout.legend as any).font) {
          (baseResult.layout.legend as any).font = {};
        }
        (baseResult.layout.legend as any).font = {
          ...((baseResult.layout.legend as any).font || {}),
          family: overrideFontFamily,
        };
      }
    }

    // Apply font scale proportionally to all text elements
    // Scale from original journal template sizes (or defaults) to preserve relative differences
    if (fontScale !== 1.0) {
      // Scale base font size
      if (!baseResult.layout.font) {
        baseResult.layout.font = {};
      }
      baseResult.layout.font = {
        ...baseResult.layout.font,
        size: Math.round(baseFontSize * fontScale),
      };

      // Scale title font size proportionally
      if (baseResult.layout.title && typeof baseResult.layout.title === 'object') {
        if (!(baseResult.layout.title as any).font) {
          (baseResult.layout.title as any).font = {};
        }
        const scaledTitleSize = Math.round(titleFontSize * fontScale);
        (baseResult.layout.title as any).font = {
          ...(baseResult.layout.title as any).font,
          size: scaledTitleSize,
        };
      }

      // Scale subtitle font size proportionally
      if (baseResult.layout.title?.subtitle && typeof baseResult.layout.title.subtitle === 'object') {
        if (!(baseResult.layout.title.subtitle as any).font) {
          (baseResult.layout.title.subtitle as any).font = {};
        }
        const scaledSubtitleSize = Math.round(subtitleFontSize * fontScale);
        (baseResult.layout.title.subtitle as any).font = {
          ...(baseResult.layout.title.subtitle as any).font,
          size: scaledSubtitleSize,
        };
      }

      // Scale axis title font sizes proportionally
      if (baseResult.layout.xaxis) {
        if ((baseResult.layout.xaxis as any).titlefont) {
          const originalSize = (baseResult.layout.xaxis as any).titlefont.size || baseFontSize;
          (baseResult.layout.xaxis as any).titlefont = {
            ...(baseResult.layout.xaxis as any).titlefont,
            size: Math.round(originalSize * fontScale),
          };
        }
        if ((baseResult.layout.xaxis as any).tickfont) {
          const originalSize = (baseResult.layout.xaxis as any).tickfont.size || baseFontSize * 0.875;
          (baseResult.layout.xaxis as any).tickfont = {
            ...(baseResult.layout.xaxis as any).tickfont,
            size: Math.round(originalSize * fontScale),
          };
        }
      }

      if (baseResult.layout.yaxis) {
        if ((baseResult.layout.yaxis as any).titlefont) {
          const originalSize = (baseResult.layout.yaxis as any).titlefont.size || baseFontSize;
          (baseResult.layout.yaxis as any).titlefont = {
            ...(baseResult.layout.yaxis as any).titlefont,
            size: Math.round(originalSize * fontScale),
          };
        }
        if ((baseResult.layout.yaxis as any).tickfont) {
          const originalSize = (baseResult.layout.yaxis as any).tickfont.size || baseFontSize * 0.875;
          (baseResult.layout.yaxis as any).tickfont = {
            ...(baseResult.layout.yaxis as any).tickfont,
            size: Math.round(originalSize * fontScale),
          };
        }
      }

      // Scale legend font size proportionally
      if (baseResult.layout.legend) {
        if (!(baseResult.layout.legend as any).font) {
          (baseResult.layout.legend as any).font = {};
        }
        const originalLegendSize = ((baseResult.layout.legend as any).font?.size) || baseFontSize;
        (baseResult.layout.legend as any).font = {
          ...((baseResult.layout.legend as any).font || {}),
          size: Math.round(originalLegendSize * fontScale),
        };
      }
    }

    // Apply line width from journal template (line width is set by template, default to 1)
    // Line width comes from journal template typography settings (lineWeightPt_min or lineWeightPt_range)
    // Always apply line width (even if 1) to ensure consistency
    if (lineWidth !== undefined) {
      baseResult.data = baseResult.data.map((trace: any, index: number) => {
        const traceType = String(trace?.type || 'scatter').toLowerCase();
        const mode = String(trace?.mode || '').toLowerCase();
        
        // Check if trace has lines
        const hasLines = 
          (traceType === 'scatter' || traceType === 'scattergl') && 
          (mode.includes('lines') || mode.includes('line'));
        
        const isBar = traceType === 'bar' || traceType === 'histogram';
        
        if (hasLines || (isBar && trace.line)) {
          const updatedTrace = {
            ...trace,
            line: {
              ...(trace.line || {}),
              width: lineWidth,
            },
          };
          console.log(`🎨 [hydratePanel] Applied lineWidth ${lineWidth} to trace ${index} (type: ${traceType}, mode: ${mode})`);
          return updatedTrace;
        }
        
        return trace;
      });
    }

    console.log(`🎨 [hydratePanel] Applied style settings to panel ${panel.id}:`, {
      fontFamilyOverride: overrideFontFamily || 'none',
      fontScale: fontScale !== 1.0 ? fontScale.toFixed(2) : 'none (1.0)',
      lineWidth: lineWidth, // From journal template (default 1)
      tracesUpdated: lineWidth !== undefined && lineWidth !== 1
        ? baseResult.data.filter((t: any) => t.line).length 
        : 0,
    });

    // Apply colorblind-safe palette if enabled
    if (composition?.theme?.colorblindSafe) {
      // Apply colorway to layout (for categorical colors)
      // Only overwrite if no colorway is already set (preserve original plot's colorway)
      if (!baseResult.layout.colorway || (Array.isArray(baseResult.layout.colorway) && baseResult.layout.colorway.length === 0)) {
        baseResult.layout.colorway = DEFAULT_COLORBLIND_PALETTE;
      }

      // Helper to check if trace uses continuous color scale
      // CRITICAL: This must detect continuous traces BEFORE coloraxis is bound
      const usesContinuousScale = (trace: any): boolean => {
        // Check for continuous scale types
        const continuousTypes = new Set(['heatmap', 'contour', 'histogram2d', 'histogram2dcontour', 'choropleth', 'choroplethmapbox', 'densitymapbox', 'image']);
        if (continuousTypes.has(String(trace.type || '').toLowerCase())) {
          return true;
        }
        // Check for colorscale or coloraxis properties
        if (trace.colorscale || trace.coloraxis) {
          return true;
        }
        if (trace.marker?.colorscale || trace.marker?.coloraxis) {
          return true;
        }
        if (trace.line?.colorscale || trace.line?.coloraxis) {
          return true;
        }
        // CRITICAL: Check for marker.color as numeric array (continuous coloring in scatter plots)
        // This detects continuous coloring BEFORE coloraxis is bound
        if (trace.marker?.color && Array.isArray(trace.marker.color) && trace.marker.color.length > 0) {
          const firstVal = trace.marker.color[0];
          if (typeof firstVal === 'number') {
            // This is a continuous color scale - DO NOT apply categorical colorway
            return true;
          }
        }
        // Check for z data (indicates continuous scale)
        if (trace.z && (Array.isArray(trace.z) || Array.isArray(trace.z?.[0]))) {
          return true;
        }
        return false;
      };

      // Apply colorblind-safe colors only to categorical traces (not continuous scales)
      // IMPORTANT: Only overwrite colors if they're not already explicitly set
      // This preserves user-customized colors and theme colors from the original plot
      baseResult.data = baseResult.data.map((trace: any, index: number) => {
        // Skip traces that use continuous color scales
        if (usesContinuousScale(trace)) {
          return trace;
        }

        // Helper to check if a trace has explicit colors set
        const hasExplicitColor = (t: any): boolean => {
          // Check for explicit marker color (not undefined, not using colorway)
          if (t.marker?.color !== undefined && typeof t.marker.color === 'string') {
            return true;
          }
          // Check for explicit line color
          if (t.line?.color !== undefined && typeof t.line.color === 'string') {
            return true;
          }
          // Check for explicit fillcolor (box/violin)
          if (t.fillcolor !== undefined && typeof t.fillcolor === 'string') {
            return true;
          }
          // Check for explicit colors array (pie/funnelarea)
          if (t.marker?.colors && Array.isArray(t.marker.colors) && t.marker.colors.length > 0) {
            // Check if colors are strings (explicit) vs using colorway
            const firstColor = t.marker.colors[0];
            if (typeof firstColor === 'string' && firstColor.length > 0) {
              return true;
            }
          }
          return false;
        };

        // If trace already has explicit colors, preserve them
        if (hasExplicitColor(trace)) {
          return trace;
        }

        // Only apply colorblind-safe palette if no explicit colors are set
        const colorIndex = index % DEFAULT_COLORBLIND_PALETTE.length;
        const color = DEFAULT_COLORBLIND_PALETTE[colorIndex];
        const updatedTrace = { ...trace };
        
        if (trace.type === 'scatter' || trace.type === 'scattergl') {
          updatedTrace.marker = { ...trace.marker, color };
          if (trace.line) {
            updatedTrace.line = { ...trace.line, color };
          }
        } else if (trace.type === 'bar' || trace.type === 'histogram') {
          updatedTrace.marker = { ...trace.marker, color };
        } else if (trace.type === 'box' || trace.type === 'violin') {
          updatedTrace.fillcolor = color;
          updatedTrace.marker = { ...trace.marker, color };
        } else if (trace.type === 'pie' || trace.type === 'funnelarea') {
          // For pie/funnelarea, apply colors to marker.colors array if present
          if (trace.marker?.colors && Array.isArray(trace.marker.colors)) {
            updatedTrace.marker = {
              ...trace.marker,
              colors: trace.marker.colors.map((_: any, i: number) => 
                DEFAULT_COLORBLIND_PALETTE[i % DEFAULT_COLORBLIND_PALETTE.length]
              ),
            };
          }
        }
        
        return updatedTrace;
      });
    }

    // Step 3: Finally enforce shared axis props (range, scale, ticks, geometry)
    // This MUST happen AFTER journal template and style to prevent overrides
    if (composition?.axesSharing && sharedProps && composition.panels.length > 1 && !panel.layoutOverrides?.axesOverride) {
      try {
        applySharedAxisProps({
          baseResult,
          composition,
          panel,
          sharedProps,
          axesSharing: composition.axesSharing,
        });
        console.log(`🎨 [hydratePanel] Applied shared axis props to panel ${panel.id} after template/style`);
      } catch (error) {
        console.warn('Failed to apply shared axis props:', error);
      }
    }

    // Cache the final result (after all transformations including journal template, style, and shared axes)
    // CRITICAL: Ensure coloraxis is in cached result before storing
    if (!baseResult.layout.coloraxis && (baseResult.layout?.meta as any)?.lumi?.theme?.sequential?.scale) {
      const sequential = (baseResult.layout?.meta as any)?.lumi?.theme?.sequential;
      baseResult.layout.coloraxis = {
        colorscale: sequential.scale,
        reversescale: !!sequential.reversed,
      } as any;
      console.log(`🔍 [hydratePanel] DEBUG - Added missing coloraxis to cache for panel ${panel.id}:`, {
        colorscale: sequential.scale,
        reversed: sequential.reversed,
      });
    }
    panelCache.set(cacheKey, baseResult);

    // DEBUG: Final state check
    console.log(`🔍 [hydratePanel] DEBUG - Final state for panel ${panel.id}:`, {
      coloraxisExists: !!baseResult.layout.coloraxis,
      colorscale: (baseResult.layout.coloraxis as any)?.colorscale,
      hasMeta: !!baseResult.layout.meta,
      hasTheme: !!(baseResult.layout.meta as any)?.lumi?.theme,
      traceCount: baseResult.data.length,
      tracesWithColoraxis: baseResult.data.filter((t: any) => t.coloraxis).length,
    });
    
    return baseResult;
  },

  setTitle: (title: string) => {
    set((state) => {
      const current = state.currentComposition;
      if (!current) return state;

      const updated: FigureComposition = {
        ...current,
        title,
        updatedAt: new Date(),
      };

      const newState = {
        ...state,
        currentComposition: updated,
        compositions: state.compositions.map((c) =>
          c.id === updated.id ? updated : c
        ),
        // saveStatus will be updated by markDirtyAndSaveDraft
      };

      return newState;
    });

    // Mark as dirty and save draft locally
    if (markDirtyAndSaveDraft) {
      markDirtyAndSaveDraft(get, set);
    }
  },

  setCaption: (caption: string) => {
    // Backwards-compat: treat as setting manual caption
    set((state) => {
      const current = state.currentComposition;
      if (!current) return state;

      const updated: FigureComposition = {
        ...current,
        caption,
        captionMode: 'manual',
        captionManual: caption,
        updatedAt: new Date(),
      };

      const newState = {
        ...state,
        currentComposition: updated,
        compositions: state.compositions.map((c) =>
          c.id === updated.id ? updated : c
        ),
        // saveStatus will be updated by markDirtyAndSaveDraft
      };

      return newState;
    });

    // Mark as dirty and save draft locally
    if (markDirtyAndSaveDraft) {
      markDirtyAndSaveDraft(get, set);
    }
  },

  setCaptionMode: (mode: 'auto' | 'manual') => {
    set((state) => {
      const current = state.currentComposition;
      if (!current) return state;

      const updated: FigureComposition = {
        ...current,
        captionMode: mode,
        // Update legacy caption field for export compatibility
        caption: mode === 'auto' ? (current.captionAuto || '') : (current.captionManual || current.caption || ''),
        updatedAt: new Date(),
      };

      const newState = {
        ...state,
        currentComposition: updated,
        compositions: state.compositions.map((c) =>
          c.id === updated.id ? updated : c
        ),
      };

      return newState;
    });

    // If switching to auto, recompute caption
    if (mode === 'auto') {
      get().recomputeAutoCaption().catch(err => console.warn('[setCaptionMode] Failed to recompute caption:', err));
    }

    // Mark as dirty and save draft locally
    if (markDirtyAndSaveDraft) {
      markDirtyAndSaveDraft(get, set);
    }
  },

  setCaptionManual: (caption: string) => {
    set((state) => {
      const current = state.currentComposition;
      if (!current) return state;

      const updated: FigureComposition = {
        ...current,
        captionManual: caption,
        captionMode: 'manual',
        caption, // Update legacy caption for export
        updatedAt: new Date(),
      };

      const newState = {
        ...state,
        currentComposition: updated,
        compositions: state.compositions.map((c) =>
          c.id === updated.id ? updated : c
        ),
      };

      return newState;
    });

    // Mark as dirty and save draft locally
    if (markDirtyAndSaveDraft) {
      markDirtyAndSaveDraft(get, set);
    }
  },

  recomputeAutoCaption: async () => {
    const state = get();
    const current = state.currentComposition;
    if (!current) return;

    // Create stable signature from sorted unique {plotId, versionId} pairs
    const panelsWithPlots = current.panels.filter(p => p.plotId);
    const uniquePairs = Array.from(
      new Map(
        panelsWithPlots.map(p => [
          `${p.plotId}:${p.plotVersionId ?? 'latest'}`,
          { plotId: p.plotId, versionId: p.plotVersionId ?? null }
        ])
      ).values()
    );
    const sorted = uniquePairs.sort((a, b) => 
      a.plotId.localeCompare(b.plotId) || 
      (a.versionId ?? '').localeCompare(b.versionId ?? '')
    );
    const panelSignature = JSON.stringify(sorted);
    
    // Check if signature is unchanged from last run - skip recompute if so
    const lastSignature = (state as any).lastCaptionSignature;
    if (lastSignature === panelSignature && current.captionAuto) {
      console.log('[recomputeAutoCaption] Panel signature unchanged, skipping recompute');
      return;
    }
    
    // Get plots store to access captions
    const plotsStore = usePlotsStore.getState();
    
    // Build caption from panels in order
    const captionLines: string[] = [];
    
    // Load insights for plots that don't have captions cached yet
    const plotIds = panelsWithPlots.map(p => p.plotId).filter((id): id is string => !!id);
    
    // Wait for insights to load before reading captions
    if (plotIds.length > 0) {
      console.log('[recomputeAutoCaption] Loading insights for plots:', plotIds);
      try {
        await plotsStore.loadInsightsForPlots(current.projectId, plotIds);
        console.log('[recomputeAutoCaption] Insights loaded, reading captions');
      } catch (error) {
        console.warn('[recomputeAutoCaption] Failed to load insights:', error);
        // Continue with whatever captions we have cached
      }
    }

    // Sort panels by display position (y first, then x) to ensure consistent order
    // This matches how GridLayout displays panels visually
    const sortedPanels = [...panelsWithPlots].sort((a, b) => {
      if (a.y !== b.y) return a.y - b.y;
      return a.x - b.x;
    });

    // Fetch plotlyJson for plots that need title extraction (title is "Draft (in progress)" or missing)
    const plotsNeedingTitleFetch: Array<{ plotId: string; versionId?: string }> = [];
    sortedPanels
      .filter(p => p.plotId)
      .forEach(p => {
        const plot = plotsStore.plots.find(pl => pl.id === p.plotId) ||
                     plotsStore.getPlotByContext?.({ projectId: current.projectId, id: p.plotId });
        const needsFetch = (!plot?.plotlyJson || !plot?.plotlyJson?.data || plot.plotlyJson.data.length === 0) &&
                          (!plot?.title || plot.title === 'Draft (in progress)');
        if (needsFetch && p.plotId) {
          plotsNeedingTitleFetch.push({ plotId: p.plotId, versionId: p.plotVersionId });
        }
      });
    
    // Fetch plotlyJson for plots that need it
    const fetchedTitles = new Map<string, string>();
    if (plotsNeedingTitleFetch.length > 0) {
      try {
        const fetchPromises = plotsNeedingTitleFetch.map(async (item) => {
          const { plotId, versionId } = item;
          try {
            const versionPath = versionId ? `versions/${versionId}` : 'versions/latest';
            const response = await apiRequest('GET', `/api/ai/v2/plots/${plotId}/${versionPath}/plotly_figure.json`);
            if (response.ok) {
              const fig = await response.json();
              const titleFromFig = fig?.layout?.title?.text || fig?.layout?.title;
              if (typeof titleFromFig === 'string' && titleFromFig.trim() && titleFromFig !== 'Draft (in progress)') {
                return { plotId, title: titleFromFig.trim() };
              }
            }
          } catch (error) {
            console.warn(`[recomputeAutoCaption] Failed to fetch plotlyJson for plot ${plotId}:`, error);
          }
          return null;
        });
        const results = await Promise.all(fetchPromises);
        results.forEach(result => {
          if (result) {
            fetchedTitles.set(result.plotId, result.title);
          }
        });
      } catch (error) {
        console.warn('[recomputeAutoCaption] Failed to fetch plotlyJson for some plots:', error);
      }
    }

    sortedPanels.forEach((panel, index) => {
      if (!panel.plotId) return;
      
      // Use the sorted index to determine the letter (a, b, c, d...)
      // This ensures captions match the visual display order, not the panel label
      const panelLetter = String.fromCharCode(97 + index); // 'a' = 97, 'b' = 98, etc.
      const caption = plotsStore.getPlotCaption(panel.plotId, current.projectId);
      const plot = plotsStore.plots.find(p => p.id === panel.plotId) ||
                   plotsStore.getPlotByContext?.({ projectId: current.projectId, id: panel.plotId });
      let plotTitle = plot?.title;
      
      // If title is "Draft (in progress)" or missing, try extracting from plotlyJson
      if ((!plotTitle || plotTitle === 'Draft (in progress)') && plot?.plotlyJson) {
        try {
          const titleFromFig = plot.plotlyJson?.layout?.title?.text || plot.plotlyJson?.layout?.title;
          if (typeof titleFromFig === 'string' && titleFromFig.trim() && titleFromFig !== 'Draft (in progress)') {
            plotTitle = titleFromFig.trim();
          }
        } catch {}
      }
      
      // If still no title, use fetched title from version API
      if ((!plotTitle || plotTitle === 'Draft (in progress)') && fetchedTitles.has(panel.plotId)) {
        plotTitle = fetchedTitles.get(panel.plotId)!;
      }
      
      const captionCache = plotsStore.plotCaptions || {};
      const cacheEntry = captionCache[panel.plotId];
      
      console.log(`[recomputeAutoCaption] Panel ${panelLetter} (${panel.plotId}):`, {
        hasCaption: !!caption,
        caption: caption?.substring(0, 50),
        hasTitle: !!plotTitle,
        title: plotTitle,
        cacheEntry: cacheEntry !== undefined ? (cacheEntry !== null ? `"${String(cacheEntry).substring(0, 50)}..."` : 'null') : 'not cached'
      });
      
      if (caption && caption.trim()) {
        captionLines.push(`(${panelLetter}) ${caption.trim()}`);
      } else if (plotTitle && plotTitle !== 'Draft (in progress)') {
        // Fallback to plot title if no caption available (skip "Draft (in progress)")
        captionLines.push(`(${panelLetter}) ${plotTitle}`);
      } else {
        // Skip panels with no caption or title
        console.warn(`[recomputeAutoCaption] Panel ${panelLetter} (${panel.plotId}) has no caption or title`);
      }
    });

    const autoCaption = captionLines.join('\n');
    console.log('[recomputeAutoCaption] Generated auto caption:', {
      panelCount: sortedPanels.length,
      captionLineCount: captionLines.length,
      captionPreview: autoCaption.substring(0, 100)
    });

    set((s) => {
      const comp = s.currentComposition;
      if (!comp) return s;

      const updated: FigureComposition = {
        ...comp,
        captionAuto: autoCaption,
        // Don't update legacy caption - UI always allows manual editing
        // Legacy caption stays as captionManual or existing caption
        updatedAt: new Date(),
      };

      return {
        ...s,
        currentComposition: updated,
        compositions: s.compositions.map((c) =>
          c.id === updated.id ? updated : c
        ),
        lastCaptionSignature: panelSignature, // Store signature for next check
      } as any;
    });
  },

  getEffectiveCaption: () => {
    const state = get();
    const current = state.currentComposition;
    if (!current) return '';
    return getEffectiveCaptionForComposition(current);
  },

  setTheme: (theme: Partial<FigureCompositionTheme>) => {
    console.log('🎨 [setTheme] Theme update requested:', theme);
    set((state) => {
      const current = state.currentComposition;
      if (!current) return state;

      const previousTheme = current.theme;
      // Remove lineWidth from theme updates (line width comes from journal template, not theme)
      const { lineWidth, ...themeWithoutLineWidth } = theme;
      
      const updated: FigureComposition = {
        ...current,
        theme: {
          ...current.theme,
          ...themeWithoutLineWidth,
        },
        updatedAt: new Date(),
      };
      
      // Clean up lineWidth from theme if it exists (backward compatibility)
      if ('lineWidth' in updated.theme) {
        delete (updated.theme as any).lineWidth;
      }

      console.log('🎨 [setTheme] Theme updated:', {
        previous: previousTheme,
        updated: updated.theme,
        changed: Object.keys(theme),
      });

      const newState = {
        ...state,
        currentComposition: updated,
        compositions: state.compositions.map((c) =>
          c.id === updated.id ? updated : c
        ),
        // saveStatus will be updated by markDirtyAndSaveDraft
      };

      return newState;
    });

    // Mark as dirty and save draft locally
    if (markDirtyAndSaveDraft) {
      markDirtyAndSaveDraft(get, set);
    }
  },

  setCanvasSize: (size: FigureComposition['canvasSize']) => {
    set((state) => {
      const current = state.currentComposition;
      if (!current) return state;

      // Recompute per-column/per-row scale groups if standardization is active
      let lockedXScaleGroups: Record<string, { scaleFactor: number; referencePanelId: string }> | null = null;
      let lockedYScaleGroups: Record<string, { scaleFactor: number; referencePanelId: string }> | null = null;

      const nextComp: FigureComposition = {
        ...current,
        canvasSize: size,
        lockedXScaleGroups: null,
        lockedYScaleGroups: null,
      };

      const axesSharing = current.axesSharing;
      if (axesSharing?.standardizeXScales) {
        lockedXScaleGroups = computeLockedXScaleGroups(nextComp, current.grid, size);
      }

      if (axesSharing?.standardizeYScales) {
        lockedYScaleGroups = computeLockedYScaleGroups(nextComp, current.grid, size);
      }

      const updated: FigureComposition = {
        ...nextComp,
        lockedXScaleGroups,
        lockedYScaleGroups,
        updatedAt: new Date(),
      };

      const newState = {
        ...state,
        currentComposition: updated,
        compositions: state.compositions.map((c) =>
          c.id === updated.id ? updated : c
        ),
        // saveStatus will be updated by markDirtyAndSaveDraft
      };

      return newState;
    });

    // Mark as dirty and save draft locally
    if (markDirtyAndSaveDraft) {
      markDirtyAndSaveDraft(get, set);
    }
  },

  setFigureLegend: (legend: Partial<NonNullable<FigureComposition['figureLegend']>>) => {
    set((state) => {
      const current = state.currentComposition;
      if (!current) return state;

      // Check if figureLegend actually changed to avoid unnecessary updates
      const newFigureLegend = {
        ...(current.figureLegend ?? { enabled: false }),
        ...legend,
      };
      
      // Deep compare to see if figureLegend actually changed
      const figureLegendChanged = 
        JSON.stringify(current.figureLegend) !== JSON.stringify(newFigureLegend);
      
      if (!figureLegendChanged) {
        // No change, return state as-is to prevent unnecessary re-renders
        return state;
      }

      // Only create new object if figureLegend actually changed
      // Preserve all other references (panels, theme, grid, etc.) to prevent re-renders
      const updated: FigureComposition = {
        ...current,
        figureLegend: newFigureLegend,
        updatedAt: new Date(),
      };

      const newState = {
        ...state,
        currentComposition: updated,
        compositions: state.compositions.map((c) =>
          c.id === updated.id ? updated : c
        ),
        // saveStatus will be updated by markDirtyAndSaveDraft
      };

      return newState;
    });

    // Mark as dirty and save draft locally
    if (markDirtyAndSaveDraft) {
      markDirtyAndSaveDraft(get, set);
    }
  },

  setLegendPosition: (position: LegendPosition) => {
    set((state) => {
      const current = state.currentComposition;
      if (!current) return state;

      // Check if legendPosition actually changed
      if (current.legendPosition === position) {
        return state;
      }

      const updated: FigureComposition = {
        ...current,
        legendPosition: position,
        updatedAt: new Date(),
      };

      const newState = {
        ...state,
        currentComposition: updated,
        compositions: state.compositions.map((c) =>
          c.id === updated.id ? updated : c
        ),
        // saveStatus will be updated by markDirtyAndSaveDraft
      };

      return newState;
    });

    // Mark as dirty and save draft locally
    if (markDirtyAndSaveDraft) {
      markDirtyAndSaveDraft(get, set);
    }
  },

  updatePanelOverrides: (panelId: string, overrides: Partial<PanelLayoutOverrides>) => {
    set((state) => {
      const current = state.currentComposition;
      if (!current) return state;

      const updated: FigureComposition = {
        ...current,
        panels: current.panels.map((p) =>
          p.id === panelId
            ? {
                ...p,
                layoutOverrides: mergeOverrides(p.layoutOverrides, overrides),
              }
            : p
        ),
        updatedAt: new Date(),
      };

      const newState = {
        ...state,
        currentComposition: updated,
        compositions: state.compositions.map((c) =>
          c.id === updated.id ? updated : c
        ),
        // saveStatus will be updated by markDirtyAndSaveDraft
      };

      return newState;
    });

    // Mark as dirty and save draft locally
    if (markDirtyAndSaveDraft) {
      markDirtyAndSaveDraft(get, set);
    }
  },

  setGridSettings: (grid: Partial<FigureCompositionGrid>) => {
    set((state) => {
      const current = state.currentComposition;
      if (!current) return state;

      const updatedGrid: FigureCompositionGrid = {
        ...current.grid,
        ...grid,
      };

      // Recompute per-column/per-row scale groups if standardization is active
      let lockedXScaleGroups: Record<string, { scaleFactor: number; referencePanelId: string }> | null = null;
      let lockedYScaleGroups: Record<string, { scaleFactor: number; referencePanelId: string }> | null = null;

      const nextComp: FigureComposition = {
        ...current,
        grid: updatedGrid,
        lockedXScaleGroups: null,
        lockedYScaleGroups: null,
      };

      const axesSharing = current.axesSharing;
      if (axesSharing?.standardizeXScales) {
        lockedXScaleGroups = computeLockedXScaleGroups(nextComp, updatedGrid, current.canvasSize);
      }

      if (axesSharing?.standardizeYScales) {
        lockedYScaleGroups = computeLockedYScaleGroups(nextComp, updatedGrid, current.canvasSize);
      }

      const updated: FigureComposition = {
        ...nextComp,
        lockedXScaleGroups,
        lockedYScaleGroups,
        updatedAt: new Date(),
      };

      const newState = {
        ...state,
        currentComposition: updated,
        compositions: state.compositions.map((c) =>
          c.id === updated.id ? updated : c
        ),
        // saveStatus will be updated by markDirtyAndSaveDraft
      };

      return newState;
    });

    // Mark as dirty and save draft locally
    if (markDirtyAndSaveDraft) {
      markDirtyAndSaveDraft(get, set);
    }
  },

  setAxesSharing: (axesSharing: Partial<AxesSharing>) => {
    set((state) => {
      const current = state.currentComposition;
      if (!current) return state;

      const mergedAxesSharing: AxesSharing = {
        shareXAcrossColumns: false,
        shareXAcrossAll: false,
        shareYAcrossRows: false,
        warnOnScaleMismatch: false,
        ...current.axesSharing,
        ...axesSharing,
      };

      // If X-axis linking is disabled, automatically disable X-axis standardization
      const hasXAxisLinking = mergedAxesSharing.shareXAcrossColumns || mergedAxesSharing.shareXAcrossAll;
      if (!hasXAxisLinking) {
        mergedAxesSharing.standardizeXTitles = false;
        mergedAxesSharing.standardizeXScales = false;
      }

      // If Y-axis linking is disabled, automatically disable Y-axis standardization
      if (!mergedAxesSharing.shareYAcrossRows) {
        mergedAxesSharing.standardizeYTitles = false;
        mergedAxesSharing.standardizeYScales = false;
      }

      // Compute per-column/per-row scale groups if standardization is active
      let lockedXScaleGroups: Record<string, { scaleFactor: number; referencePanelId: string }> | null = null;
      let lockedYScaleGroups: Record<string, { scaleFactor: number; referencePanelId: string }> | null = null;

      const nextComp: FigureComposition = {
        ...current,
        axesSharing: mergedAxesSharing,
        lockedXScaleGroups: null,
        lockedYScaleGroups: null,
      };

      if (mergedAxesSharing.standardizeXScales) {
        lockedXScaleGroups = computeLockedXScaleGroups(nextComp, current.grid, current.canvasSize);
      }

      if (mergedAxesSharing.standardizeYScales) {
        lockedYScaleGroups = computeLockedYScaleGroups(nextComp, current.grid, current.canvasSize);
      }

      const updated: FigureComposition = {
        ...nextComp,
        lockedXScaleGroups,
        lockedYScaleGroups,
        updatedAt: new Date(),
      };

      const newState = {
        ...state,
        currentComposition: updated,
        compositions: state.compositions.map((c) =>
          c.id === updated.id ? updated : c
        ),
        // saveStatus will be updated by markDirtyAndSaveDraft
      };

      return newState;
    });

    // Mark as dirty and save draft locally
    if (markDirtyAndSaveDraft) {
      markDirtyAndSaveDraft(get, set);
    }
  },

  setSelectedPanelId: (panelId: string | null) => {
    set({ 
      selectedPanelId: panelId,
      selectedPanelIds: panelId ? [panelId] : [],
      selectedAnnotationId: null, // Clear annotation selection when panel is selected
    });
  },

  togglePanelSelection: (panelId: string) => {
    set((state) => {
      const currentIds = state.selectedPanelIds;
      const isSelected = currentIds.includes(panelId);
      
      if (isSelected) {
        // Deselect
        const newIds = currentIds.filter(id => id !== panelId);
        return {
          selectedPanelId: newIds.length > 0 ? newIds[0] : null,
          selectedPanelIds: newIds,
        };
      } else {
        // Add to selection
        const newIds = [...currentIds, panelId];
        return {
          selectedPanelId: panelId, // Primary selection is the last clicked
          selectedPanelIds: newIds,
        };
      }
    });
  },

  clearSelection: () => {
    set({ selectedPanelId: null, selectedPanelIds: [] });
  },

  duplicatePanel: (panelId: string) => {
    const { currentComposition } = get();
    if (!currentComposition) return;

    const panel = currentComposition.panels.find(p => p.id === panelId);
    if (!panel) return;

    // Ensure hideTitle defaults to true for duplicated panels
    const defaultLayoutOverrides = {
      ...panel.layoutOverrides,
      hideTitle: panel.layoutOverrides?.hideTitle ?? true,
    };
    
    const newPanel: FigurePanel = {
      ...panel,
      id: nanoid(),
      x: panel.x + 1, // Offset slightly
      y: panel.y + 1,
      label: generatePanelLabel(currentComposition.panels),
      // Always use saved server data - ensure figureJsonOverride is never set
      figureJsonOverride: undefined,
      layoutOverrides: defaultLayoutOverrides,
    };

    const updated = {
      ...currentComposition,
      panels: [...currentComposition.panels, newPanel],
      updatedAt: new Date(),
    };

    set((state) => ({
      currentComposition: updated,
      compositions: state.compositions.map((c) =>
        c.id === updated.id ? updated : c
      ),
      // saveStatus will be updated by markDirtyAndSaveDraft
      selectedPanelId: newPanel.id,
      selectedPanelIds: [newPanel.id],
    }));

    // Mark as dirty and save draft locally
    if (markDirtyAndSaveDraft) {
      markDirtyAndSaveDraft(get, set);
    }
  },

  duplicatePanels: (panelIds: string[]) => {
    const { currentComposition } = get();
    if (!currentComposition) return;

    const panelsToDuplicate = currentComposition.panels.filter(p => panelIds.includes(p.id));
    if (panelsToDuplicate.length === 0) return;

    // Generate labels for all new panels first
    const newPanels: FigurePanel[] = [];
    panelsToDuplicate.forEach((panel) => {
      // Ensure hideTitle defaults to true for duplicated panels
      const defaultLayoutOverrides = {
        ...panel.layoutOverrides,
        hideTitle: panel.layoutOverrides?.hideTitle ?? true,
      };
      
      const newPanel: FigurePanel = {
        ...panel,
        id: nanoid(),
        x: panel.x + 1,
        y: panel.y + 1,
        label: generatePanelLabel([...currentComposition.panels, ...newPanels]),
        // Strip figureJsonOverride - duplicates should use saved server data, not stale overrides
        figureJsonOverride: undefined,
        layoutOverrides: defaultLayoutOverrides,
      };
      newPanels.push(newPanel);
    });

    const updated = {
      ...currentComposition,
      panels: [...currentComposition.panels, ...newPanels],
      updatedAt: new Date(),
    };

    set((state) => ({
      currentComposition: updated,
      compositions: state.compositions.map((c) =>
        c.id === updated.id ? updated : c
      ),
      // saveStatus will be updated by markDirtyAndSaveDraft
      selectedPanelId: newPanels[0]?.id ?? null,
      selectedPanelIds: newPanels.map(p => p.id),
    }));

    // Mark as dirty and save draft locally
    if (markDirtyAndSaveDraft) {
      markDirtyAndSaveDraft(get, set);
    }
  },

  nudgePanels: (panelIds: string[], deltaX: number, deltaY: number) => {
    const { currentComposition } = get();
    if (!currentComposition) return;

    const updated = {
      ...currentComposition,
      panels: currentComposition.panels.map((p) => {
        if (panelIds.includes(p.id)) {
          return {
            ...p,
            x: Math.max(0, p.x + deltaX),
            y: Math.max(0, p.y + deltaY),
          };
        }
        return p;
      }),
      updatedAt: new Date(),
    };

    set((state) => ({
      currentComposition: updated,
      compositions: state.compositions.map((c) =>
        c.id === updated.id ? updated : c
      ),
      // saveStatus will be updated by markDirtyAndSaveDraft
    }));

    // Mark as dirty and save draft locally
    if (markDirtyAndSaveDraft) {
      markDirtyAndSaveDraft(get, set);
    }
  },

  addAnnotation: (panelId: string, annotation: Omit<PanelAnnotation, 'id'>) => {
    const { currentComposition } = get();
    if (!currentComposition) return;

    const newAnnotation: PanelAnnotation = {
      id: nanoid(),
      ...annotation,
    };

    const updated = {
      ...currentComposition,
      panels: currentComposition.panels.map((p) =>
        p.id === panelId
          ? {
              ...p,
              annotations: [...(p.annotations ?? []), newAnnotation],
            }
          : p
      ),
      updatedAt: new Date(),
    };

    set((state) => ({
      currentComposition: updated,
      compositions: state.compositions.map((c) =>
        c.id === updated.id ? updated : c
      ),
      // saveStatus will be updated by markDirtyAndSaveDraft
      selectedAnnotationId: newAnnotation.id,
      selectedPanelId: panelId, // Ensure panel is selected when annotation is added
    }));

    // Mark as dirty and save draft locally
    if (markDirtyAndSaveDraft) {
      markDirtyAndSaveDraft(get, set);
    }
  },

  updateAnnotation: (panelId: string, annotationId: string, updates: Partial<PanelAnnotation>) => {
    const { currentComposition } = get();
    if (!currentComposition) return;

    const updated = {
      ...currentComposition,
      panels: currentComposition.panels.map((p) =>
        p.id === panelId
          ? {
              ...p,
              annotations: (p.annotations ?? []).map((a) =>
                a.id === annotationId ? { ...a, ...updates } : a
              ),
            }
          : p
      ),
      updatedAt: new Date(),
    };

    set((state) => ({
      currentComposition: updated,
      compositions: state.compositions.map((c) =>
        c.id === updated.id ? updated : c
      ),
      // saveStatus will be updated by markDirtyAndSaveDraft
    }));

    // Mark as dirty and save draft locally
    if (markDirtyAndSaveDraft) {
      markDirtyAndSaveDraft(get, set);
    }
  },

  removeAnnotation: (panelId: string, annotationId: string) => {
    const { currentComposition, selectedAnnotationId } = get();
    if (!currentComposition) return;

    const updated = {
      ...currentComposition,
      panels: currentComposition.panels.map((p) =>
        p.id === panelId
          ? {
              ...p,
              annotations: (p.annotations ?? []).filter((a) => a.id !== annotationId),
            }
          : p
      ),
      updatedAt: new Date(),
    };

    set((state) => {
      // Clear annotation selection if it was deleted
      const newSelectedAnnotationId =
        selectedAnnotationId === annotationId ? null : selectedAnnotationId;
      return {
        currentComposition: updated,
        compositions: state.compositions.map((c) =>
          c.id === updated.id ? updated : c
        ),
        // saveStatus will be updated by markDirtyAndSaveDraft
        selectedAnnotationId: newSelectedAnnotationId,
      };
    });

    // Mark as dirty and save draft locally
    if (markDirtyAndSaveDraft) {
      markDirtyAndSaveDraft(get, set);
    }
  },

  setSelectedAnnotationId: (annotationId: string | null) => {
    set({ selectedAnnotationId: annotationId });
  },

  nudgeAnnotation: (panelId: string, annotationId: string, deltaX: number, deltaY: number) => {
    const { currentComposition } = get();
    if (!currentComposition) return;

    const updated = {
      ...currentComposition,
      panels: currentComposition.panels.map((p) =>
        p.id === panelId
          ? {
              ...p,
              annotations: (p.annotations ?? []).map((a) =>
                a.id === annotationId
                  ? {
                      ...a,
                      x: Math.max(0, Math.min(1, a.x + deltaX)),
                      y: Math.max(0, Math.min(1, a.y + deltaY)),
                      // Also update x2/y2 if they exist
                      ...(a.x2 !== undefined && { x2: Math.max(0, Math.min(1, a.x2 + deltaX)) }),
                      ...(a.y2 !== undefined && { y2: Math.max(0, Math.min(1, a.y2 + deltaY)) }),
                    }
                  : a
              ),
            }
          : p
      ),
      updatedAt: new Date(),
    };

    set((state) => ({
      currentComposition: updated,
      compositions: state.compositions.map((c) =>
        c.id === updated.id ? updated : c
      ),
      // saveStatus will be updated by markDirtyAndSaveDraft
    }));

    // Mark as dirty and save draft locally
    if (markDirtyAndSaveDraft) {
      markDirtyAndSaveDraft(get, set);
    }
  },

  exportToSVG: async () => {
    // Check export lock
    if (isExporting) {
      throw new Error('An export is already in progress. Please wait until it finishes.');
    }

    isExporting = true;
    try {
      return await exportToSVGInternal(get);
    } finally {
      isExporting = false;
    }
  },

  exportToPNG: async (dpi: number = 300) => {
    // Check export lock
    if (isExporting) {
      throw new Error('An export is already in progress. Please wait until it finishes.');
    }

    isExporting = true;

    try {
      const svg = await exportToSVGInternal(get);

      return new Promise<Blob>((resolve, reject) => {
        const blob = new Blob([svg], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);

        const img = new Image();
        img.onload = () => {
          try {
            // Extract dimensions from SVG viewBox first (most reliable)
            // This ensures we use the exact pixel dimensions we set in the SVG
            let actualWidth = 0;
            let actualHeight = 0;
            
            const viewBoxMatch = svg.match(/viewBox=["']?\s*0\s+0\s+([\d.]+)\s+([\d.]+)/i);
            if (viewBoxMatch && viewBoxMatch[1] && viewBoxMatch[2]) {
              actualWidth = Number.parseFloat(viewBoxMatch[1]);
              actualHeight = Number.parseFloat(viewBoxMatch[2]);
            }
            
            // Fallback to naturalWidth/height if viewBox not found
            if (actualWidth <= 0 || actualHeight <= 0) {
              actualWidth = img.naturalWidth || img.width || 0;
              actualHeight = img.naturalHeight || img.height || 0;
            }
            
            // Final fallback: try to parse width/height attributes
            if (actualWidth <= 0 || actualHeight <= 0) {
              const widthMatch = svg.match(/width=["']?([\d.]+)/i);
              const heightMatch = svg.match(/height=["']?([\d.]+)/i);
              if (widthMatch && heightMatch) {
                actualWidth = Number.parseFloat(widthMatch[1]);
                actualHeight = Number.parseFloat(heightMatch[1]);
              }
            }

            // Validate dimensions
            if (actualWidth <= 0 || actualHeight <= 0) {
              URL.revokeObjectURL(url);
              reject(new Error(`Invalid SVG dimensions: width=${actualWidth}, height=${actualHeight}`));
              return;
            }

            const scale = dpi / 96;
            const canvas = document.createElement('canvas');
            canvas.width = actualWidth * scale;
            canvas.height = actualHeight * scale;

            const ctx = canvas.getContext('2d');
            if (!ctx) {
              URL.revokeObjectURL(url);
              throw new Error('Failed to get canvas context');
            }

            ctx.scale(scale, scale);
            ctx.drawImage(img, 0, 0);

            canvas.toBlob(
              (blob) => {
                URL.revokeObjectURL(url);
                if (blob) {
                  resolve(blob);
                } else {
                  reject(new Error('Failed to create PNG blob'));
                }
              },
              'image/png'
            );
          } catch (error) {
            URL.revokeObjectURL(url);
            reject(error instanceof Error ? error : new Error(String(error)));
          }
        };

        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('Failed to load SVG image'));
        };

        img.src = url;
      });
    } finally {
      isExporting = false;
    }
  },

  exportPanelToSVG: async (panelId: string) => {
    if (isExporting) {
      throw new Error('An export is already in progress. Please wait until it finishes.');
    }

    isExporting = true;
    try {
      return await exportPanelToSVGInternal(get, panelId);
    } finally {
      isExporting = false;
    }
  },

  exportPanelToPNG: async (panelId: string, dpi: number = 300) => {
    if (isExporting) {
      throw new Error('An export is already in progress. Please wait until it finishes.');
    }

    isExporting = true;
    try {
      const svg = await exportPanelToSVGInternal(get, panelId);
      
      return new Promise<Blob>((resolve, reject) => {
        const blob = new Blob([svg], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);

        const img = new Image();
        img.onload = () => {
          try {
            const viewBoxMatch = svg.match(/viewBox=["']?\s*0\s+0\s+([\d.]+)\s+([\d.]+)/i);
            let actualWidth = 0;
            let actualHeight = 0;
            
            if (viewBoxMatch && viewBoxMatch[1] && viewBoxMatch[2]) {
              actualWidth = Number.parseFloat(viewBoxMatch[1]);
              actualHeight = Number.parseFloat(viewBoxMatch[2]);
            }
            
            if (actualWidth <= 0 || actualHeight <= 0) {
              actualWidth = img.naturalWidth || img.width || 0;
              actualHeight = img.naturalHeight || img.height || 0;
            }
            
            if (actualWidth <= 0 || actualHeight <= 0) {
              const widthMatch = svg.match(/width=["']?([\d.]+)/i);
              const heightMatch = svg.match(/height=["']?([\d.]+)/i);
              if (widthMatch && heightMatch) {
                actualWidth = Number.parseFloat(widthMatch[1]);
                actualHeight = Number.parseFloat(heightMatch[1]);
              }
            }

            if (actualWidth <= 0 || actualHeight <= 0) {
              URL.revokeObjectURL(url);
              reject(new Error(`Invalid SVG dimensions: width=${actualWidth}, height=${actualHeight}`));
              return;
            }

            const scale = dpi / 96;
            const canvas = document.createElement('canvas');
            canvas.width = actualWidth * scale;
            canvas.height = actualHeight * scale;

            const ctx = canvas.getContext('2d');
            if (!ctx) {
              URL.revokeObjectURL(url);
              throw new Error('Failed to get canvas context');
            }

            ctx.scale(scale, scale);
            ctx.drawImage(img, 0, 0);

            canvas.toBlob(
              (blob) => {
                URL.revokeObjectURL(url);
                if (blob) {
                  resolve(blob);
                } else {
                  reject(new Error('Failed to create PNG blob'));
                }
              },
              'image/png'
            );
          } catch (error) {
            URL.revokeObjectURL(url);
            reject(error instanceof Error ? error : new Error(String(error)));
          }
        };

        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('Failed to load SVG image'));
        };

        img.src = url;
      });
    } finally {
      isExporting = false;
    }
  },

  exportPanelsToZip: async (format: 'svg' | 'png', dpi: number = 300) => {
    if (isExporting) {
      throw new Error('An export is already in progress. Please wait until it finishes.');
    }

    isExporting = true;
    try {
      // Dynamic import of JSZip
      let JSZip: any;
      try {
        const JSZipModule = await import('jszip');
        JSZip = JSZipModule.default || (JSZipModule as any).JSZip || JSZipModule;
        if (!JSZip) {
          throw new Error('JSZip module not found');
        }
      } catch (importError) {
        throw new Error(`Failed to import JSZip: ${importError instanceof Error ? importError.message : String(importError)}`);
      }
      
      const zip = new JSZip();

      const comp = get().currentComposition;
      if (!comp) {
        throw new Error('No composition loaded');
      }

      const panelsWithPlots = comp.panels.filter(p => p.plotId);
      if (panelsWithPlots.length === 0) {
        throw new Error('No panels with plots to export');
      }

      // Sort panels by display position (y first, then x) to match visual order
      // This ensures exported files are named in the same order as they appear visually
      const sortedPanels = [...panelsWithPlots].sort((a, b) => {
        if (a.y !== b.y) return a.y - b.y;
        return a.x - b.x;
      });

      // Generate file names based on panel labels or figure title
      // Format: Fig 1a.svg, Fig 1b.svg, etc. (matching journal submission requirements)
      const sanitizeFileName = (name: string) => {
        // Extract figure number if present (e.g., "Figure 1" -> "Fig 1")
        const figMatch = name.match(/figure\s*(\d+)/i);
        if (figMatch) {
          return `Fig ${figMatch[1]}`;
        }
        // Otherwise sanitize and use as-is
        return name.replace(/[^a-zA-Z0-9\s]/g, '').trim() || 'Fig';
      };

      // Sanitize plot title for use in filename
      const sanitizePlotTitle = (title: string): string => {
        return title
          .replace(/[^a-zA-Z0-9\s_-]/g, '') // Remove special chars except spaces, dashes, underscores
          .replace(/\s+/g, ' ') // Normalize whitespace
          .trim()
          .substring(0, 50); // Limit length
      };
      
      const baseName = sanitizeFileName(comp.title || 'figure');
      let filesAdded = 0;
      const errors: string[] = [];

      // Fetch all plot titles upfront
      const plotTitleMap = new Map<string, string>();
      try {
        const response = await apiRequest('GET', `/api/projects/${comp.projectId}/plots`);
        if (response.ok) {
          const plots = await response.json();
          plots.forEach((plot: any) => {
            if (plot.id && plot.title) {
              plotTitleMap.set(plot.id, plot.title);
            }
          });
        }
      } catch (error) {
        console.warn('Failed to fetch plot titles:', error);
      }

      for (let i = 0; i < sortedPanels.length; i++) {
        const panel = sortedPanels[i];
        // Use sorted index to determine letter (a, b, c, d...) to match visual order
        const panelLetter = String.fromCharCode(97 + i); // 'a' = 97, 'b' = 98, etc.
        const plotTitle = panel.plotId ? plotTitleMap.get(panel.plotId) : undefined;
        
        // Build filename: format is "figure(a) Plot Title.png"
        let fileName: string;
        if (plotTitle) {
          const sanitizedTitle = sanitizePlotTitle(plotTitle);
          fileName = `${baseName}(${panelLetter}) ${sanitizedTitle}.${format}`;
        } else {
          fileName = `${baseName}(${panelLetter}).${format}`;
        }
        
        console.log(`[exportPanelsToZip] Processing panel ${i + 1}/${sortedPanels.length}: ${panel.id}, label="${panel.label}", letter="${panelLetter}", plotTitle="${plotTitle}", fileName="${fileName}"`);

        try {
          if (format === 'svg') {
            const svg = await exportPanelToSVGInternal(get, panel.id);
            if (svg && svg.length > 0) {
              zip.file(fileName, svg);
              filesAdded++;
              console.log(`[exportPanelsToZip] Added ${fileName} to ZIP (${svg.length} bytes)`);
            } else {
              errors.push(`Panel ${panelLetter}: Empty SVG content`);
            }
          } else {
            // For PNG, we need to convert SVG to PNG
            const svg = await exportPanelToSVGInternal(get, panel.id);
            if (!svg || svg.length === 0) {
              errors.push(`Panel ${panelLetter}: Empty SVG content`);
              continue;
            }
            
            const pngBlob = await new Promise<Blob>((resolve, reject) => {
              const blob = new Blob([svg], { type: 'image/svg+xml' });
              const url = URL.createObjectURL(blob);

              const img = new Image();
              img.onload = () => {
                try {
                  const viewBoxMatch = svg.match(/viewBox=["']?\s*0\s+0\s+([\d.]+)\s+([\d.]+)/i);
                  let actualWidth = 0;
                  let actualHeight = 0;
                  
                  if (viewBoxMatch && viewBoxMatch[1] && viewBoxMatch[2]) {
                    actualWidth = Number.parseFloat(viewBoxMatch[1]);
                    actualHeight = Number.parseFloat(viewBoxMatch[2]);
                  }
                  
                  if (actualWidth <= 0 || actualHeight <= 0) {
                    actualWidth = img.naturalWidth || img.width || 0;
                    actualHeight = img.naturalHeight || img.height || 0;
                  }
                  
                  if (actualWidth <= 0 || actualHeight <= 0) {
                    const widthMatch = svg.match(/width=["']?([\d.]+)/i);
                    const heightMatch = svg.match(/height=["']?([\d.]+)/i);
                    if (widthMatch && heightMatch) {
                      actualWidth = Number.parseFloat(widthMatch[1]);
                      actualHeight = Number.parseFloat(heightMatch[1]);
                    }
                  }

                  if (actualWidth <= 0 || actualHeight <= 0) {
                    URL.revokeObjectURL(url);
                    reject(new Error(`Invalid SVG dimensions: width=${actualWidth}, height=${actualHeight}`));
                    return;
                  }

                  const scale = dpi / 96;
                  const canvas = document.createElement('canvas');
                  canvas.width = actualWidth * scale;
                  canvas.height = actualHeight * scale;

                  const ctx = canvas.getContext('2d');
                  if (!ctx) {
                    URL.revokeObjectURL(url);
                    reject(new Error('Failed to get canvas context'));
                    return;
                  }

                  ctx.scale(scale, scale);
                  ctx.drawImage(img, 0, 0);

                  canvas.toBlob(
                    (blob) => {
                      URL.revokeObjectURL(url);
                      if (blob) {
                        resolve(blob);
                      } else {
                        reject(new Error('Failed to create PNG blob'));
                      }
                    },
                    'image/png'
                  );
                } catch (error) {
                  URL.revokeObjectURL(url);
                  reject(error instanceof Error ? error : new Error(String(error)));
                }
              };

              img.onerror = () => {
                URL.revokeObjectURL(url);
                reject(new Error('Failed to load SVG image'));
              };

              img.src = url;
            });
            
            if (pngBlob && pngBlob.size > 0) {
              zip.file(fileName, pngBlob);
              filesAdded++;
              console.log(`[exportPanelsToZip] Added ${fileName} to ZIP (${pngBlob.size} bytes)`);
            } else {
              errors.push(`Panel ${panelLetter}: Empty PNG blob`);
            }
          }
        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : String(error);
          console.error(`Failed to export panel ${panel.id} (${panelLetter}):`, error);
          errors.push(`Panel ${panelLetter}: ${errorMsg}`);
        }
      }

      if (filesAdded === 0) {
        throw new Error(`Failed to export any panels. Errors: ${errors.join('; ')}`);
      }

      if (errors.length > 0) {
        console.warn(`Some panels failed to export: ${errors.join('; ')}`);
      }

      console.log(`[exportPanelsToZip] Added ${filesAdded} file(s) to ZIP, ${errors.length} error(s)`);
      
      const zipBlob = await zip.generateAsync({ 
        type: 'blob', 
        compression: 'DEFLATE', 
        compressionOptions: { level: 6 },
        streamFiles: false // Ensure all files are included
      });
      
      if (!zipBlob || zipBlob.size === 0) {
        console.error('[exportPanelsToZip] Generated ZIP is empty', { filesAdded, errors });
        throw new Error(`Generated ZIP file is empty. Files added: ${filesAdded}, Errors: ${errors.join('; ')}`);
      }

      console.log(`[exportPanelsToZip] ZIP generated successfully, size: ${zipBlob.size} bytes`);
      return zipBlob;
    } finally {
      isExporting = false;
    }
  },
  }
});

